import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useStore } from '../context/StoreContext';
import { Button } from '../components/ui/button';
import { ExpiryNudgeBanner } from '../components/ExpiryNudgeBanner';
import { TillOpenPrompt } from '../components/TillOpenPrompt';
import { ProtocolTillGuide } from '../components/ProtocolTillGuide';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import LinkBarcodeDialog from '../components/LinkBarcodeDialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import { printReceipt as qzPrintReceipt } from '../services/PrinterService';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Search, Plus, Minus, Trash2, ShoppingCart, CreditCard, Banknote, 
  Receipt, X, Check, Loader2, Package, Barcode, User, Calculator,
  Star, Gift, Phone, UserPlus, Wifi, WifiOff, RotateCcw, Settings, DollarSign, Store,
  MoreHorizontal, ArrowUpCircle, ArrowDownCircle, AlertTriangle, FileText, Mail, ArrowRightLeft, Clock, Sparkles, Edit, ArrowLeft, Printer, Pencil, Percent, Wallet, HeartPulse, Lightbulb
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, formatDateTime } from '../utils/dateFormat';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// A product scans on its primary barcode, any additional barcode, or its SKU —
// exact first, then digits-only / leading-zero tolerant (UPC-A ↔ EAN-13 scanners).
const normScanCode = (s) => {
  const d = (s || '').toString().replace(/\D/g, '');
  return d ? (d.replace(/^0+/, '') || '0') : '';
};
export const productMatchesScan = (p, scan) => {
  if (!p || !scan) return false;
  const codes = [p.barcode, ...(p.alt_barcodes || [])].map(c => (c || '').toString().trim()).filter(Boolean);
  if (codes.includes(scan) || p.sku === scan) return true;
  const ns = normScanCode(scan);
  return !!ns && codes.some(c => normScanCode(c) === ns);
};

// Escape user-entered nudge match words for regex use
const escapeRx = (s) => s.replace(/[.*+?^${}()[\]\\]/g, '\\$&');

// Reasons staff must pick when applying a discount (margin-loss tracking)
const DISCOUNT_REASONS = ['Damaged', 'Near Expiry', 'VIP Customer', 'Staff', 'Manager Special', 'Price Match', 'Other'];

const POS = () => {
  const { getAuthHeader, activeStaff, user } = useAuth();
  const { selectedStore, getCurrentStore, storeName } = useStore();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(''); // committed search (debounced from the uncontrolled input)
  const [cart, setCart] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [cashReceived, setCashReceived] = useState('');
  const [processing, setProcessing] = useState(false);
  const [lastReceipt, setLastReceipt] = useState(null);
  const searchInputRef = useRef(null);
  const searchDebounceRef = useRef(null);
  // The search input is UNCONTROLLED — typing never re-renders the (huge) POS tree.
  // searchTerm state only commits after a short pause, and clearSearch syncs the DOM input.
  const clearSearch = () => {
    clearTimeout(searchDebounceRef.current);
    if (searchInputRef.current) searchInputRef.current.value = '';
    setSearchTerm('');
  };
  const onSearchInputChange = (e) => {
    const v = e.target.value;
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => setSearchTerm(v), 120);
  };
  
  // Customer loyalty state
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [searchingCustomer, setSearchingCustomer] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState('');
  const [newCustomerPhone, setNewCustomerPhone] = useState('');
  const [newCustomerEmail, setNewCustomerEmail] = useState('');
  const [redeemPoints, setRedeemPoints] = useState(0);
  const [pointsDiscount, setPointsDiscount] = useState(0);

  // Manual cart-level discount (Lightspeed-style): toggle % or $, applied to whole cart total
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [discountType, setDiscountType] = useState('percent'); // 'percent' | 'dollar'
  const [discountValue, setDiscountValue] = useState(''); // raw string the user typed
  const [discountReason, setDiscountReason] = useState(''); // required reason for the cart discount
  const [appliedDiscount, setAppliedDiscount] = useState({ type: null, value: 0, reason: null }); // committed
  // Per-line discount modal: { item, value, reason }
  const [lineDiscountModal, setLineDiscountModal] = useState(null);
  
  // Smartpay state
  const [smartpayConnected, setSmartpayConnected] = useState(false);
  const [smartpayPolling, setSmartpayPolling] = useState(false);
  // Every EFTPOS attempt gets a new id; a poll chain stops the moment it is superseded (cancel / new attempt / sale saved)
  const smartpayPollIdRef = useRef(0);
  const stopSmartpayPolling = () => { smartpayPollIdRef.current += 1; };
  const [smartpayPollAttempts, setSmartpayPollAttempts] = useState(0);
  const [smartpayTransactionId, setSmartpayTransactionId] = useState(null);
  const [smartpayTerminals, setSmartpayTerminals] = useState([]); // terminals for the current store
  const [selectedTerminalId, setSelectedTerminalId] = useState(null);
  // Records the cart total ($) sent to the EFTPOS terminal at /smartpay/initiate.
  // We compare this back to the current cart total before committing the sale so
  // a staff member can NEVER change the cart between "tap card" and "complete sale".
  const [smartpayRequestedAmount, setSmartpayRequestedAmount] = useState(null);
  
  // Gift Card state
  const [showGiftCardModal, setShowGiftCardModal] = useState(false);
  const [giftCardTab, setGiftCardTab] = useState('sell');
  const [giftCardAmount, setGiftCardAmount] = useState('');
  const [giftCardRecipient, setGiftCardRecipient] = useState('');
  
  // Margin validation state
  const [showMarginWarning, setShowMarginWarning] = useState(false);
  const [lowMarginItems, setLowMarginItems] = useState([]);
  const [editingPrices, setEditingPrices] = useState({});
  const [updatingPrice, setUpdatingPrice] = useState(false);
  const MIN_MARGIN_PERCENT = 42;

  // === Loyalty rules — HARDCODED, not editable in Settings ===
  // Earn: 3% of post-discount total as credit (e.g. $100 spend → $3)
  // Redeem: only when balance >= $5; flat $5 per transaction
  const LOYALTY_EARN_RATE = 0.03;
  const LOYALTY_REDEEM_AMOUNT = 5;
  const LOYALTY_MIN_BALANCE_TO_REDEEM = 5;
  const [giftCardCode, setGiftCardCode] = useState('');
  const [giftCardLookup, setGiftCardLookup] = useState(null);
  const [giftCardRedeemAmount, setGiftCardRedeemAmount] = useState('');
  const [giftCardLoading, setGiftCardLoading] = useState(false);
  const [appliedGiftCard, setAppliedGiftCard] = useState(null);

  // Store credit applied to current cart (partial redeem line item)
  const [appliedStoreCredit, setAppliedStoreCredit] = useState(0);
  // Amount user enters on the Store Credit payment screen (string, so blank possible)
  const [storeCreditUseAmount, setStoreCreditUseAmount] = useState('');
  // Manager PIN typed on the On Account (owes us) payment screen
  const [onAccountPin, setOnAccountPin] = useState('');
  // Swap/exchange modal — pick the item the customer is returning
  const [showSwapModal, setShowSwapModal] = useState(false);
  const [swapSearch, setSwapSearch] = useState('');
  // Email receipt (Sale Complete modal)
  const [emailReceiptTo, setEmailReceiptTo] = useState('');
  const [showEmailReceiptInput, setShowEmailReceiptInput] = useState(false);
  const [sendingEmailReceipt, setSendingEmailReceipt] = useState(false);
  // Goal-based product suggestions (from customer health notes)
  const [goalSuggestions, setGoalSuggestions] = useState(null);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  // Condition quick picks — staff types a condition, top 5 protocol products come up
  const [showConditionsModal, setShowConditionsModal] = useState(false);
  const [conditionQuery, setConditionQuery] = useState('');
  const [conditionList, setConditionList] = useState([]);
  const [activeCondition, setActiveCondition] = useState(null);
  const [conditionResults, setConditionResults] = useState([]);
  const [loadingConditionProducts, setLoadingConditionProducts] = useState(false);
  // DB-backed scan nudges (editable in Protocol Editor)
  const [nudgeRules, setNudgeRules] = useState([]);
  const [scanNudge, setScanNudge] = useState(null); // { productName, message, protocols } — scan nudge data
  const [scanNudgeOpen, setScanNudgeOpen] = useState(false); // protocol tips stay in the background until staff ask for them

  useEffect(() => {
    axios.get(`${API_URL}/nudges`, { headers: getAuthHeader() })
      .then(res => setNudgeRules(res.data || []))
      .catch(() => {});
  }, []);

  const findNudge = (name) => nudgeRules.find(r => {
    const terms = (r.match_text || '').split('|').map(t => t.trim()).filter(Boolean);
    if (!terms.length) return false;
    return new RegExp('\\b(' + terms.map(escapeRx).join('|') + ')s?\\b', 'i').test(name || '');
  });

  // Refund state
  const [showRefundModal, setShowRefundModal] = useState(false);
  // Login-nudge banner state — fed by /api/staff/shift-now every 5 min
  const [shiftNow, setShiftNow] = useState(null);
  const [nudgeDismissed, setNudgeDismissed] = useState(false);
  // Daily Target Tracker — fed by /api/reports/today-target every 30s
  const [dailyProgress, setDailyProgress] = useState(null);
  const [celebratedToday, setCelebratedToday] = useState(false);
  // Frequently Bought Together suggestions (last added product)
  const [fbtSuggestions, setFbtSuggestions] = useState([]);
  const [fbtDismissedIds, setFbtDismissedIds] = useState(new Set());
  // Loyalty tier for selected customer
  const [customerTier, setCustomerTier] = useState(null);
  const [refundMode, setRefundMode] = useState('menu'); // 'menu', 'phone', 'plain', 'sale'
  const [refundSaleId, setRefundSaleId] = useState('');
  const [refundSale, setRefundSale] = useState(null);
  const [refundItems, setRefundItems] = useState([]);
  const [refundReason, setRefundReason] = useState('');
  const [refundMethod, setRefundMethod] = useState('cash');
  const [refundLoading, setRefundLoading] = useState(false);
  const [phoneSearch, setPhoneSearch] = useState('');
  const [phoneSales, setPhoneSales] = useState([]);
  const [phoneSearching, setPhoneSearching] = useState(false);
  const [plainRefundAmount, setPlainRefundAmount] = useState('');
  const [plainRefundItems, setPlainRefundItems] = useState([]);
  // Refund-to-store-credit customer picker
  const [refundCreditCustomer, setRefundCreditCustomer] = useState(null);
  const [refundCreditSearch, setRefundCreditSearch] = useState('');
  const [refundCreditResults, setRefundCreditResults] = useState([]);

  // Product edit state
  const [showEditProduct, setShowEditProduct] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [editProductData, setEditProductData] = useState({});
  const [editProductSaving, setEditProductSaving] = useState(false);
  
  // Loyalty Settings state
  const [showLoyaltySettingsModal, setShowLoyaltySettingsModal] = useState(false);
  const [loyaltySettings, setLoyaltySettings] = useState({
    points_per_dollar: 0.03,
    redemption_value: 1.0,
    min_redemption_points: 3
  });
  const [loyaltyLoading, setLoyaltyLoading] = useState(false);
  
  // Misc Modal state
  const [showMiscModal, setShowMiscModal] = useState(false);
  const [miscTab, setMiscTab] = useState('item');
  const [miscSaleDesc, setMiscSaleDesc] = useState('');
  const [miscSaleAmount, setMiscSaleAmount] = useState('');
  const [miscItemQty, setMiscItemQty] = useState(1);
  const [cashMovementType, setCashMovementType] = useState('in');
  const [cashMovementAmount, setCashMovementAmount] = useState('');
  const [cashMovementReason, setCashMovementReason] = useState('');
  const [miscLoading, setMiscLoading] = useState(false);
  
  // Stock Report state
  const [showStockReportModal, setShowStockReportModal] = useState(false);
  const [stockReport, setStockReport] = useState(null);
  const [stockReportLoading, setStockReportLoading] = useState(false);
  const [lowStockThreshold, setLowStockThreshold] = useState(10);
  
  // New Products Notification state
  const [newProducts, setNewProducts] = useState([]);
  const [showNewProductsDialog, setShowNewProductsDialog] = useState(false);
  
  // Check if new products notification was already shown today
  const getNewProductsDismissedToday = () => {
    const dismissedDate = localStorage.getItem('newProductsDismissedDate');
    const today = new Date().toDateString();
    return dismissedDate === today;
  };
  
  const dismissNewProductsForToday = () => {
    const today = new Date().toDateString();
    localStorage.setItem('newProductsDismissedDate', today);
    setShowNewProductsDialog(false);
  };
  
  // Quick Keys state
  const [quickKeys, setQuickKeys] = useState([]);
  const [showQuickKeySetup, setShowQuickKeySetup] = useState(false);
  
  // Negative Stock Warning state
  const [negativeStockWarnings, setNegativeStockWarnings] = useState([]);
  const [showNegativeStockModal, setShowNegativeStockModal] = useState(false);
  const [negativeStockFixes, setNegativeStockFixes] = useState({}); // {productId: "new qty"}
  const [savingNegFix, setSavingNegFix] = useState(null);
  const [showStockWarning, setShowStockWarning] = useState(false);
  
  // Overdue Stock Count Warning state
  const [overdueSuppliers, setOverdueSuppliers] = useState([]);
  const [showOverdueWarning, setShowOverdueWarning] = useState(false);
  const [editingQuickKeyIndex, setEditingQuickKeyIndex] = useState(null);
  const [quickKeySearch, setQuickKeySearch] = useState('');
  const QUICK_KEY_SLOTS = 12; // Number of quick key slots
  
  // Short-dated stock state
  const [shortDatedItems, setShortDatedItems] = useState([]);
  const [showShortDatedAlert, setShowShortDatedAlert] = useState(false);
  const [shortDatedProduct, setShortDatedProduct] = useState(null);
  
  // Out-of-Stock Warning state
  const [showOOSWarning, setShowOOSWarning] = useState(false);
  const [oosProduct, setOosProduct] = useState(null);
  const [allowNegativeStock, setAllowNegativeStock] = useState(false);

  // Customer Orders Reminder state
  const [pendingOrderCount, setPendingOrderCount] = useState(0);
  const [showOrderReminder, setShowOrderReminder] = useState(false);

  // Price Mismatch state
  const [priceMismatches, setPriceMismatches] = useState([]);
  const [showReportMismatch, setShowReportMismatch] = useState(false);
  const [mismatchProduct, setMismatchProduct] = useState(null);
  const [mismatchLabelPrice, setMismatchLabelPrice] = useState('');

  // Wholesale mode (Sabhyata's Price)
  const [wholesaleMode, setWholesaleMode] = useState(false);

  const fetchProducts = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/products?limit=15000`, { headers: getAuthHeader() });
      // Map products to use store-specific stock for the current store
      const productsWithStoreStock = response.data.map(p => ({
        ...p,
        // Use the stock for the selected store, with fallback
        store_quantity: selectedStore === 'orewa' ? (p.stock_orewa || 0) : (p.stock_eastridge || 0),
        // Pre-compute lowercase search fields for fast filtering
        _search: [
          p.name, p.sku, p.supplier_name, p.brand, p.category
        ].filter(Boolean).join('|').toLowerCase(),
        _barcode: [p.barcode, ...(p.alt_barcodes || [])].filter(Boolean).join('|')
      }));
      // Sort once at load — filtering keeps this order so no per-keystroke sorting is needed
      productsWithStoreStock.sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));
      setProducts(productsWithStoreStock);
      
      // Check for negative stock and group by supplier
      const negativeStock = productsWithStoreStock.filter(p => p.store_quantity < 0);
      if (negativeStock.length > 0) {
        // Group by supplier
        const bySupplier = {};
        negativeStock.forEach(p => {
          const supplier = p.supplier_name || 'Unknown Supplier';
          if (!bySupplier[supplier]) {
            bySupplier[supplier] = [];
          }
          bySupplier[supplier].push(p);
        });
        
        const warnings = Object.entries(bySupplier).map(([supplier, items]) => ({
          supplier,
          count: items.length,
          products: items,            // full list for the modal
          previewProducts: items.slice(0, 5) // first 5 for the banner preview text
        }));
        
        setNegativeStockWarnings(warnings);
        setShowStockWarning(true);
      } else {
        setNegativeStockWarnings([]);
        setShowStockWarning(false);
      }
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, selectedStore]);

  // Save a corrected stock value for a single product to the active store.
  // Used by the Negative Stock fix modal.
  const saveNegativeStockFix = async (product, newQty) => {
    const qty = parseInt(newQty, 10);
    if (Number.isNaN(qty) || qty < 0) {
      toast.error('Enter a valid number (0 or greater)');
      return;
    }
    setSavingNegFix(product.id);
    try {
      const field = selectedStore === 'orewa' ? 'stock_orewa' : 'stock_eastridge';
      await axios.put(
        `${API_URL}/products/${product.id}`,
        { [field]: qty },
        { headers: getAuthHeader() }
      );
      toast.success(`Updated ${product.name}: ${product.store_quantity} → ${qty}`);
      // Clear this row's input and refresh the product list
      setNegativeStockFixes(prev => {
        const next = { ...prev };
        delete next[product.id];
        return next;
      });
      await fetchProducts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update stock');
    } finally {
      setSavingNegFix(null);
    }
  };

  // Load paired terminals for current store from the canonical `stores` record
  // (Hardware Settings now saves multi-terminal config there, not in /settings/hardware).
  const testSmartpayConnection = useCallback(async () => {
    try {
      let terminals = [];
      // 1) Preferred source: /stores/{code} (multi-terminal store config)
      try {
        const storesRes = await axios.get(`${API_URL}/stores`, { headers: getAuthHeader() });
        const me = (storesRes.data || []).find(s => s.code === selectedStore);
        terminals = Array.isArray(me?.terminals) ? me.terminals.filter(t => t.paired) : [];
      } catch (_) { /* fall through */ }

      // 2) Fallback: legacy single-terminal location under /settings/hardware
      if (terminals.length === 0) {
        try {
          const hw = await axios.get(`${API_URL}/settings/hardware`, { headers: getAuthHeader() });
          const storeSp = hw.data?.smartpay?.[selectedStore] || {};
          if (storeSp.enabled) {
            terminals = (storeSp.terminals || []).filter(t => t.paired);
          }
        } catch (_) { /* ignore */ }
      }

      // Normalise: ensure each terminal has id + label for the dropdown
      terminals = terminals.map((t, idx) => ({
        ...t,
        id: t.id || `${selectedStore}-${idx}`,
        label: t.name || t.register_name || t.register_id || `Terminal ${idx + 1}`,
      }));

      setSmartpayConnected(terminals.length > 0);
      setSmartpayTerminals(terminals);
      if (terminals.length > 0) {
        const storedKey = `pos_default_terminal_id_${selectedStore}`;
        const stored = localStorage.getItem(storedKey);
        const match = terminals.find(t => t.id === stored);
        setSelectedTerminalId(match ? match.id : terminals[0].id);
      } else {
        setSelectedTerminalId(null);
      }
    } catch (error) {
      console.error('testSmartpayConnection failed', error);
      setSmartpayConnected(false);
      setSmartpayTerminals([]);
      setSelectedTerminalId(null);
    }
  }, [getAuthHeader, selectedStore]);

  // Persist terminal choice per device
  useEffect(() => {
    if (selectedTerminalId && selectedStore) {
      localStorage.setItem(`pos_default_terminal_id_${selectedStore}`, selectedTerminalId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTerminalId, selectedStore]);
  
  // Fetch loyalty settings
  const fetchLoyaltySettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/settings/loyalty`, { headers: getAuthHeader() });
      setLoyaltySettings(response.data);
    } catch (error) {
      console.error('Failed to load loyalty settings');
    }
  }, [getAuthHeader]);

  // Fetch Short-Dated Items
  const fetchShortDatedItems = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/short-dated-stock?store=${selectedStore}`, { headers: getAuthHeader() });
      setShortDatedItems(response.data);
    } catch (error) {
      console.error('Failed to load short-dated stock');
    }
  }, [getAuthHeader, selectedStore]);

  // Fetch New Products (added in last 7 days) - only show once per day
  const fetchNewProducts = useCallback(async () => {
    // Skip if already dismissed today
    if (getNewProductsDismissedToday()) {
      return;
    }
    
    try {
      const response = await axios.get(`${API_URL}/products/new-since/7`, { headers: getAuthHeader() });
      if (response.data.count > 0) {
        setNewProducts(response.data.products);
        setShowNewProductsDialog(true);
      }
    } catch (error) {
      console.error('Failed to load new products');
    }
  }, [getAuthHeader]);

  // Fetch Quick Keys
  const fetchQuickKeys = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/settings/quick-keys`, { headers: getAuthHeader() });
      // Ensure we have the right number of slots
      const keys = response.data.keys || [];
      const paddedKeys = [...keys];
      while (paddedKeys.length < QUICK_KEY_SLOTS) {
        paddedKeys.push(null);
      }
      setQuickKeys(paddedKeys.slice(0, QUICK_KEY_SLOTS));
    } catch (error) {
      // Initialize with empty slots
      setQuickKeys(Array(QUICK_KEY_SLOTS).fill(null));
    }
  }, [getAuthHeader]);

  // Save Quick Keys
  const saveQuickKeys = async (keys) => {
    try {
      await axios.put(`${API_URL}/settings/quick-keys`, { keys }, { headers: getAuthHeader() });
      toast.success('Quick Keys saved');
    } catch (error) {
      toast.error('Failed to save Quick Keys');
    }
  };

  // Assign product to quick key slot
  const assignQuickKey = (index, product) => {
    const newKeys = [...quickKeys];
    newKeys[index] = product ? {
      product_id: product.id,
      name: product.name,
      sell_price: product.sell_price,
      color: getRandomColor()
    } : null;
    setQuickKeys(newKeys);
    saveQuickKeys(newKeys);
    setEditingQuickKeyIndex(null);
    setQuickKeySearch('');
  };

  // Remove quick key
  const removeQuickKey = (index) => {
    const newKeys = [...quickKeys];
    newKeys[index] = null;
    setQuickKeys(newKeys);
    saveQuickKeys(newKeys);
  };

  // Get random color for quick key
  const getRandomColor = () => {
    const colors = [
      'bg-blue-500', 'bg-emerald-500', 'bg-purple-500', 'bg-orange-500',
      'bg-pink-500', 'bg-cyan-500', 'bg-amber-500', 'bg-rose-500',
      'bg-indigo-500', 'bg-teal-500', 'bg-lime-500', 'bg-fuchsia-500'
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  // Add product from quick key to cart
  const addFromQuickKey = (quickKey) => {
    if (!quickKey) return;
    const product = products.find(p => p.id === quickKey.product_id);
    if (product) {
      addToCart(product);
    } else {
      toast.error('Product not found');
    }
  };

  // Filter products for quick key assignment
  const quickKeyProductResults = quickKeySearch.length >= 2
    ? [...products].filter(p => {
        const term = quickKeySearch.toLowerCase();
        return p.name.toLowerCase().includes(term) ||
          p.sku?.toLowerCase().includes(term) ||
          p.brand?.toLowerCase().includes(term) ||
          p.category?.toLowerCase().includes(term) ||
          p.supplier_name?.toLowerCase().includes(term) ||
          p.barcode?.includes(quickKeySearch);
      }).sort((a, b) => a.name.localeCompare(b.name)).slice(0, 20)
    : [];

  // Check for overdue supplier stock counts
  const checkOverdueStockCounts = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/stock-count/overdue-suppliers`, { headers: getAuthHeader() });
      if (response.data && response.data.length > 0) {
        setOverdueSuppliers(response.data);
        setShowOverdueWarning(true);
      }
    } catch (error) {
      console.log('Could not check overdue stock counts');
    }
  }, [getAuthHeader]);

  // Fetch active price mismatches for current store
  const fetchPriceMismatches = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/price-mismatches?store=${selectedStore}`, { headers: getAuthHeader() });
      setPriceMismatches(response.data);
    } catch (err) {
      console.error('Failed to fetch price mismatches');
    }
  }, [selectedStore]);

  // Check for pending customer orders (once a day reminder)
  const checkPendingOrders = useCallback(async () => {
    const dismissKey = 'order_reminder_dismissed_' + new Date().toDateString();
    if (localStorage.getItem(dismissKey)) return;
    try {
      const response = await axios.get(`${API_URL}/customer-orders/count`, { headers: getAuthHeader() });
      const count = response.data?.not_ordered || 0;
      if (count > 0) {
        setPendingOrderCount(count);
        setShowOrderReminder(true);
      }
    } catch (err) {
      console.error('Failed to check pending orders');
    }
  }, []);

  // Report a price mismatch
  const reportPriceMismatch = async (product, labelPrice) => {
    try {
      const stockQty = selectedStore === 'orewa' ? (product.stock_orewa || 0) : (product.stock_eastridge || 0);
      await axios.post(`${API_URL}/price-mismatches`, {
        product_id: product.id,
        product_name: product.name,
        system_price: product.sell_price,
        sold_price: labelPrice,
        store: selectedStore,
        stock_on_hand: stockQty
      }, { headers: getAuthHeader() });
      toast.warning(`Price mismatch reported for ${product.name}`);
      fetchPriceMismatches();
    } catch (err) {
      console.error('Failed to report mismatch');
    }
  };

  // Resolve a price mismatch
  const resolvePriceMismatch = async (mismatchId) => {
    try {
      await axios.put(`${API_URL}/price-mismatches/${mismatchId}/resolve`, { notes: 'Shelf labels checked and corrected' }, { headers: getAuthHeader() });
      toast.success('Price mismatch resolved');
      fetchPriceMismatches();
    } catch (err) {
      toast.error('Failed to resolve');
    }
  };

  // Auto-detect price mismatches after sale
  const checkForPriceMismatches = (cartItems) => {
    cartItems.forEach(item => {
      if (item.is_gift_card) return;
      const product = products.find(p => p.id === item.product_id);
      if (product && Math.abs(item.unit_price - product.sell_price) > 0.01) {
        reportPriceMismatch(product, item.unit_price);
      }
    });
  };

  useEffect(() => {
    fetchProducts();
    testSmartpayConnection();
    fetchLoyaltySettings();
    fetchQuickKeys();
    checkOverdueStockCounts();
    fetchShortDatedItems();
    fetchNewProducts();
    fetchPriceMismatches();
    checkPendingOrders();
  }, [fetchProducts, testSmartpayConnection, fetchLoyaltySettings, fetchQuickKeys, checkOverdueStockCounts, fetchShortDatedItems, fetchNewProducts, fetchPriceMismatches, checkPendingOrders, selectedStore]);

  // Focus search on load
  useEffect(() => {
    if (searchInputRef.current && !showPaymentModal && !showCustomerModal) {
      searchInputRef.current.focus();
    }
  }, [cart, showPaymentModal, showCustomerModal]);

  // Self-heal: a payment window with nothing to pay for must never linger on screen
  useEffect(() => {
    if (showPaymentModal && cart.length === 0) {
      stopSmartpayPolling();
      setSmartpayPolling(false);
      setProcessing(false);
      setShowPaymentModal(false);
      setPaymentMethod('');
    }
  }, [showPaymentModal, cart.length]);

  // Global barcode scanner capture — catches scanner input even when search field isn't focused
  const addToCartRef = useRef(null);
  useEffect(() => { addToCartRef.current = addToCart; });
  // Unknown barcode → "link it to a product" dialog
  const [linkBarcodeScan, setLinkBarcodeScan] = useState(null);
  const handleBarcodeLinked = (product, data) => {
    setProducts(prev => prev.map(p => p.id === product.id
      ? { ...p, barcode: data.barcode, alt_barcodes: data.alt_barcodes, _barcode: [data.barcode, ...(data.alt_barcodes || [])].filter(Boolean).join('|') }
      : p));
    if (addToCartRef.current) addToCartRef.current({ ...product, barcode: data.barcode, alt_barcodes: data.alt_barcodes }, false, null, true);
    clearSearch();
    searchInputRef.current?.focus();
  };
  // When the Refund modal is open, a scanned product becomes a refund line
  // (NOT a cart item behind the modal). Returns true if the scan was consumed.
  const refundScanRef = useRef(null);
  useEffect(() => {
    refundScanRef.current = (product) => {
      if (!showRefundModal) return false;
      const price = product.sell_price || 0;
      setRefundMode('plain');
      setPlainRefundItems(prev => [...prev, {
        product_id: product.id,
        product_name: product.name,
        quantity: 1,
        unit_price: price
      }]);
      setPlainRefundAmount(prev => ((parseFloat(prev) || 0) + price).toFixed(2));
      toast.success(`${product.name} added to refund — $${price.toFixed(2)}`);
      return true;
    };
  });
  useEffect(() => {
    let scanBuffer = '';
    let scanTimeout = null;

    const handleGlobalKeyDown = (e) => {
      const tag = e.target.tagName;
      // Skip ALL inputs/textareas — the search input has its own onKeyDown that
      // handles Enter → addToCart. We only want this global handler when the
      // scanner fires while NO input is focused (e.g. cursor on the page body).
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.ctrlKey || e.altKey || e.metaKey) return;

      if (e.key === 'Enter' && scanBuffer.length >= 4) {
        e.preventDefault();
        const scan = scanBuffer.trim();
        console.log('[BARCODE SCAN] Captured:', JSON.stringify(scan), 'length:', scan.length);

        // Normalize numeric barcodes by stripping leading zeros AND any non-digit
        // characters (spaces, dashes, etc.). Handles scanners that drop/add leading
        // zeros and stored barcodes that contain stray whitespace.
        const digitsOnly = (s) => (s || '').toString().replace(/\D/g, '');
        const normNum = (s) => {
          const d = digitsOnly(s);
          return d ? (d.replace(/^0+/, '') || '0') : '';
        };
        const match = products.find(p => productMatchesScan(p, scan));

        if (match) {
          console.log('[BARCODE SCAN] Match:', match.name);
          const consumedByRefund = refundScanRef.current && refundScanRef.current(match);
          if (!consumedByRefund && addToCartRef.current) addToCartRef.current(match, false, null, true);
        } else {
          console.log('[BARCODE SCAN] No match for:', scan);
          if (/^\d{6,}$/.test(scan)) {
            toast.error(`Barcode "${scan}" not found — pick the product to link it`);
            setLinkBarcodeScan(scan);
          } else {
            toast.error(`Barcode "${scan}" not found`);
          }
        }

        scanBuffer = '';
        clearSearch();
        if (searchInputRef.current) searchInputRef.current.focus();
        return;
      }

      if (e.key.length === 1) {
        scanBuffer += e.key;
        // Mirror the live scan into the search box so staff can SEE what
        // they're scanning in real-time. (Only fires when no input is focused.)
        if (searchInputRef.current) searchInputRef.current.value = scanBuffer;
        clearTimeout(scanTimeout);
        scanTimeout = setTimeout(() => {
          scanBuffer = '';
          clearSearch();
        }, 200);
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown);
      clearTimeout(scanTimeout);
    };
  }, [products]);

  // Customer search
  const searchCustomers = async (query) => {
    if (!query || query.length < 2) {
      setCustomerResults([]);
      return;
    }
    setSearchingCustomer(true);
    try {
      const response = await axios.get(`${API_URL}/customers/search?q=${encodeURIComponent(query)}`, { headers: getAuthHeader() });
      setCustomerResults(response.data);
    } catch (error) {
      console.error('Customer search failed');
    } finally {
      setSearchingCustomer(false);
    }
  };

  useEffect(() => {
    const debounce = setTimeout(() => searchCustomers(customerSearch), 300);
    return () => clearTimeout(debounce);
  }, [customerSearch]);

  // Create new customer
  const createCustomer = async () => {
    if (!newCustomerName) {
      toast.error('Customer name is required');
      return;
    }
    if (!newCustomerPhone) {
      toast.error('Phone number is required for loyalty');
      return;
    }
    // Soft nudge — don't block, but make staff confirm they actually asked.
    // Marketing emails are the #1 way the user gets repeat foot traffic, so a
    // skipped email is a real revenue miss.
    if (!newCustomerEmail.trim()) {
      const ok = window.confirm(
        'Have you asked them for their email address?\n\n' +
        'Emails let us send marketing campaigns (sales, birthday offers, new product alerts) — that\'s how loyalty members come back in.\n\n' +
        'OK = save without email\nCancel = go back and type it in'
      );
      if (!ok) return;
    }
    try {
      const response = await axios.post(`${API_URL}/customers`, {
        name: newCustomerName,
        phone: newCustomerPhone,
        email: newCustomerEmail || ''
      }, { headers: getAuthHeader() });
      setSelectedCustomer(response.data);
      setShowCustomerModal(false);
      setNewCustomerName('');
      setNewCustomerPhone('');
      setNewCustomerEmail('');
      toast.success(`Customer ${response.data.name} added to loyalty!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create customer');
    }
  };

  // Fetch the current shift on mount + every 5 minutes so the login-nudge banner
  // updates as the day rolls over (e.g. Katie's morning → Karen's afternoon at 1:30pm)
  useEffect(() => {
    let cancelled = false;
    const fetchShift = async () => {
      try {
        const res = await axios.get(`${API_URL}/staff/shift-now?store=${selectedStore}`, { headers: getAuthHeader() });
        if (!cancelled) setShiftNow(res.data);
      } catch (_) { /* non-blocking */ }
    };
    fetchShift();
    const t = setInterval(fetchShift, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(t); };
  }, [getAuthHeader, selectedStore]);

  // Daily Target Tracker — fetch this staff member's sales-vs-target every 30s
  const lastPaceWarnRef = useRef(0);
  useEffect(() => {
    let cancelled = false;
    const staffName = activeStaff?.name || user?.name || '';
    const fetchTarget = async () => {
      try {
        const url = `${API_URL}/reports/today-target?store=${encodeURIComponent(selectedStore)}` +
                    (staffName ? `&staff_name=${encodeURIComponent(staffName)}` : '');
        const res = await axios.get(url, { headers: getAuthHeader() });
        if (cancelled) return;
        // Mid-shift pace alert — at most once per hour
        if (res.data?.pace?.behind_pace) {
          const nowTs = Date.now();
          if (nowTs - (lastPaceWarnRef.current || 0) > 60 * 60 * 1000) {
            lastPaceWarnRef.current = nowTs;
            const p = res.data.pace;
            toast.warning(
              `⏱ Pace check${staffName ? ` ${staffName}` : ''}: $${(res.data.actual || 0).toFixed(0)} sold vs $${p.expected_by_now.toFixed(0)} expected by now — $${p.behind_by.toFixed(0)} behind. Push the add-ons!`,
              { duration: 12000 }
            );
          }
        }
        setDailyProgress(prev => {
          // Trigger celebration when crossing from not-achieved → achieved
          if (res.data && res.data.achieved && (!prev || !prev.achieved) && !celebratedToday) {
            setCelebratedToday(true);
            try {
              toast.success(`🎉 Good work ${res.data.staff_name || staffName}! Daily target hit!`, { duration: 6000 });
            } catch (_) { /* ignore */ }
          }
          return res.data;
        });
      } catch (_) { /* non-blocking */ }
    };
    fetchTarget();
    const t = setInterval(fetchTarget, 30 * 1000);
    return () => { cancelled = true; clearInterval(t); };
  }, [getAuthHeader, selectedStore, activeStaff?.name, user?.name, celebratedToday]);

  // Reset the celebration flag when staff changes (so the new staff gets their own "good work")
  useEffect(() => {
    setCelebratedToday(false);
  }, [activeStaff?.name, selectedStore]);

  // Frequently Bought Together — fetch suggestions for the LAST item added to cart
  useEffect(() => {
    if (cart.length === 0) {
      setFbtSuggestions([]);
      return;
    }
    const lastItem = cart[cart.length - 1];
    if (!lastItem?.product_id) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/growth/fbt/${lastItem.product_id}?limit=4`, { headers: getAuthHeader() });
        if (cancelled) return;
        // Filter: not already in cart, not dismissed
        const inCartIds = new Set(cart.map(c => c.product_id));
        const filtered = (res.data || []).filter(s => !inCartIds.has(s.product_id) && !fbtDismissedIds.has(s.product_id));
        setFbtSuggestions(filtered.slice(0, 3));
      } catch (_) { /* non-blocking */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart.length, cart[cart.length - 1]?.product_id]);

  // Customer loyalty tier — fetch when selectedCustomer changes
  useEffect(() => {
    if (!selectedCustomer?.id) {
      setAppliedStoreCredit(0);
      setStoreCreditUseAmount('');
      setGoalSuggestions(null);
      setCustomerTier(null);
      return;
    }
    // Debt alert — remind staff to collect when an owing customer is in store
    if ((selectedCustomer.store_credit || 0) < 0) {
      toast.warning(
        `${selectedCustomer.name} OWES $${Math.abs(selectedCustomer.store_credit).toFixed(2)} — collect payment and add it back via Store Credit`,
        { duration: 8000 }
      );
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/growth/loyalty/customer/${selectedCustomer.id}`, { headers: getAuthHeader() });
        if (!cancelled) setCustomerTier(res.data);
      } catch (_) { /* non-blocking */ }
    })();
    return () => { cancelled = true; };
  }, [selectedCustomer?.id, getAuthHeader]);

  // Filter products based on search (name, SKU, barcode, supplier, or brand)
  const filteredProducts = useMemo(() => {
    const list = products.filter(p => {
      const stockQty = p.store_quantity ?? 0;
      if (!searchTerm || searchTerm.length === 0) {
        return stockQty > 0;
      }
      const term = searchTerm.toLowerCase().trim();
      // Barcode match (exact substring on full term)
      if (p._barcode.includes(searchTerm)) return true;
      // Multi-word AND match — every whitespace-separated word must appear
      // in the searchable text (in any order). e.g. "tws brahmi" matches "Brahmi TWS".
      const words = term.split(/\s+/).filter(Boolean);
      return words.every(w => p._search.includes(w));
    });
    // Products are pre-sorted A-Z at load, so filtering preserves order — no sort here
    return list;
  }, [products, searchTerm]);

  // Add item to cart
  const addToCart = (product, useShortDated = false, shortDatedInfo = null, fromScan = false) => {
    // CART LOCK — refuse to add items while an EFTPOS transaction is in flight.
    // This is the root-cause guard for the "cart total changed mid-payment" bug.
    if (smartpayPolling) {
      toast.error('EFTPOS in progress — cart is locked. Cancel the terminal transaction first.');
      return;
    }
    // Research cross-sell nudge + protocol add-on lookup (skipped for items added FROM a recommendation)
    if (!product.condition_source) {
      const nudge = findNudge(product.name);
      if (nudge) setScanNudge({ productName: product.name, message: nudge.message, protocols: [] });  // background only — never pops
      axios.get(`${API_URL}/conditions/match-product?name=${encodeURIComponent(product.name)}&store=${selectedStore}`, { headers: getAuthHeader() })
        .then(res => {
          const matches = res.data?.matches || [];
          if (!matches.length) return;
          setScanNudge(prev => (prev && prev.productName === product.name)
            ? { ...prev, protocols: matches }
            : { productName: product.name, message: nudge ? nudge.message : null, protocols: matches });
          setScanNudgeOpen(false);  // tips wait behind the "Protocol tips" button — staff open them when needed
        })
        .catch(() => {});
    }
    const existingItem = cart.find(item => item.product_id === product.id && !item.is_short_dated);
    const existingShortDated = cart.find(item => item.product_id === product.id && item.is_short_dated);
    
    // Check if product has short-dated stock available (one batch per expiry date)
    const sdBatches = shortDatedItems.filter(sd => sd.product_id === product.id && sd.quantity > 0);
    const shortDated = sdBatches[0];
    
    // If short-dated exists and user hasn't made a choice yet, show alert
    if (shortDated && !useShortDated && shortDatedInfo === null && !existingItem && !existingShortDated) {
      setShortDatedProduct({ product, shortDated, batches: sdBatches });
      setShowShortDatedAlert(true);
      return;
    }
    
    // Add as short-dated item with discount
    if (useShortDated && shortDatedInfo) {
      if (existingShortDated && existingShortDated.short_dated_id !== shortDatedInfo.id) {
        toast.error(`${product.name} is already in the cart at ${existingShortDated.discount_percent}% OFF — remove it first to sell a different batch`);
        return;
      }
      if (existingShortDated) {
        if (existingShortDated.quantity >= shortDatedInfo.quantity) {
          toast.error(`Only ${shortDatedInfo.quantity} short-dated units available`);
          return;
        }
        setCart(cart.map(item => 
          item.product_id === product.id && item.is_short_dated
            ? { ...item, quantity: item.quantity + 1 }
            : item
        ));
      } else {
        setCart([...cart, {
          product_id: product.id,
          name: product.name,  // Use regular name for receipt
          display_name: `${product.name} (SHORT DATED)`,  // Display name for POS screen only
          product_name: product.name,
          quantity: 1,
          unit_price: shortDatedInfo.discounted_price,
          original_price: shortDatedInfo.original_price,
          buy_price: product.buy_price || 0,
          max_quantity: shortDatedInfo.quantity,
          is_short_dated: true,
          short_dated_id: shortDatedInfo.id,
          discount_percent: shortDatedInfo.discount_percent,
          expiry_date: shortDatedInfo.expiry_date
        }]);
      }
      toast.success(`Added short-dated item at ${shortDatedInfo.discount_percent}% OFF!`);
      clearSearch();
      return;
    }
    
    // For bulk products, check bulk quantity
    if (product.is_bulk && product.portion_size > 0) {
      const bulkQty = selectedStore === 'orewa' 
        ? (product.bulk_quantity_orewa || 0) 
        : (product.bulk_quantity_eastridge || 0);
      const portionsAvailable = Math.floor(bulkQty / product.portion_size);
      
      if (existingItem) {
        const willGoNegative = existingItem.quantity >= portionsAvailable;
        setCart(cart.map(item => 
          item.product_id === product.id 
            ? { ...item, quantity: item.quantity + 1, max_quantity: 999, is_oos: item.is_oos || willGoNegative }
            : item
        ));
        if (willGoNegative) {
          setAllowNegativeStock(true);
          toast.warning(`${product.name} — bulk stock will go negative (fix in Inventory later)`);
          clearSearch();
          return;
        }
      } else {
        const bulkOOS = portionsAvailable <= 0;
        setCart([...cart, {
          product_id: product.id,
          name: `${product.name} (${product.portion_size}${product.bulk_unit})`,
          product_name: product.name,
          quantity: 1,
          unit_price: product.sell_price,
          buy_price: product.buy_price || 0,
          max_quantity: 999,
          is_bulk: true,
          is_oos: bulkOOS,
          portion_size: product.portion_size,
          bulk_unit: product.bulk_unit
        }]);
        if (bulkOOS) {
          setAllowNegativeStock(true);
          toast.warning(`${product.name} — 0 ${product.bulk_unit || 'gm'} in stock at ${getCurrentStore()?.name || selectedStore}, sold anyway (fix in Inventory later)`);
          clearSearch();
          return;
        }
      }
    } else {
      // Regular product
      const storeStock = product.store_quantity ?? 0;
      
      // Calculate price based on mode
      const unitPrice = wholesaleMode 
        ? Math.round((product.buy_price || 0) * 1.15 * 1.15 * 100) / 100  // buy + GST + 15% markup
        : product.sell_price;
      
      if (existingItem) {
        // Allow going into negative stock — staff can fix later via inventory
        // adjust. Warn but do NOT block.
        const willGoNegative = existingItem.quantity >= storeStock;
        setCart(cart.map(item =>
          item.product_id === product.id
            ? { ...item, quantity: item.quantity + 1, is_oos: item.is_oos || willGoNegative }
            : item
        ));
        if (willGoNegative) {
          setAllowNegativeStock(true);
          toast.warning(`${product.name} — stock will go negative (fix in Inventory later)`);
          clearSearch();
          return;  // suppress trailing toast.success — one warning is enough
        }
      } else {
        if (storeStock <= 0) {
          // Any add path — scanner OR manual — allows OOS/negative stock so
          // the sale can complete. No modal, no blocking. Staff can reconcile
          // in Inventory when they know the true count.
          setCart([...cart, {
            product_id: product.id,
            name: wholesaleMode ? `${product.name} (W)` : product.name,
            product_name: product.name,
            quantity: 1,
            unit_price: unitPrice,
            buy_price: product.buy_price || 0,
            original_retail_price: product.sell_price,
            is_wholesale: wholesaleMode,
            max_quantity: 999,
            is_oos: true,
            condition_source: product.condition_source || null,
          }]);
          setAllowNegativeStock(true);
          clearSearch();
          toast.warning(`${product.name} — 0 in stock, sold anyway (fix in Inventory later)`);
          return;
        }
        setCart([...cart, {
          product_id: product.id,
          name: wholesaleMode ? `${product.name} (W)` : product.name,
          product_name: product.name,
          quantity: 1,
          unit_price: unitPrice,
          buy_price: product.buy_price || 0,
          original_retail_price: product.sell_price,
          is_wholesale: wholesaleMode,
          max_quantity: storeStock > 0 ? storeStock : 999,
          condition_source: product.condition_source || null
        }]);
      }
    }
    clearSearch();
    toast.success(`Added ${product.name}`);
  };

  // Add OOS product to cart after confirmation
  const addOOSToCart = () => {
    if (!oosProduct) return;
    const product = oosProduct;
    const unitPrice = wholesaleMode 
      ? Math.round((product.buy_price || 0) * 1.15 * 1.15 * 100) / 100
      : product.sell_price;
    setCart([...cart, {
      product_id: product.id,
      name: wholesaleMode ? `${product.name} (W)` : product.name,
      product_name: product.name,
      quantity: 1,
      unit_price: unitPrice,
      buy_price: product.buy_price || 0,
      original_retail_price: product.sell_price,
      is_wholesale: wholesaleMode,
      max_quantity: 999,
      is_oos: true
    }]);
    setAllowNegativeStock(true);  // Flag to allow negative stock on checkout
    setShowOOSWarning(false);
    setOosProduct(null);
    clearSearch();
    toast.warning(`Added ${product.name} (OUT OF STOCK) - stock will go negative`);
  };

  // Update cart item quantity
  const updateQuantity = (productId, delta, isShortDated = false, isWholesale = false) => {
    setCart(cart.map(item => {
      if (item.product_id === productId && !!item.is_short_dated === isShortDated && !!item.is_wholesale === isWholesale) {
        // If the line is a swap/return (negative), the +/- buttons adjust its absolute size.
        if (item.is_swap || item.quantity < 0) {
          const newQty = item.quantity - delta;  // - flips: pressing "+" makes -1 -> -2 (more returned)
          if (newQty >= 0) return null;  // back to 0 → remove
          return { ...item, quantity: newQty };
        }
        const newQty = item.quantity + delta;
        if (newQty <= 0) return null;
        if (newQty > item.max_quantity && !item.is_oos) {
          // Never block the sale — stock goes negative and gets fixed in Inventory later
          setAllowNegativeStock(true);
          toast.warning(`${item.product_name || item.name} — only ${item.max_quantity} in stock, selling ${newQty} anyway`);
          return { ...item, quantity: newQty, is_oos: true };
        }
        return { ...item, quantity: newQty };
      }
      return item;
    }).filter(Boolean));
  };

  // Manually override the unit price on a cart line (e.g. price label mismatch).
  // Keeps original_price for the receipt + price-mismatch report so we don't lose history.
  // Per-line discount: staff enter "10%" (percent) or "5" ($ off this line)
  const setLineDiscount = (productId, isShortDated, isWholesale, raw, reason) => {
    const trimmed = (raw || '').trim();
    setCart(prev => prev.map(item => {
      if (!(item.product_id === productId && !!item.is_short_dated === isShortDated && !!item.is_wholesale === isWholesale)) return item;
      if (trimmed === '' || trimmed === '0') {
        const { line_discount_type, line_discount_value, line_discount_reason, ...rest } = item;
        return rest;
      }
      const isPercent = trimmed.includes('%');
      const num = parseFloat(trimmed.replace('%', '').replace('$', ''));
      if (isNaN(num) || num <= 0) {
        toast.error('Enter a discount like 10% or 5');
        return item;
      }
      if (isPercent && num > 100) {
        toast.error('Percent discount must be 100% or less');
        return item;
      }
      return { ...item, line_discount_type: isPercent ? 'percent' : 'dollar', line_discount_value: num, line_discount_reason: reason || null };
    }));
  };

  const updatePrice = (productId, newPriceStr, isShortDated = false, isWholesale = false) => {
    const newPrice = parseFloat(newPriceStr);
    if (Number.isNaN(newPrice) || newPrice < 0) {
      toast.error('Enter a valid price');
      return;
    }
    setCart(cart.map(item => {
      if (item.product_id === productId && !!item.is_short_dated === isShortDated && !!item.is_wholesale === isWholesale) {
        return {
          ...item,
          original_price: item.original_price ?? item.unit_price,
          unit_price: parseFloat(newPrice.toFixed(2)),
          price_overridden: true,
        };
      }
      return item;
    }));
    toast.success(`Price updated to $${newPrice.toFixed(2)}`);
  };

  // Swap flow: add the RETURNED item as a negative line — total becomes the balance to pay
  const addSwapReturn = (product) => {
    if (smartpayPolling) {
      toast.error('EFTPOS in progress — cart is locked. Cancel the terminal transaction first.');
      return;
    }
    const existing = cart.find(i => i.product_id === product.id && !i.is_short_dated);
    if (existing) {
      setCart(cart.map(i =>
        i.product_id === product.id && !i.is_short_dated
          ? { ...i, quantity: i.quantity - 1, is_swap: (i.quantity - 1) < 0, max_quantity: 999 }
          : i
      ));
    } else {
      setCart([...cart, {
        product_id: product.id,
        name: product.name,
        product_name: product.name,
        quantity: -1,
        unit_price: product.sell_price,
        buy_price: product.buy_price || 0,
        max_quantity: 999,
        is_swap: true,
      }]);
    }
    setShowSwapModal(false);
    setSwapSearch('');
    toast.success(`${product.name} marked as RETURNED (−$${(product.sell_price || 0).toFixed(2)}) — now scan the replacement item`);
  };

  // Toggle a line between SALE (+qty) and RETURN/SWAP (-qty). Used for exchanges:  // staff scans the returned item, hits the swap icon → goes to -1, then scans
  // the replacement item normally → cart total = price difference.
  const toggleSwap = (productId, isShortDated = false, isWholesale = false) => {
    setCart(cart.map(item => {
      if (item.product_id === productId && !!item.is_short_dated === isShortDated && !!item.is_wholesale === isWholesale) {
        const flipped = -item.quantity;
        return { ...item, quantity: flipped, is_swap: flipped < 0 };
      }
      return item;
    }));
  };

  // Remove item from cart
  const removeFromCart = (productId, isShortDated = false, isWholesale = false) => {
    setCart(cart.filter(item => !(item.product_id === productId && !!item.is_short_dated === isShortDated && !!item.is_wholesale === isWholesale)));
  };

  // Clear entire cart
  const clearCart = () => {
    setCart([]);
    setSelectedCustomer(null);
    setRedeemPoints(0);
    setPointsDiscount(0);
    toast.info('Cart cleared');
  };

  // Per-line discount amount (dollars) for a cart item
  const lineDiscountAmount = (item) => {
    if (!item.line_discount_value || item.quantity < 0 || item.is_gift_card) return 0;
    const gross = item.quantity * item.unit_price;
    const d = item.line_discount_type === 'percent'
      ? gross * (item.line_discount_value / 100)
      : Math.min(item.line_discount_value, gross);
    return +Math.max(0, d).toFixed(2);
  };

  // Calculate totals - NZ prices are GST-inclusive
  const cartTotal = cart.reduce((sum, item) => sum + (item.quantity * item.unit_price) - lineDiscountAmount(item), 0);
  const subtotal = cartTotal / 1.15; // Ex-GST amount
  const tax = cartTotal - subtotal; // GST portion (15%)
  const totalBeforeDiscount = cartTotal;
  const giftCardDiscount = appliedGiftCard?.redeemAmount || 0;
  const storeCreditDiscount = appliedStoreCredit || 0;
  // Manual cart-level discount (computed from appliedDiscount)
  const manualDiscount = (() => {
    if (!appliedDiscount.type || !appliedDiscount.value) return 0;
    if (appliedDiscount.type === 'percent') {
      return Math.min(cartTotal, +(cartTotal * (appliedDiscount.value / 100)).toFixed(2));
    }
    return Math.min(cartTotal, +appliedDiscount.value.toFixed(2));
  })();
  // Final total. NOTE: we don't clamp to >= 0 because of SWAP/EXCHANGE flow —
  // a negative total means the customer is owed a refund (price of returned
  // item > price of replacement). The payment screen handles both cases.
  const total = totalBeforeDiscount - pointsDiscount - giftCardDiscount - storeCreditDiscount - manualDiscount;
  const change = cashReceived ? parseFloat(cashReceived) - total : 0;
  
  // Points earned (customizable rate)
  // Loyalty credit earned — fixed 3% on the post-discount total, rounded down.
  // No longer reads from server settings to prevent accidental misconfig.
  const pointsEarned = Math.floor(Math.max(0, total) * LOYALTY_EARN_RATE);

  // Check margin for all cart items (using ex-GST prices for accurate profit)
  const checkMargins = () => {
    const itemsBelowMargin = cart.filter(item => {
      if (item.is_gift_card) return false; // Skip gift cards
      if (item.is_misc) return false; // Skip misc items (no margin tracking)
      const buyPrice = item.buy_price || 0; // Buy price is ex-GST
      const sellPriceExGst = (item.unit_price || 0) / 1.15; // Convert sell price to ex-GST
      if (sellPriceExGst <= 0) return true; // Invalid price
      const margin = ((sellPriceExGst - buyPrice) / sellPriceExGst) * 100;
      return margin < MIN_MARGIN_PERCENT;
    }).map(item => {
      const buyPrice = item.buy_price || 0;
      const sellPriceIncGst = item.unit_price || 0;
      const sellPriceExGst = sellPriceIncGst / 1.15;
      const margin = sellPriceExGst > 0 ? ((sellPriceExGst - buyPrice) / sellPriceExGst) * 100 : 0;
      return {
        ...item,
        margin: margin.toFixed(1),
        buy_price: buyPrice,
        sell_price: sellPriceIncGst,
        sell_price_ex_gst: sellPriceExGst.toFixed(2)
      };
    });
    return itemsBelowMargin;
  };

  // Handle Pay button click with margin check
  const handlePayClick = () => {
    const itemsBelowMargin = checkMargins();
    if (itemsBelowMargin.length > 0) {
      setLowMarginItems(itemsBelowMargin);
      // Pre-fill the editing input with the SUGGESTED minimum sell price (rounded
      // up to the nearest 5c). Previously this was pre-filled with the current
      // sell_price, which meant clicking ✓ "saved" the same low-margin price and
      // nothing visibly changed — the bug the user reported.
      const initialPrices = {};
      itemsBelowMargin.forEach(item => {
        const minPriceExGst = item.buy_price / (1 - MIN_MARGIN_PERCENT / 100);
        const minPriceIncGst = minPriceExGst * 1.15;
        // Round up to nearest 5c for a tidy shelf price
        const rounded = Math.ceil(minPriceIncGst * 20) / 20;
        initialPrices[item.product_id] = rounded.toFixed(2);
      });
      setEditingPrices(initialPrices);
      setShowMarginWarning(true);
      return;
    }
    setShowPaymentModal(true);
  };

  // Update product price from warning dialog
  const handleUpdatePrice = async (productId, newPrice) => {
    setUpdatingPrice(true);
    try {
      await axios.put(`${API_URL}/products/${productId}`, {
        sell_price: parseFloat(newPrice)
      }, { headers: getAuthHeader() });
      
      // Update cart with new price
      setCart(cart.map(item => 
        item.product_id === productId 
          ? { ...item, unit_price: parseFloat(newPrice) }
          : item
      ));
      
      // Refresh products list
      await fetchProducts();
      
      // Recheck margins (using ex-GST prices)
      const updatedItems = lowMarginItems.map(item => {
        if (item.product_id === productId) {
          const newSellPrice = parseFloat(newPrice);
          const newSellPriceExGst = newSellPrice / 1.15;
          const margin = ((newSellPriceExGst - item.buy_price) / newSellPriceExGst) * 100;
          return { ...item, sell_price: newSellPrice, sell_price_ex_gst: newSellPriceExGst.toFixed(2), margin: margin.toFixed(1) };
        }
        return item;
      }).filter(item => parseFloat(item.margin) < MIN_MARGIN_PERCENT);
      
      setLowMarginItems(updatedItems);
      
      if (updatedItems.length === 0) {
        toast.success('All prices updated! Margins are now OK.');
      } else {
        toast.success('Price updated');
      }
    } catch (error) {
      toast.error('Failed to update price');
    } finally {
      setUpdatingPrice(false);
    }
  };

  // Proceed with sale despite low margin (just warning)
  const proceedWithLowMargin = () => {
    setShowMarginWarning(false);
    setShowPaymentModal(true);
  };

  // Calculate required price for 40% margin
  // Calculate minimum sell price (inc GST) to achieve target margin on ex-GST values
  const getMinSellPrice = (buyPrice) => {
    // Calculate ex-GST min price for target margin
    const minPriceExGst = buyPrice / (1 - MIN_MARGIN_PERCENT / 100);
    // Add GST back for display (since sell prices are GST-inclusive)
    return minPriceExGst * 1.15;
  };

  // Quick cash buttons
  const quickCashAmounts = [20, 50, 100, Math.ceil(total / 10) * 10];

  // Handle points redemption
  const handleRedeemPoints = () => {
    if (!selectedCustomer) return;
    // Max redeem = the customer's current credit, capped at the cart total
    // (you can't refund more than they're paying).
    const maxRedeem = Math.min(selectedCustomer.loyalty_points, Math.floor(totalBeforeDiscount));
    const pointsToRedeem = Math.min(redeemPoints, maxRedeem);
    setPointsDiscount(pointsToRedeem * loyaltySettings.redemption_value);
  };
  
  // Gift Card Functions
  const handleSellGiftCard = async () => {
    if (!giftCardAmount || parseFloat(giftCardAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    setGiftCardLoading(true);
    try {
      const response = await axios.post(`${API_URL}/giftcards`, {
        amount: parseFloat(giftCardAmount),
        customer_name: giftCardRecipient || 'Anonymous'
      }, { headers: getAuthHeader() });
      
      toast.success(`Gift card created: ${response.data.code}`);
      
      // Add gift card to cart as a product
      setCart([...cart, {
        product_id: `gc-${response.data.id}`,
        product_name: `Gift Card (${response.data.code})`,
        quantity: 1,
        unit_price: parseFloat(giftCardAmount),
        max_quantity: 1,
        is_gift_card: true
      }]);
      
      setShowGiftCardModal(false);
      setGiftCardAmount('');
      setGiftCardRecipient('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create gift card');
    } finally {
      setGiftCardLoading(false);
    }
  };
  
  const handleLookupGiftCard = async () => {
    if (!giftCardCode) {
      toast.error('Please enter a gift card code');
      return;
    }
    setGiftCardLoading(true);
    try {
      const response = await axios.get(`${API_URL}/giftcards/lookup/${giftCardCode}`, { headers: getAuthHeader() });
      setGiftCardLookup(response.data);
      setGiftCardRedeemAmount(response.data.balance.toFixed(2));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gift card not found');
      setGiftCardLookup(null);
    } finally {
      setGiftCardLoading(false);
    }
  };
  
  const handleApplyGiftCard = () => {
    if (!giftCardLookup) return;
    const amount = Math.min(parseFloat(giftCardRedeemAmount) || 0, giftCardLookup.balance, totalBeforeDiscount);
    if (amount <= 0) {
      toast.error('Invalid redemption amount');
      return;
    }
    setAppliedGiftCard({ ...giftCardLookup, redeemAmount: amount });
    setShowGiftCardModal(false);
    toast.success(`Gift card applied: -$${amount.toFixed(2)}`);
  };
  
  const removeAppliedGiftCard = () => {
    setAppliedGiftCard(null);
  };
  
  // Refund Functions
  const resetRefundState = () => {
    setRefundMode('menu');
    setRefundSaleId('');
    setRefundSale(null);
    setRefundItems([]);
    setRefundReason('');
    setRefundMethod('cash');
    setPhoneSearch('');
    setPhoneSales([]);
    setPlainRefundAmount('');
    setPlainRefundItems([]);
    setRefundCreditCustomer(null);
    setRefundCreditSearch('');
    setRefundCreditResults([]);
  };

  const searchRefundCreditCustomer = async (q) => {
    setRefundCreditSearch(q);
    if (!q || q.length < 2) { setRefundCreditResults([]); return; }
    try {
      const res = await axios.get(`${API_URL}/customers/search?q=${encodeURIComponent(q)}`, { headers: getAuthHeader() });
      setRefundCreditResults(res.data || []);
    } catch (_) { /* non-blocking */ }
  };

  const handlePhoneSearch = async () => {
    if (!phoneSearch || phoneSearch.length < 3) {
      toast.error('Enter at least 3 digits');
      return;
    }
    setPhoneSearching(true);
    try {
      const response = await axios.get(`${API_URL}/sales/search-by-phone/${phoneSearch}`, { headers: getAuthHeader() });
      setPhoneSales(response.data);
      if (response.data.length === 0) toast.info('No sales found for this phone number');
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setPhoneSearching(false);
    }
  };

  const selectSaleForRefund = (sale) => {
    setRefundSale(sale);
    setRefundSaleId(sale.id);
    setRefundItems(sale.items.map(item => ({ ...item, refund: false, refund_qty: item.quantity })));
    if (sale.customer_id) setRefundCreditCustomer({ id: sale.customer_id, name: sale.customer_name || 'Customer', phone: sale.customer_phone || '' });
    setRefundMode('sale');
  };

  const handleLookupSale = async () => {
    if (!refundSaleId) {
      toast.error('Please enter a sale/receipt ID');
      return;
    }
    setRefundLoading(true);
    try {
      const response = await axios.get(`${API_URL}/sales/${refundSaleId}`, { headers: getAuthHeader() });
      setRefundSale(response.data);
      setRefundItems(response.data.items.map(item => ({ ...item, refund: false, refund_qty: item.quantity })));
      if (response.data.customer_id) setRefundCreditCustomer({ id: response.data.customer_id, name: response.data.customer_name || 'Customer', phone: response.data.customer_phone || '' });
      setRefundMode('sale');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sale not found');
      setRefundSale(null);
    } finally {
      setRefundLoading(false);
    }
  };
  
  const toggleRefundItem = (index) => {
    setRefundItems(refundItems.map((item, i) => 
      i === index ? { ...item, refund: !item.refund } : item
    ));
  };
  
  const updateRefundQty = (index, qty) => {
    setRefundItems(refundItems.map((item, i) => 
      i === index ? { ...item, refund_qty: Math.min(Math.max(1, qty), item.quantity) } : item
    ));
  };
  
  const calculateRefundTotal = () => {
    return refundItems
      .filter(item => item.refund)
      .reduce((sum, item) => sum + (item.refund_qty * item.unit_price), 0);
  };
  
  const handleProcessRefund = async () => {
    const itemsToRefund = refundItems.filter(item => item.refund);
    if (itemsToRefund.length === 0) {
      toast.error('Please select items to refund');
      return;
    }
    if (refundMethod === 'store_credit' && !refundCreditCustomer?.id) {
      toast.error('Select a customer to receive the store credit');
      return;
    }
    
    const refundAmount = calculateRefundTotal();

    // Helper: build a refund receipt and send to printer (best-effort)
    const printRefundReceipt = (method, amount, items) => {
      try {
        const store = getCurrentStore();
        const payload = {
          id: `REFUND-${Date.now()}`,
          created_at: new Date().toISOString(),
          store_name: store?.name || selectedStore || '',
          store_phone: store?.phone || '',
          gst_number: store?.gst_number || '',
          customer_name: 'Walk-in Customer',
          is_refund: true,
          refund_reason: refundReason || '',
          items: (items || []).map(it => ({
            ...it,
            quantity: -Math.abs(it.quantity || it.refund_qty || 1),
          })),
          subtotal: -Math.abs(amount / 1.15),
          tax_amount: -Math.abs(amount - amount / 1.15),
          total_amount: -Math.abs(amount),
          payment_method: method,
          cash_received: null,
          change_given: null,
          points_discount: 0,
          gift_card_discount: 0,
          gift_card_code: '',
          split_payment: false,
          points_earned: 0,
          points_balance: 0,
        };
        qzPrintReceipt(payload);
      } catch (e) {
        console.log('Refund receipt print error:', e.message);
      }
    };

    // For card refunds, prompt EFTPOS first; only commit on approval
    if (refundMethod === 'card' && smartpayConnected && selectedTerminalId) {
      setRefundLoading(true);
      const ok = await runSmartpayTransaction(refundAmount, 'Card.Refund', async () => {
        // On approval, post the refund to backend
        try {
          await axios.post(`${API_URL}/refunds`, {
            sale_id: refundSaleId,
            items: itemsToRefund.map(item => ({
              product_id: item.product_id,
              product_name: item.product_name,
              quantity: item.refund_qty,
              unit_price: item.unit_price
            })),
            refund_amount: refundAmount,
            reason: refundReason,
            refund_method: 'card'
          }, { headers: getAuthHeader() });
          toast.success(`Card refund of $${refundAmount.toFixed(2)} processed`);
          printRefundReceipt('card', refundAmount, itemsToRefund.map(it => ({
            product_name: it.product_name, quantity: it.refund_qty, unit_price: it.unit_price
          })));
          setShowRefundModal(false);
          resetRefundState();
          setCart([]);
          fetchProducts();
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Refund approved on terminal but failed to record locally');
        } finally {
          setRefundLoading(false);
        }
      }, () => {
        // On terminal cancel/decline/timeout — re-enable the refund button so user can retry
        setRefundLoading(false);
      });
      if (!ok) setRefundLoading(false);
      return;
    }

    // Non-card refund → write locally (cash, store credit, etc.)
    setRefundLoading(true);
    try {
      const res = await axios.post(`${API_URL}/refunds`, {
        sale_id: refundSaleId,
        items: itemsToRefund.map(item => ({
          product_id: item.product_id,
          product_name: item.product_name,
          quantity: item.refund_qty,
          unit_price: item.unit_price
        })),
        refund_amount: refundAmount,
        reason: refundReason,
        refund_method: refundMethod,
        customer_id: refundMethod === 'store_credit' ? refundCreditCustomer?.id : null,
        staff_name: activeStaff?.name || user?.name || ''
      }, { headers: getAuthHeader() });
      
      if (refundMethod === 'store_credit') {
        toast.success(`$${refundAmount.toFixed(2)} added to ${refundCreditCustomer.name}'s store credit (balance $${(res.data.store_credit_balance || 0).toFixed(2)})`);
      } else {
        toast.success(`${refundMethod.charAt(0).toUpperCase() + refundMethod.slice(1)} refund of $${refundAmount.toFixed(2)} processed`);
      }
      printRefundReceipt(refundMethod, refundAmount, itemsToRefund.map(it => ({
        product_name: it.product_name, quantity: it.refund_qty, unit_price: it.unit_price
      })));
      setShowRefundModal(false);
      resetRefundState();
      setCart([]);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process refund');
    } finally {
      setRefundLoading(false);
    }
  };

  // Print a receipt for a plain (unlinked) refund — best-effort, never blocks the refund
  const printPlainRefundReceipt = (method, amount) => {
    try {
      const store = getCurrentStore();
      const refundLines = plainRefundItems.length
        ? plainRefundItems
        : [{ product_name: 'Refund', quantity: 1, unit_price: amount }];
      const payload = {
        id: `REFUND-${Date.now()}`,
        created_at: new Date().toISOString(),
        store_name: store?.name || selectedStore || '',
        store_phone: store?.phone || '',
        gst_number: store?.gst_number || '',
        customer_name: 'Walk-in Customer',
        is_refund: true,
        refund_reason: refundReason || '',
        items: refundLines.map(it => ({ ...it, quantity: -Math.abs(it.quantity || 1) })),
        subtotal: -Math.abs(amount / 1.15),
        tax_amount: -Math.abs(amount - amount / 1.15),
        total_amount: -Math.abs(amount),
        payment_method: method,
        cash_received: null,
        change_given: null,
        points_discount: 0,
        gift_card_discount: 0,
        gift_card_code: '',
        split_payment: false,
        points_earned: 0,
        points_balance: 0,
      };
      qzPrintReceipt(payload);
    } catch (e) {
      console.log('Plain refund receipt print error:', e.message);
    }
  };

  // Shared customer picker for store-credit refunds (plain + sale-linked)
  const renderRefundCreditPicker = () => (
    <div className="space-y-2 p-3 border-2 border-amber-200 bg-amber-50/50 rounded-lg" data-testid="refund-credit-picker">
      <Label className="text-amber-800">Credit to Customer</Label>
      {refundCreditCustomer ? (
        <div className="flex items-center justify-between p-2 bg-white border rounded-sm" data-testid="refund-credit-customer-chip">
          <div>
            <p className="font-medium text-sm">{refundCreditCustomer.name}</p>
            {refundCreditCustomer.phone && <p className="text-xs text-slate-500">{refundCreditCustomer.phone}</p>}
          </div>
          <Button variant="ghost" size="sm" onClick={() => setRefundCreditCustomer(null)} data-testid="refund-credit-customer-clear">
            <X className="w-4 h-4" />
          </Button>
        </div>
      ) : (
        <>
          <Input
            placeholder="Search customer by name or phone..."
            value={refundCreditSearch}
            onChange={(e) => searchRefundCreditCustomer(e.target.value)}
            data-testid="refund-credit-customer-search"
          />
          {refundCreditResults.length > 0 && (
            <div className="max-h-40 overflow-y-auto border rounded-sm bg-white divide-y">
              {refundCreditResults.map(c => (
                <button
                  key={c.id}
                  className="w-full text-left p-2 hover:bg-amber-50 text-sm"
                  onClick={() => { setRefundCreditCustomer(c); setRefundCreditResults([]); setRefundCreditSearch(''); }}
                  data-testid={`refund-credit-customer-result-${c.id}`}
                >
                  <span className="font-medium">{c.name}</span>
                  <span className="text-slate-500 ml-2">{c.phone}</span>
                  <span className="text-amber-600 ml-2">${(c.store_credit || 0).toFixed(2)} credit</span>
                </button>
              ))}
            </div>
          )}
          <p className="text-xs text-amber-700">The refund amount will be added to this customer's store credit balance.</p>
        </>
      )}
    </div>
  );

  const handlePlainRefund = async () => {
    const amount = parseFloat(plainRefundAmount);
    if (!amount || amount <= 0) {
      toast.error('Enter a valid refund amount');
      return;
    }
    if (refundMethod === 'store_credit' && !refundCreditCustomer?.id) {
      toast.error('Select a customer to receive the store credit');
      return;
    }

    if (refundMethod === 'card' && smartpayConnected && selectedTerminalId) {
      setRefundLoading(true);
      const ok = await runSmartpayTransaction(amount, 'Card.Refund', async () => {
        try {
          await axios.post(`${API_URL}/refunds/plain`, {
            items: plainRefundItems,
            refund_amount: amount,
            reason: refundReason,
            refund_method: 'card',
            store: selectedStore
          }, { headers: getAuthHeader() });
          toast.success(`Card refund of $${amount.toFixed(2)} processed`);
          printPlainRefundReceipt('card', amount);
          setShowRefundModal(false);
          resetRefundState();
          setCart([]);
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Refund approved on terminal but failed to record locally');
        } finally {
          setRefundLoading(false);
        }
      }, () => {
        setRefundLoading(false);
      });
      if (!ok) setRefundLoading(false);
      return;
    }

    setRefundLoading(true);
    try {
      const res = await axios.post(`${API_URL}/refunds/plain`, {
        items: plainRefundItems,
        refund_amount: amount,
        reason: refundReason,
        refund_method: refundMethod,
        store: selectedStore,
        customer_id: refundMethod === 'store_credit' ? refundCreditCustomer?.id : null,
        staff_name: activeStaff?.name || user?.name || ''
      }, { headers: getAuthHeader() });
      
      if (refundMethod === 'store_credit') {
        toast.success(`$${amount.toFixed(2)} added to ${refundCreditCustomer.name}'s store credit (balance $${(res.data.store_credit_balance || 0).toFixed(2)})`);
      } else {
        toast.success(`${refundMethod.charAt(0).toUpperCase() + refundMethod.slice(1)} refund of $${amount.toFixed(2)} processed`);
      }
      printPlainRefundReceipt(refundMethod, amount);
      setShowRefundModal(false);
      resetRefundState();
      setCart([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process refund');
    } finally {
      setRefundLoading(false);
    }
  };
  
  // Product Edit Functions
  const openEditProduct = (product, e) => {
    e.stopPropagation();
    setEditProduct(product);
    setEditProductData({
      name: product.name || '',
      sell_price: product.sell_price || 0,
      buy_price: product.buy_price || 0,
      stock_orewa: product.stock_orewa || 0,
      stock_eastridge: product.stock_eastridge || 0,
      barcode: product.barcode || '',
      sku: product.sku || '',
      category: product.category || '',
      brand: product.brand || '',
      description: product.description || '',
    });
    setShowEditProduct(true);
  };

  const handleSaveEditProduct = async () => {
    if (!editProduct) return;
    setEditProductSaving(true);
    try {
      const payload = {
        name: editProductData.name,
        sell_price: parseFloat(editProductData.sell_price) || 0,
        buy_price: parseFloat(editProductData.buy_price) || 0,
        stock_orewa: parseInt(editProductData.stock_orewa) || 0,
        stock_eastridge: parseInt(editProductData.stock_eastridge) || 0,
        barcode: editProductData.barcode,
        sku: editProductData.sku,
        category: editProductData.category,
        brand: editProductData.brand,
        description: editProductData.description,
      };
      const putRes = await axios.put(`${API_URL}/products/${editProduct.id}`, payload, { headers: getAuthHeader() });
      const sync = putRes.data?.shopify_sync;
      const isShopify = editProduct.shopify_product_id || editProduct.shopify_variant_id;
      if (sync === 'synced') {
        toast.success('Product updated — Shopify price updated too ✓');
      } else if (sync && (sync.startsWith('failed') || sync.startsWith('error'))) {
        toast.error(`Saved here BUT Shopify update FAILED (${sync}) — the website price is still the old one!`, { duration: 10000 });
      } else if (sync === 'skipped_preview') {
        toast.warning('Product updated — Shopify push skipped (preview environment)', { duration: 6000 });
      } else if (isShopify) {
        toast.warning('Product updated — Shopify was not updated (no price/details change detected)', { duration: 6000 });
      } else {
        toast.success('Product updated (not linked to Shopify)');
      }
      setShowEditProduct(false);
      setEditProduct(null);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update product');
    } finally {
      setEditProductSaving(false);
    }
  };

  // Loyalty Settings Functions
  const handleSaveLoyaltySettings = async () => {
    setLoyaltyLoading(true);
    try {
      await axios.put(`${API_URL}/settings/loyalty`, loyaltySettings, { headers: getAuthHeader() });
      toast.success('Loyalty settings saved');
      setShowLoyaltySettingsModal(false);
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setLoyaltyLoading(false);
    }
  };
  
  // Misc Sale Functions
  const handleMiscSale = async () => {
    if (!miscSaleDesc || !miscSaleAmount || parseFloat(miscSaleAmount) <= 0) {
      toast.error('Please enter description and amount');
      return;
    }
    setMiscLoading(true);
    try {
      await axios.post(`${API_URL}/misc-sales`, {
        description: miscSaleDesc,
        amount: parseFloat(miscSaleAmount),
        store: selectedStore
      }, { headers: getAuthHeader() });
      
      toast.success(`Misc sale recorded: $${parseFloat(miscSaleAmount).toFixed(2)}`);
      setMiscSaleDesc('');
      setMiscSaleAmount('');
      setShowMiscModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record misc sale');
    } finally {
      setMiscLoading(false);
    }
  };

  // Add misc item to cart (for items not in system)
  const handleAddMiscItemToCart = () => {
    if (!miscSaleDesc.trim()) {
      toast.error('Please enter a product description');
      return;
    }
    if (!miscSaleAmount || parseFloat(miscSaleAmount) <= 0) {
      toast.error('Please enter a valid price');
      return;
    }

    const price = parseFloat(miscSaleAmount);
    const qty = Math.max(1, parseInt(miscItemQty) || 1);

    // Add misc item to cart
    setCart([...cart, {
      product_id: `misc-${Date.now()}`,
      name: miscSaleDesc,
      product_name: miscSaleDesc,
      quantity: qty,
      unit_price: price,
      buy_price: 0, // No cost tracking for misc items
      max_quantity: 999,
      is_misc: true
    }]);

    toast.success(`Added: ${miscSaleDesc} x${qty}`);
    setMiscSaleDesc('');
    setMiscSaleAmount('');
    setMiscItemQty(1);
    setShowMiscModal(false);
  };
  
  const handleCashMovement = async () => {
    if (!cashMovementAmount || parseFloat(cashMovementAmount) <= 0 || !cashMovementReason) {
      toast.error('Please enter amount and reason');
      return;
    }
    setMiscLoading(true);
    try {
      await axios.post(`${API_URL}/cash-movements`, {
        type: cashMovementType,
        amount: parseFloat(cashMovementAmount),
        reason: cashMovementReason,
        store: selectedStore
      }, { headers: getAuthHeader() });
      
      toast.success(`Cash ${cashMovementType === 'in' ? 'added' : 'removed'}: $${parseFloat(cashMovementAmount).toFixed(2)}`);
      setCashMovementAmount('');
      setCashMovementReason('');
      setShowMiscModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record cash movement');
    } finally {
      setMiscLoading(false);
    }
  };
  
  // Stock Report Functions
  const fetchStockReport = async () => {
    setStockReportLoading(true);
    try {
      const response = await axios.get(`${API_URL}/reports/stock-issues?low_stock_threshold=${lowStockThreshold}`, { headers: getAuthHeader() });
      setStockReport(response.data);
    } catch (error) {
      toast.error('Failed to load stock report');
    } finally {
      setStockReportLoading(false);
    }
  };
  
  const openStockReport = () => {
    setShowStockReportModal(true);
    fetchStockReport();
  };

  // Initiate Smartpay payment
  const initiateSmartpayPayment = async () => {
    setProcessing(true);
    try {
      const ok = await runSmartpayTransaction(total, 'Card.Purchase');
      if (!ok) {
        setProcessing(false);
      }
    } catch (e) {
      setProcessing(false);
    }
  };

  // Generic SmartConnect runner used by purchase / refund / split-card.
  // Returns true if the request was sent successfully (poll begins). Caller wraps onApproved with the
  // commit logic (e.g. write the refund). onRejected is called when terminal cancels/declines/times out.
  const smartpayInitiatingRef = useRef(false);
  const runSmartpayTransaction = async (amount, transactionType, onApproved = null, onRejected = null) => {
    if (smartpayPolling || smartpayInitiatingRef.current) {
      toast.error('EFTPOS is already waiting for the terminal — cancel it first if you need to restart.');
      return false;
    }
    smartpayInitiatingRef.current = true;
    try {
      return await _runSmartpayTransaction(amount, transactionType, onApproved, onRejected);
    } finally {
      smartpayInitiatingRef.current = false;
    }
  };

  const _runSmartpayTransaction = async (amount, transactionType, onApproved = null, onRejected = null) => {
    // Claim this attempt up front: if staff press Cancel (or pay another way) while SmartConnect is slow to
    // answer, the late reply must be ignored instead of reviving the EFTPOS flow and hijacking the sale.
    const attemptId = ++smartpayPollIdRef.current;
    const cancelledMeanwhile = () => attemptId !== smartpayPollIdRef.current;
    // ALWAYS refetch the terminal list right before initiating — this guarantees we
    // pick up the latest register_id even if Hardware Settings was changed in
    // another tab (e.g. terminal swap) without the POS being refreshed.
    let freshTerminal = null;
    try {
      const storesRes = await axios.get(`${API_URL}/stores`, { headers: getAuthHeader() });
      const me = (storesRes.data || []).find(s => s.code === selectedStore);
      const freshTerminals = Array.isArray(me?.terminals) ? me.terminals.filter(t => t.paired) : [];
      // Prefer the currently-selected terminal by id; if it was removed/replaced and id no longer
      // matches, fall back to the first paired terminal.
      freshTerminal = freshTerminals.find(t => t.id === selectedTerminalId) || freshTerminals[0] || null;
      // Quietly refresh the in-memory list too so the UI dropdown shows the latest.
      setSmartpayTerminals(freshTerminals.map((t, idx) => ({
        ...t,
        id: t.id || `${selectedStore}-${idx}`,
        label: t.name || t.register_name || t.register_id || `Terminal ${idx + 1}`,
      })));
      if (freshTerminal && freshTerminal.id !== selectedTerminalId) {
        setSelectedTerminalId(freshTerminal.id);
      }
    } catch (e) {
      console.warn('[EFTPOS] fresh terminal fetch failed, using cached', e?.message);
      freshTerminal = smartpayTerminals.find(t => t.id === selectedTerminalId);
    }

    const terminal = freshTerminal;
    if (cancelledMeanwhile()) return false;
    if (!terminal || !terminal.register_id) {
      toast.error('No paired terminal selected. Pair one in Hardware → Smartpay first.');
      return false;
    }
    try {
      const body = {
        amount,
        transaction_type: transactionType,
        reference: `${transactionType.includes('Refund') ? 'REF' : 'SALE'}-${Date.now()}`,
        register_id: terminal.register_id,
        environment: terminal.environment || 'dev',
      };
      console.log('[EFTPOS] sending to SmartConnect:', { register_id: terminal.register_id, amount, env: body.environment, terminal_name: terminal.label || terminal.name });
      const response = await axios.post(`${API_URL}/smartpay/initiate`, body, { headers: getAuthHeader() });
      if (cancelledMeanwhile()) {
        console.warn('[EFTPOS] attempt was cancelled before SmartConnect replied — ignoring late reply');
        toast.warning('EFTPOS reply arrived after you cancelled — ignored. Check the terminal is idle.');
        return false;
      }
      if (!response.data.success) {
        toast.error(response.data.error || 'Failed to initiate EFTPOS transaction');
        return false;
      }
      setSmartpayTransactionId(response.data.transaction_id);
      setSmartpayRequestedAmount(parseFloat(amount));  // <-- record so we can verify on approval
      setSmartpayPolling(true);
      const pollId = attemptId;
      const action = transactionType.toLowerCase().includes('refund') ? 'refund' : 'payment';
      toast.info(`Present card to ${terminal.label || terminal.name || 'terminal'} for ${action}...`);
      if (onApproved) {
        pollWithCallback(response.data.polling_url, onApproved, onRejected, 0, pollId);
      } else {
        pollSmartpayStatus(response.data.polling_url, response.data.transaction_id, 0, pollId);
      }
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to connect to SmartConnect');
      return false;
    }
  };

  // Polling variant that calls a custom callback on approval (e.g. for refunds, split)
  const pollWithCallback = async (pollingUrl, onApproved, onRejected = null, attempts = 0, pollId = smartpayPollIdRef.current) => {
    if (pollId !== smartpayPollIdRef.current) return;  // superseded — stop silently
    if (attempts > 60) {
      setSmartpayPolling(false);
      setProcessing(false);
      setPaymentMethod('');
      if (onRejected) try { onRejected('timeout'); } catch (_) {}
      toast.error('EFTPOS: Transaction timed out. Please try again.');
      return;
    }
    if (!pollingUrl) {
      setSmartpayPolling(false);
      setProcessing(false);
      if (onRejected) try { onRejected('no-polling-url'); } catch (_) {}
      toast.error('No polling URL returned by SmartConnect');
      return;
    }
    try {
      const response = await axios.get(`${API_URL}/smartpay/status`, {
        params: { polling_url: pollingUrl },
        headers: getAuthHeader(),
      });
      const data = response.data || {};
      if (pollId !== smartpayPollIdRef.current) return;
      if (data.terminal) {
        stopSmartpayPolling();
        setSmartpayPolling(false);
        if (data.approved) {
          await onApproved(data);
        } else {
          setProcessing(false);
          setPaymentMethod('');
          if (onRejected) try { onRejected(data.transaction_result || 'rejected'); } catch (_) {}
          const reason = data.result_text || data.transaction_result || 'Transaction was not approved';
          toast.error(`EFTPOS: ${reason}. Please try again.`);
        }
      } else {
        setTimeout(() => pollWithCallback(pollingUrl, onApproved, onRejected, attempts + 1, pollId), 2000);
      }
    } catch (error) {
      setTimeout(() => pollWithCallback(pollingUrl, onApproved, onRejected, attempts + 1, pollId), 2000);
    }
  };

  // Poll Smartpay for transaction status (uses pollingUrl returned by initiate)
  // Timeout = 150 attempts × 2s = 5 minutes (customers can be slow with cards).
  const pollSmartpayStatus = async (pollingUrl, transactionId, attempts = 0, pollId = smartpayPollIdRef.current) => {
    if (pollId !== smartpayPollIdRef.current) return;  // superseded — stop silently
    if (attempts === 0) {
      console.log('[EFTPOS] Polling started', { transactionId, pollingUrl });
    }
    setSmartpayPollAttempts(attempts);
    if (attempts > 150) {
      setSmartpayPolling(false);
      setProcessing(false);
      setPaymentMethod('');  // Auto-close on timeout
      console.warn('[EFTPOS] Timed out after 5 minutes', { transactionId, pollingUrl });
      toast.error('EFTPOS: Transaction timed out after 5 min. Check the terminal — if the card was charged, void it on the terminal before retrying.');
      return;
    }
    if (!pollingUrl) {
      setSmartpayPolling(false);
      setProcessing(false);
      setPaymentMethod('');
      console.error('[EFTPOS] No polling URL returned by SmartConnect');
      toast.error('EFTPOS: SmartConnect did not return a polling URL. Transaction NOT processed. Re-pair the terminal in Hardware Settings.');
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/smartpay/status`, {
        params: { polling_url: pollingUrl },
        headers: getAuthHeader(),
      });
      const data = response.data || {};
      if (pollId !== smartpayPollIdRef.current) return;

      if (data.terminal) {
        stopSmartpayPolling();
        setSmartpayPolling(false);
        console.log('[EFTPOS] Terminal returned final status', { approved: data.approved, result: data.transaction_result, attempts });
        if (data.approved) {
          // SAFETY GUARD — the terminal charged whatever amount was sent at /smartpay/initiate
          // (recorded in `smartpayRequestedAmount`). If the cart total has CHANGED since then
          // (e.g. staff added more items or applied a discount while the customer was tapping
          // their card), DO NOT auto-commit the sale at the new total. Force staff to
          // manually reconcile so we never under-charge or over-charge a customer.
          const chargedAmt = parseFloat(data.amount_total) || smartpayRequestedAmount || 0;
          const currentTotal = parseFloat(total) || 0;
          const drift = Math.abs(chargedAmt - currentTotal);
          if (smartpayRequestedAmount != null && drift > 0.05) {
            setProcessing(false);
            setPaymentMethod('');
            setSmartpayRequestedAmount(null);
            console.error('[EFTPOS] Amount drift detected', { chargedAmt, currentTotal, drift });
            window.alert(
              'WARNING — cart was modified during EFTPOS!\n\n' +
              `The terminal charged $${chargedAmt.toFixed(2)} but the cart total is now $${currentTotal.toFixed(2)}.\n\n` +
              'The sale has NOT been saved.\n\n' +
              'Actions:\n' +
              `  • If the customer paid $${chargedAmt.toFixed(2)} on the terminal, manually create a sale for that amount\n` +
              `    OR refund $${chargedAmt.toFixed(2)} on the terminal and run a fresh EFTPOS for $${currentTotal.toFixed(2)}.\n\n` +
              'Never modify the cart while EFTPOS is processing.'
            );
            toast.error('Sale NOT saved — amount mismatch. See alert.');
            return;
          }
          setSmartpayRequestedAmount(null);
          await completeSale('card', data);
        } else {
          setProcessing(false);
          setPaymentMethod('');
          setSmartpayRequestedAmount(null);
          const reason = data.result_text || data.transaction_result || 'Payment was not approved';
          toast.error(`EFTPOS: ${reason}. Card NOT charged.`);
        }
      } else {
        setTimeout(() => pollSmartpayStatus(pollingUrl, transactionId, attempts + 1, pollId), 2000);
      }
    } catch (error) {
      console.warn('[EFTPOS] Poll attempt error (will retry):', error?.message || error);
      setTimeout(() => pollSmartpayStatus(pollingUrl, transactionId, attempts + 1, pollId), 2000);
    }
  };

  // Complete sale
  const completeSale = async (method, cardData = null) => {
    try {
      // Check if any item in cart is OOS
      const hasOOSItems = cart.some(item => item.is_oos);
      
      // Check if this is a split payment
      const isSplitPayment = method === 'split' && cardData?.split_payment;

      // Store credit used: full-cover payment OR partial applied earlier
      const storeCreditUsed = method === 'store_credit' ? +total.toFixed(2) : +(appliedStoreCredit || 0).toFixed(2);
      
      const saleData = {
        items: cart.filter(item => !item.is_gift_card).map(item => ({
          product_id: item.product_id,
          product_name: item.product_name,
          quantity: item.quantity,
          unit_price: item.unit_price,
          line_discount: lineDiscountAmount(item) || 0,
          line_discount_type: item.line_discount_type || null,
          line_discount_value: item.line_discount_value || 0,
          line_discount_reason: item.line_discount_reason || null,
          condition_source: item.condition_source || null
        })),
        total_amount: total,
        subtotal: subtotal,
        tax_amount: tax,
        payment_method: method,
        customer_name: selectedCustomer?.name || 'Walk-in Customer',
        customer_id: selectedCustomer?.id || null,
        customer_email: selectedCustomer?.email || null,
        customer_phone: selectedCustomer?.phone || null,
        staff_name: activeStaff?.name || user?.name || '',
        cash_received: method === 'cash' ? (parseFloat(cashReceived) || total) : (isSplitPayment ? cardData.cash_amount : null),
        change_given: method === 'cash' ? change : null,
        store: selectedStore,
        allow_negative_stock: hasOOSItems || allowNegativeStock,
        // Manual cart-level discount (Lightspeed-style)
        manual_discount: manualDiscount,
        manual_discount_type: appliedDiscount.type || null,
        manual_discount_value: appliedDiscount.value || 0,
        manual_discount_reason: appliedDiscount.reason || null,
        // Split payment data
        split_payment: isSplitPayment,
        cash_amount: isSplitPayment ? cardData.cash_amount : null,
        card_amount: isSplitPayment ? cardData.card_amount : null,
        store_credit_used: storeCreditUsed
      };

      const response = await axios.post(`${API_URL}/sales`, saleData, { headers: getAuthHeader() });

      // Build receipt payload once
      const isSplit = method === 'split' && cardData?.split_payment;
      const fullReceipt = {
        ...response.data,
        subtotal,
        tax_amount: tax,
        payment_method: method,
        customer_name: selectedCustomer?.name || 'Walk-in Customer',
        cash_received: method === 'cash' ? (parseFloat(cashReceived) || total) : (isSplit ? cardData.cash_amount : null),
        change_given: method === 'cash' ? change : null,
        items: cart.filter(item => !item.is_gift_card),
        points_earned: pointsEarned,
        points_redeemed: redeemPoints,
        points_discount: pointsDiscount,
        gift_card_discount: giftCardDiscount,
        gift_card_code: appliedGiftCard?.code,
        manual_discount: manualDiscount,
        manual_discount_type: appliedDiscount.type || null,
        manual_discount_value: appliedDiscount.value || 0,
        card_data: cardData,
        split_payment: isSplit,
        cash_amount: isSplit ? cardData.cash_amount : null,
        card_amount: isSplit ? cardData.card_amount : null,
        store_credit_used: storeCreditUsed,
      };

      // === RETURN THE CASHIER TO THE POS SCREEN IMMEDIATELY ===
      // The /sales POST has already saved everything important (stock, sale record).
      // All the work below (loyalty points, gift card redeem, short-dated deduct,
      // product refresh, receipt print) is best-effort cleanup — none of it must
      // block the next customer. We snapshot the values we need first then fire-and-forget.
      const snapCart = cart;
      const snapCustomer = selectedCustomer;
      const snapAppliedGiftCard = appliedGiftCard;
      const snapStoreCreditUsed = storeCreditUsed;
      const snapOnAccountPin = onAccountPin;
      const snapPointsEarned = pointsEarned;
      const snapRedeemPoints = redeemPoints;
      const snapShortDated = cart.filter(item => item.is_short_dated && item.short_dated_id);

      stopSmartpayPolling();
      setSmartpayPolling(false);
      setSmartpayPollAttempts(0);
      setSmartpayRequestedAmount(null);
      setLastReceipt(fullReceipt);
      setShowPaymentModal(false);
      setShowReceiptModal(true);
      setCart([]);
      setScanNudge(null);
      setScanNudgeOpen(false);
      setSelectedCustomer(null);
      setCashReceived('');
      setPaymentMethod('');
      setRedeemPoints(0);
      setPointsDiscount(0);
      setAppliedGiftCard(null);
      setAppliedStoreCredit(0);
      setStoreCreditUseAmount('');
      setOnAccountPin('');
      setAppliedDiscount({ type: null, value: 0, reason: null });
      setAllowNegativeStock(false);
      if (method === 'on_account') {
        toast.warning(`Sale on account — ${snapCustomer?.name || 'customer'} now OWES $${total.toFixed(2)}`);
      } else {
        toast.success('Payment successful!');
      }
      setProcessing(false);

      // ===== Background cleanup — all non-blocking =====

      // Gift card redeem
      if (snapAppliedGiftCard) {
        axios.post(`${API_URL}/giftcards/${snapAppliedGiftCard.code}/redeem?amount=${snapAppliedGiftCard.redeemAmount}`, {}, { headers: getAuthHeader() })
          .catch(err => console.error('Gift card redemption failed:', err));
      }

      // Store credit redeem
      if (snapCustomer && snapStoreCreditUsed > 0) {
        axios.post(`${API_URL}/customers/${snapCustomer.id}/store-credit/redeem`, {
          amount: snapStoreCreditUsed,
          sale_id: response.data.id,
          staff_name: activeStaff?.name || user?.name || ''
        }, { headers: getAuthHeader() })
          .catch(err => console.error('Store credit redemption failed:', err));
      }

      // On Account — record the debt against the customer (negative store credit)
      if (method === 'on_account' && snapCustomer) {
        axios.post(`${API_URL}/customers/${snapCustomer.id}/store-credit/debt`, {
          amount: +total.toFixed(2),
          reason: 'On account sale — payment owed',
          manager_pin: snapOnAccountPin,
          sale_id: response.data.id,
          staff_name: activeStaff?.name || user?.name || ''
        }, { headers: getAuthHeader() })
          .catch(err => {
            console.error('On-account debt record failed:', err);
            toast.error('Sale saved but debt was NOT recorded — add it manually via the customer\'s Store Credit dialog');
          });
      }

      // Loyalty points: add earned + redeem used (parallel)
      if (snapCustomer) {
        if (snapPointsEarned > 0) {
          axios.post(`${API_URL}/customers/${snapCustomer.id}/add-points?points=${snapPointsEarned}`, {}, { headers: getAuthHeader() })
            .catch(err => console.error('Add points failed:', err));
        }
        if (snapRedeemPoints > 0) {
          axios.post(`${API_URL}/customers/${snapCustomer.id}/redeem-points?points=${snapRedeemPoints}`, {}, { headers: getAuthHeader() })
            .catch(err => console.error('Redeem points failed:', err));
        }
      }

      // Short-dated stock — run all deducts in PARALLEL, not serial
      if (snapShortDated.length > 0) {
        Promise.all(
          snapShortDated.map(item =>
            axios.post(`${API_URL}/short-dated-stock/${item.short_dated_id}/deduct?quantity=${item.quantity}`, {}, { headers: getAuthHeader() })
              .catch(err => console.error('Short-dated deduct failed:', err))
          )
        ).then(() => fetchShortDatedItems());
      }

      // Refresh products list (don't await)
      fetchProducts();

      // Price mismatch detector (already non-blocking but kept here for clarity)
      checkForPriceMismatches(snapCart);

      // Receipt print (don't await)
      try {
        const store = getCurrentStore();
        const receiptPayload = {
          id: response.data.id,
          created_at: response.data.created_at,
          store_name: store?.name || selectedStore || '',
          store_phone: store?.phone || '',
          gst_number: store?.gst_number || '',
          customer_name: snapCustomer?.name || 'Walk-in Customer',
          items: snapCart.filter(item => !item.is_gift_card),
          subtotal,
          tax_amount: tax,
          total_amount: total,
          payment_method: method,
          cash_received: method === 'cash' ? (parseFloat(cashReceived) || total) : null,
          change_given: method === 'cash' ? change : null,
          points_discount: pointsDiscount,
          gift_card_discount: giftCardDiscount,
          gift_card_code: snapAppliedGiftCard?.code || '',
          split_payment: isSplit,
          cash_amount: isSplit ? cardData.cash_amount : null,
          card_amount: isSplit ? cardData.card_amount : null,
          points_earned: snapPointsEarned,
          points_balance: snapCustomer ? (snapCustomer.loyalty_points || 0) + snapPointsEarned - snapRedeemPoints : 0,
        };
        qzPrintReceipt(receiptPayload);
      } catch (printError) {
        console.log('Print error:', printError.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payment failed');
      setProcessing(false);
    }
  };

  // Process cash payment
  const processCashPayment = async () => {
    if (parseFloat(cashReceived) < total) {
      toast.error('Insufficient cash received');
      return;
    }
    setProcessing(true);
    await completeSale('cash');
  };

  // Condition quick picks — load list once, fetch top-5 protocol products per condition
  const loadConditionList = async () => {
    if (conditionList.length > 0) return;
    try {
      const res = await axios.get(`${API_URL}/conditions`, { headers: getAuthHeader() });
      setConditionList(res.data || []);
    } catch (_) { /* non-blocking */ }
  };

  const fetchConditionProducts = async (name) => {
    setActiveCondition(name);
    setLoadingConditionProducts(true);
    setConditionResults([]);
    try {
      const res = await axios.get(
        `${API_URL}/conditions/products?condition=${encodeURIComponent(name)}&store=${selectedStore}`,
        { headers: getAuthHeader() }
      );
      setConditionResults(res.data.suggestions || []);
      if ((res.data.suggestions || []).length === 0) toast.info('No in-stock matches for this condition right now');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not load condition products');
    } finally {
      setLoadingConditionProducts(false);
    }
  };

  // Fetch LLM goal-based product suggestions for the attached customer
  const fetchGoalSuggestions = async () => {
    setLoadingSuggestions(true);
    try {
      const res = await axios.get(
        `${API_URL}/customers/${selectedCustomer.id}/suggestions?store=${selectedStore}`,
        { headers: getAuthHeader() }
      );
      setGoalSuggestions(res.data);
      if ((res.data.suggestions || []).length === 0) {
        toast.info('No in-stock products matched their goals');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not get suggestions');
    } finally {
      setLoadingSuggestions(false);
    }
  };

  // Email the last receipt to the customer
  const handleEmailReceipt = async () => {
    const email = emailReceiptTo.trim();
    if (!email || !email.includes('@')) {
      toast.error('Enter a valid email address');
      return;
    }
    setSendingEmailReceipt(true);
    try {
      await axios.post(`${API_URL}/sales/${lastReceipt.id}/email-receipt`, { email }, { headers: getAuthHeader() });
      toast.success(`Receipt emailed to ${email}`);
      setShowEmailReceiptInput(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to email receipt');
    } finally {
      setSendingEmailReceipt(false);
    }
  };

  // On Account (debt) — verify manager PIN first, then complete the sale
  const handleOnAccountConfirm = async () => {
    if (!onAccountPin.trim()) {
      toast.error('Manager PIN is required');
      return;
    }
    setProcessing(true);
    try {
      const res = await axios.post(`${API_URL}/stock-receipts/verify-pin`, { pin: onAccountPin }, { headers: getAuthHeader() });
      if (!res.data.valid) {
        toast.error('Invalid manager PIN');
        setProcessing(false);
        return;
      }
    } catch (e) {
      toast.error('PIN check failed — try again');
      setProcessing(false);
      return;
    }
    await completeSale('on_account');
  };

  // Store credit payment — full cover completes the sale, partial applies as credit then staff picks another tender for the rest
  const handleStoreCreditConfirm = async () => {
    const balance = selectedCustomer?.store_credit || 0;
    const amt = Math.min(parseFloat(storeCreditUseAmount) || 0, balance, total);
    if (amt <= 0) {
      toast.error('Enter a valid store credit amount');
      return;
    }
    if (amt >= total - 0.005) {
      setProcessing(true);
      await completeSale('store_credit');
    } else {
      setAppliedStoreCredit(+amt.toFixed(2));
      setPaymentMethod('');
      toast.success(`$${amt.toFixed(2)} store credit applied — $${(total - amt).toFixed(2)} remaining`);
    }
  };

  // Process card payment (manual - without EFTPOS terminal)
  const processCardPayment = async (cardType) => {
    setProcessing(true);
    await completeSale('card', { card_type: cardType, terminal: 'manual' });
  };

  // Process split payment (cash + card)
  const processSplitPayment = async (cashAmount, cardAmount) => {
    setProcessing(true);
    // If a Smartpay terminal is connected and the card portion > 0, run it through EFTPOS first
    if (cardAmount > 0 && smartpayConnected && selectedTerminalId) {
      const ok = await runSmartpayTransaction(cardAmount, 'Card.Purchase', async (eftposData) => {
        await completeSale('split', {
          cash_amount: cashAmount,
          card_amount: cardAmount,
          split_payment: true,
          eftpos: eftposData,
        });
      }, () => {
        // Card portion cancelled/declined → unlock UI so cashier can pick another tender
        setProcessing(false);
      });
      if (!ok) setProcessing(false);
      return;
    }
    // Manual card entry / no terminal → record split as-is
    await completeSale('split', {
      cash_amount: cashAmount,
      card_amount: cardAmount,
      split_payment: true,
    });
  };

  // Handle barcode scan (Enter key in search)
  const handleSearchKeyDown = (e) => {
    // Read the LIVE input value — scanners press Enter before the debounce commits
    const liveValue = (e.target.value || '').trim();
    if (e.key === 'Enter' && liveValue) {
      clearTimeout(searchDebounceRef.current);
      const scan = liveValue;
      console.log('[SCAN] Scanned value:', JSON.stringify(scan), 'length:', scan.length);
      
      const exactMatch = products.find(p => productMatchesScan(p, scan));
      
      if (exactMatch) {
        console.log('[SCAN] Match found:', exactMatch.name, 'barcode:', exactMatch.barcode);
        addToCart(exactMatch, false, null, true);
        clearSearch();
        searchInputRef.current?.focus();
      } else if (searchTerm === scan && filteredProducts.length === 1) {
        addToCart(filteredProducts[0], false, null, true);
        clearSearch();
        searchInputRef.current?.focus();
      } else {
        // Show what the scanner sent so user can see the mismatch
        console.log('[SCAN] No match. Scanner sent:', JSON.stringify(scan));
        setSearchTerm(scan); // commit immediately so the dropdown shows results
        const partialMatch = products.find(p => p._barcode && p._barcode.includes(scan));
        if (partialMatch && !/^\d{6,}$/.test(scan)) {
          console.log('[SCAN] Partial match found — DB barcode:', partialMatch.barcode, 'vs scanned:', scan);
          toast.error(`Scanned "${scan}" — close match: "${partialMatch.barcode}" (${partialMatch.name})`);
        } else if (/^\d{6,}$/.test(scan)) {
          // Looks like a real barcode the system doesn't know yet — let staff link it to the product
          setLinkBarcodeScan(scan);
        } else {
          toast.error(`Barcode "${scan}" not found in products`);
        }
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[600px]">
        <div className="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full spinner"></div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col gap-4" data-testid="pos-page">
      {/* Scan Nudge Banner — add-on recommendation popup shown when a matching product is scanned/added */}
      {scanNudge && scanNudgeOpen && (
        <div className="fixed inset-0 z-[100] flex items-start justify-center pt-24 px-4 bg-black/30" data-testid="scan-nudge-overlay" onClick={() => setScanNudgeOpen(false)}>
          <div
            className="w-full max-w-2xl rounded-xl border-2 border-amber-400 bg-amber-50 shadow-2xl p-5 animate-in slide-in-from-top-4 fade-in duration-200 max-h-[70vh] overflow-y-auto"
            data-testid="scan-nudge-banner"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-full bg-amber-400 flex items-center justify-center shrink-0">
                <Lightbulb className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold uppercase tracking-wide text-amber-700 mb-0.5">{(scanNudge.protocols || []).length ? `Protocol product · ${scanNudge.protocols.map(m => m.condition).join(', ')}` : 'Add-on recommendation'}</p>
                <p className="text-sm font-semibold text-slate-800 truncate">{scanNudge.productName}</p>
                {scanNudge.message && (
                  <p className="text-base text-amber-900 mt-1.5 leading-snug">{scanNudge.message}</p>
                )}
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setScanNudgeOpen(false)}
                className="border-amber-400 text-amber-900 hover:bg-amber-100 shrink-0"
                data-testid="scan-nudge-close-btn"
              >
                <X className="w-4 h-4 mr-1" /> Got it
              </Button>
            </div>
            {(scanNudge.protocols || []).map(m => (
              <div key={m.condition} className="mt-3 border-t border-amber-200 pt-3" data-testid={`scan-nudge-protocol-${m.condition.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
                <ProtocolTillGuide condition={m.condition} scanned={m.scanned} staffNotes={m.staff_notes} />
                {m.suggestions.length > 0 && (
                <p className="text-xs font-bold uppercase tracking-wide text-teal-700 flex items-center gap-1.5 mb-2 mt-3">
                  <HeartPulse className="w-3.5 h-3.5" /> Part of your {m.condition} protocol — suggest these add-ons:
                </p>
                )}
                <div className="space-y-1.5">
                  {m.suggestions.map(p => (
                    <div key={p.id} className="flex items-center justify-between gap-2 bg-white/70 border border-amber-200 rounded-md px-3 py-1.5">
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate" title={p.name}>
                          {p.pinned && <Star className="w-3.5 h-3.5 inline mr-1 text-amber-500 fill-amber-400" />}
                          {p.name}
                        </p>
                        <p className="text-xs text-slate-500">
                          ${(p.sell_price || 0).toFixed(2)} · {p.order_in ? <span className="text-indigo-600 font-bold">Order-in · Dispensary</span> : <span className={p.store_quantity > 0 ? '' : 'text-red-500 font-bold'}>{p.store_quantity} in stock</span>}
                        </p>
                        {p.benefit && <p className="text-xs text-teal-700">{p.benefit}</p>}
                        {p.caution && <p className="text-xs text-amber-700 font-medium">⚠ {p.caution}</p>}
                        {p.dose && <p className="text-xs text-indigo-700">Dose: {p.dose}</p>}
                      </div>
                      <Button
                        size="sm"
                        onClick={() => { addToCart({ ...p, condition_source: m.condition }); toast.success(`${p.name} added`); }}
                        className="shrink-0 bg-emerald-600 hover:bg-emerald-700 h-8 w-8 p-0"
                        data-testid={`scan-nudge-add-${p.id}`}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Expiry nudges — 12:00 and 15:00 reminders to sell short-dated stock first */}
      <ExpiryNudgeBanner
        items={shortDatedItems}
        storeName={getCurrentStore()?.name || selectedStore}
        onRefresh={fetchShortDatedItems}
        onViewAll={() => window.open('/short-dated', '_blank')}
      />
      {/* Daily till routine — asks for the counted float each time POS is opened until the till is open */}
      <TillOpenPrompt />
      {/* Login Nudge Banner — friendly reminder when the logged-in PIN doesn't
          match who the schedule says should be on shift right now. */}
      {shiftNow && shiftNow.on_shift_now && !nudgeDismissed && (() => {
        const expected = (shiftNow.on_shift_now.staff_name || '').trim().toLowerCase();
        const current = (activeStaff?.name || user?.name || user?.username || '').trim().toLowerCase();
        if (!expected) return null;
        const matches = current && (current === expected || current.includes(expected) || expected.includes(current));
        if (matches) return null;
        return (
          <div className="rounded-md border-2 border-amber-300 bg-amber-50 px-3 py-2 flex items-center gap-3 flex-wrap" data-testid="login-nudge-banner">
            <span className="text-xl">👋</span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-amber-900">
                Hey {shiftNow.on_shift_now.staff_name} — please log in to your own PIN account
              </p>
              <p className="text-xs text-amber-800">
                {shiftNow.weekday} {shiftNow.on_shift_now.start}–{shiftNow.on_shift_now.end} is your shift. Sales must be tracked under YOUR name, not "{activeStaff?.name || user?.username || 'someone else'}".
              </p>
            </div>
            <Button size="sm" variant="outline" onClick={() => setNudgeDismissed(true)} className="border-amber-400 text-amber-900 hover:bg-amber-100">
              Dismiss
            </Button>
          </div>
        );
      })()}
      {/* Daily Target Tracker — pink while pushing, green when smashed it */}
      {dailyProgress && dailyProgress.target > 0 && (() => {
        const pct = Math.min(100, Math.max(0, (dailyProgress.actual / dailyProgress.target) * 100));
        const achieved = dailyProgress.achieved;
        const remaining = Math.max(0, dailyProgress.target - dailyProgress.actual);
        const isStoreView = dailyProgress.is_store_view;
        const name = dailyProgress.staff_name || activeStaff?.name || 'Team';
        const storeLabel = getCurrentStore()?.name || dailyProgress.store || 'Store';
        const headlineActive = isStoreView
          ? `${storeLabel} daily target`
          : `${name}'s daily target`;
        const headlineDone = isStoreView
          ? `Great day ${storeLabel}!`
          : `Good work ${name}!`;
        const hits = dailyProgress.hits_this_month || 0;
        const worked = dailyProgress.days_worked_this_month || 0;
        return (
          <div
            className={`rounded-lg border-2 px-4 py-2.5 flex items-center gap-4 transition-colors ${
              achieved
                ? 'border-emerald-400 bg-emerald-50'
                : 'border-pink-300 bg-pink-50'
            }`}
            data-testid="pos-daily-target-tracker"
          >
            <div className="flex-shrink-0 flex items-center gap-2">
              <span className="text-2xl">{achieved ? '🎉' : '🎯'}</span>
              <div className="text-sm">
                <div className={`font-bold ${achieved ? 'text-emerald-900' : 'text-pink-900'}`}>
                  {achieved ? headlineDone : headlineActive}
                </div>
                <div className={`text-xs ${achieved ? 'text-emerald-700' : 'text-pink-700'}`}>
                  {achieved
                    ? `Smashed it! $${dailyProgress.actual.toFixed(2)} of $${dailyProgress.target.toFixed(2)}`
                    : `$${dailyProgress.actual.toFixed(2)} of $${dailyProgress.target.toFixed(2)} — $${remaining.toFixed(2)} to go`}
                </div>
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className={`h-3 w-full rounded-full overflow-hidden ${achieved ? 'bg-emerald-100' : 'bg-pink-100'}`}>
                <div
                  className={`h-full transition-all duration-700 ease-out ${achieved ? 'bg-emerald-500' : 'bg-pink-500'}`}
                  style={{ width: `${pct}%` }}
                  data-testid="pos-target-progress-fill"
                />
              </div>
            </div>
            <div className="flex-shrink-0 text-right">
              <div className={`text-lg font-bold ${achieved ? 'text-emerald-700' : 'text-pink-700'}`} data-testid="pos-target-percent">
                {pct.toFixed(0)}%
              </div>
              {!isStoreView && worked > 0 && (
                <div className={`text-[10px] ${achieved ? 'text-emerald-600' : 'text-pink-600'}`}>
                  {hits}/{worked} days hit this month
                </div>
              )}
            </div>
          </div>
        );
      })()}
      {/* Negative Stock Warning Banner */}
      {/* Main POS Content */}
      <div className="flex-1 flex gap-4 min-h-0">
      {/* Left Side - Product Grid */}
      <div className="flex-1 flex flex-col">
        {/* Top Bar with Store & Smartpay Status & Action Buttons */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            {/* Current Store Badge */}
            <Badge 
              variant="outline" 
              className={`text-base px-3 py-1 ${selectedStore === 'orewa' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}
              data-testid="current-store-badge"
            >
              <Store className="w-4 h-4 mr-1" /> {getCurrentStore()?.name || selectedStore}
            </Badge>
            {smartpayConnected ? (
              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                <Wifi className="w-3 h-3 mr-1" /> EFTPOS
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                <WifiOff className="w-3 h-3 mr-1" /> EFTPOS
              </Badge>
            )}
            {smartpayTerminals.length > 1 && (
              <select
                value={selectedTerminalId || ''}
                onChange={(e) => setSelectedTerminalId(e.target.value)}
                className="text-xs border border-slate-200 rounded px-2 py-1 bg-white"
                title="Select which EFTPOS terminal this register uses"
                data-testid="pos-terminal-selector"
              >
                {smartpayTerminals.map((t) => (
                  <option key={t.id} value={t.id}>{t.label}</option>
                ))}
              </select>
            )}
            {/* Wholesale Mode Toggle — platform owner's own wholesale customer pricing */}
            {user?.is_platform_business && (
            <button
              onClick={() => { setWholesaleMode(!wholesaleMode); if (!wholesaleMode) toast.info("Sabhyata's Price ON — wholesale + GST + 15%"); else toast.info("Normal pricing restored"); }}
              className={`px-3 py-1 rounded-full text-xs font-bold transition-all ${
                wholesaleMode 
                  ? 'bg-purple-600 text-white shadow-md ring-2 ring-purple-300' 
                  : 'bg-slate-100 text-slate-500 border border-slate-200 hover:bg-purple-50 hover:text-purple-600'
              }`}
              data-testid="wholesale-mode-toggle"
            >
              {wholesaleMode ? "Sabhyata's Price ON" : "Sabhyata's Price"}
            </button>
            )}
            {/* Reprint last receipt */}
            {lastReceipt && (
              <button
                onClick={() => setShowReceiptModal(true)}
                className="px-3 py-1 rounded-full text-xs font-bold bg-slate-100 text-slate-500 border border-slate-200 hover:bg-slate-200 transition-all flex items-center gap-1"
                data-testid="reprint-last-receipt"
                title="Reopen the last receipt to reprint or email"
              >
                <Printer className="w-3 h-3" /> Last Receipt
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowMiscModal(true)}
              className="bg-slate-100 text-slate-700 border-slate-300 hover:bg-slate-200"
              data-testid="misc-button"
            >
              <MoreHorizontal className="w-4 h-4 mr-1" /> Misc
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={openStockReport}
              className="bg-red-50 text-red-700 border-red-200 hover:bg-red-100"
              data-testid="stock-report-button"
            >
              <AlertTriangle className="w-4 h-4 mr-1" /> Stock
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={testSmartpayConnection}
              data-testid="refresh-connection"
            >
              Refresh
            </Button>
          </div>
        </div>

        {/* Search Bar with Barcode Icon and Dropdown Results */}
        <div className="mb-4">
          <div className="relative">
            <Barcode className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 z-10" />
            <Input
              ref={searchInputRef}
              placeholder="Search product name, SKU, barcode, brand..."
              defaultValue=""
              onChange={onSearchInputChange}
              onKeyDown={handleSearchKeyDown}
              className="pl-10 h-14 text-lg border-2 border-slate-300 focus:border-orange-500"
              data-testid="pos-search"
            />
            {searchTerm && (
              <button 
                onClick={() => clearSearch()}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 z-10"
              >
                <X className="w-5 h-5" />
              </button>
            )}
            
            {/* Search Results Dropdown */}
            {searchTerm && searchTerm.length >= 1 && (
              <div className="absolute z-50 w-full mt-1 bg-white border-2 border-orange-400 rounded-lg shadow-xl max-h-96 overflow-auto">
                {filteredProducts.length === 0 ? (
                  <div className="px-4 py-6 text-center text-slate-500">
                    <Search className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                    <p>No products found for "{searchTerm}"</p>
                  </div>
                ) : (
                  <>
                    <div className="px-3 py-1.5 bg-slate-50 text-xs text-slate-500 border-b sticky top-0">
                      {filteredProducts.length > 50
                        ? `${filteredProducts.length} results — showing first 50, keep typing to narrow down`
                        : `${filteredProducts.length} results — sorted A-Z`}
                    </div>
                    {filteredProducts.slice(0, 50).map(product => {
                    const stockQty = product.store_quantity ?? 0;
                    const isOutOfStock = stockQty <= 0;
                    
                    return (
                      <button
                        key={product.id}
                        className={`w-full px-4 py-3 text-left border-b last:border-b-0 flex items-center justify-between gap-3 ${
                          isOutOfStock 
                            ? 'bg-red-50 hover:bg-red-100 cursor-pointer' 
                            : 'hover:bg-orange-50 cursor-pointer'
                        }`}
                        onClick={() => {
                          addToCart(product);
                          clearSearch();
                          searchInputRef.current?.focus();
                        }}
                        data-testid={`search-result-${product.id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className={`font-medium truncate ${isOutOfStock ? 'text-red-700' : 'text-slate-900'}`}>
                            {product.name}
                            {isOutOfStock && <span className="ml-2 text-xs text-red-500">(click to sell OOS)</span>}
                          </p>
                          <p className="text-xs text-slate-500 truncate">
                            {product.sku && `SKU: ${product.sku}`}
                            {product.sku && product.barcode && ' • '}
                            {product.barcode && product.barcode}
                            {product.brand && ` • ${product.brand}`}
                          </p>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <span className={`font-bold ${isOutOfStock ? 'text-red-700' : 'text-slate-900'}`}>
                            ${product.sell_price?.toFixed(2) || '0.00'}
                          </span>
                          <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                            isOutOfStock ? 'bg-red-100 text-red-700' :
                            stockQty < 10 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'
                          }`}>
                            {isOutOfStock ? 'OOS' : stockQty}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                  </>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center justify-between gap-2 mt-1 ml-1">
            <p className="text-xs text-slate-500">
              Scan barcode or type to search • {products.length} products
            </p>
            {scanNudge && ((scanNudge.protocols || []).length > 0 || scanNudge.message) && (
              <button
                onClick={() => setScanNudgeOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-full border border-teal-300 bg-teal-50 px-3 py-1 text-xs font-semibold text-teal-800 hover:bg-teal-100"
                title={`${scanNudge.productName} — staff tips, screening questions, dose and add-ons`}
                data-testid="scan-nudge-open-btn"
              >
                <HeartPulse className="w-3.5 h-3.5" /> {(scanNudge.protocols || []).length ? `Protocol tips: ${scanNudge.protocols.map(m => m.condition).join(', ')}` : 'Staff tip for this product'}
              </button>
            )}
          </div>
        </div>

        {/* Quick Keys Section */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <Package className="w-4 h-4" /> Quick Keys
            </h3>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setConditionQuery(''); setActiveCondition(null); setConditionResults([]); loadConditionList(); setShowConditionsModal(true); }}
                className="text-xs text-teal-600 hover:text-teal-800 hover:bg-teal-50"
                data-testid="conditions-button"
              >
                <HeartPulse className="w-4 h-4 mr-1" /> Conditions
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowQuickKeySetup(!showQuickKeySetup)}
                className="text-xs text-slate-500 hover:text-slate-700"
              >
                {showQuickKeySetup ? <X className="w-4 h-4 mr-1" /> : <Settings className="w-4 h-4 mr-1" />}
                {showQuickKeySetup ? 'Done' : 'Setup'}
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-6 gap-2">
            {quickKeys.map((qk, index) => (
              <div key={index} className="relative">
                {qk ? (
                  <button
                    onClick={() => showQuickKeySetup ? setEditingQuickKeyIndex(index) : addFromQuickKey(qk)}
                    className={`w-full h-24 rounded-lg bg-white text-slate-800 p-2 flex flex-col items-center justify-center hover:bg-slate-50 transition-all shadow-sm border border-slate-200`}
                    data-testid={`quick-key-${index}`}
                    title={qk.name}
                  >
                    <span className="text-sm font-semibold text-center leading-tight line-clamp-3 w-full">
                      {qk.name || qk.product_name || 'Unknown'}
                    </span>
                    <span className="text-xs text-slate-500 mt-1">${qk.sell_price?.toFixed(2)}</span>
                  </button>
                ) : (
                  <button
                    onClick={() => showQuickKeySetup && setEditingQuickKeyIndex(index)}
                    className={`w-full h-24 rounded-lg border-2 border-dashed ${
                      showQuickKeySetup ? 'border-slate-300 hover:border-orange-400 hover:bg-orange-50' : 'border-slate-200'
                    } flex items-center justify-center transition-all`}
                    disabled={!showQuickKeySetup}
                    data-testid={`quick-key-empty-${index}`}
                  >
                    {showQuickKeySetup && <Plus className="w-5 h-5 text-slate-400" />}
                  </button>
                )}
                {showQuickKeySetup && qk && (
                  <button
                    onClick={(e) => { e.stopPropagation(); removeQuickKey(index); }}
                    className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            ))}
          </div>
          
          {/* Quick Key Assignment Popover */}
          {editingQuickKeyIndex !== null && (
            <div className="mt-3 p-3 bg-white border-2 border-orange-300 rounded-lg shadow-lg">
              <p className="text-sm font-medium text-slate-700 mb-2">
                Assign product to Quick Key #{editingQuickKeyIndex + 1}
              </p>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search product..."
                  value={quickKeySearch}
                  onChange={(e) => setQuickKeySearch(e.target.value)}
                  className="pl-9"
                  autoFocus
                />
              </div>
              {quickKeyProductResults.length > 0 && (
                <div className="mt-2 max-h-48 overflow-y-auto border rounded-sm">
                  {quickKeyProductResults.map(product => (
                    <button
                      key={product.id}
                      onClick={() => assignQuickKey(editingQuickKeyIndex, product)}
                      className="w-full p-2 text-left hover:bg-orange-50 border-b last:border-b-0 flex justify-between items-center"
                    >
                      <div>
                        <p className="font-medium text-sm">{product.name}</p>
                        <p className="text-xs text-slate-500">{product.sku || product.barcode || ''}</p>
                      </div>
                      <span className="font-bold text-sm">${product.sell_price.toFixed(2)}</span>
                    </button>
                  ))}
                </div>
              )}
              {quickKeySearch.length >= 2 && quickKeyProductResults.length === 0 && (
                <p className="mt-2 text-sm text-slate-500 text-center py-2">No products found for "{quickKeySearch}"</p>
              )}
              <div className="flex gap-2 mt-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setEditingQuickKeyIndex(null); setQuickKeySearch(''); }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                {quickKeys[editingQuickKeyIndex] && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => assignQuickKey(editingQuickKeyIndex, null)}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                  >
                    Clear
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Product Grid - Only shown when searching */}
        {searchTerm && searchTerm.length > 0 && (
        <ScrollArea className="flex-1">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {filteredProducts.filter(p => {
              const storeStock = p.store_quantity ?? 0;
              return storeStock > 0 || storeStock < 0;
            }).slice(0, 100).map((product) => {
              const stockQty = product.store_quantity ?? 0;
              
              return (
                <div
                  key={product.id}
                  className="bg-white border border-slate-200 rounded-sm p-4 text-left hover:border-orange-500 hover:shadow-md transition-all group relative"
                  data-testid={`product-tile-${product.id}`}
                >
                  <button
                    onClick={() => addToCart(product)}
                    className="w-full text-left"
                  >
                    <div className="flex items-center justify-center w-full h-16 bg-slate-100 rounded-sm mb-3 group-hover:bg-orange-50">
                      <Package className="w-8 h-8 text-slate-400 group-hover:text-orange-500" />
                    </div>
                    <h3 className="font-medium text-slate-900 text-sm truncate">{product.name}</h3>
                    {product.barcode && (
                      <p className="text-xs text-slate-400 truncate">{product.barcode}</p>
                    )}
                    <div className="flex items-center justify-between mt-2">
                      <span className="font-bold text-lg text-slate-900">${product.sell_price?.toFixed(2) || '0.00'}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        stockQty < 10 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'
                      }`}>
                        {stockQty}
                      </span>
                    </div>
                  </button>
                  <button
                    onClick={(e) => openEditProduct(product, e)}
                    className="absolute top-2 right-2 w-7 h-7 bg-white/80 hover:bg-blue-100 border border-slate-200 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`edit-product-${product.id}`}
                    title="Edit product"
                  >
                    <Edit className="w-3.5 h-3.5 text-blue-600" />
                  </button>
                  {product.shopify_product_id && (
                    <span className="absolute top-2 left-2 text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full opacity-70">Shopify</span>
                  )}
                </div>
              );
            })}
            {filteredProducts.filter(p => (p.store_quantity ?? 0) > 0 || (searchTerm && (p.store_quantity ?? 0) !== 0)).length === 0 && (
              <div className="col-span-full text-center py-12 text-slate-500">
                <Package className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No products found for "{searchTerm}"</p>
              </div>
            )}
          </div>
        </ScrollArea>
        )}
        
        {/* Stock Warnings - Bottom of left panel */}
        {showStockWarning && negativeStockWarnings.length > 0 && (
          <div
            className="mt-3 bg-slate-50 border border-slate-200 rounded-lg p-3 flex items-start justify-between cursor-pointer hover:bg-slate-100 transition-colors"
            data-testid="stock-warning-banner"
            onClick={() => { setShowNegativeStockModal(true); setNegativeStockFixes({}); }}
            role="button"
            tabIndex={0}
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-slate-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-bold text-slate-800 text-sm">Negative stock — click to fix</p>
                <p className="text-xs text-slate-600">
                  {negativeStockWarnings.map((w, i) => (
                    <span key={w.supplier}>
                      {i > 0 && ', '}
                      <strong>{w.supplier}</strong> ({w.count} items)
                    </span>
                  ))}
                  {' '}— fix individually without a full stock take.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              <Button
                size="sm"
                variant="outline"
                className="border-slate-300 text-slate-700 hover:bg-slate-100 text-xs"
                onClick={() => { setShowNegativeStockModal(true); setNegativeStockFixes({}); }}
                data-testid="open-negative-stock-modal"
              >
                Fix items
              </Button>
              <Button size="sm" variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-100 text-xs" onClick={() => window.location.href = '/stock-count'} data-testid="go-to-stock-count">
                Stock Count
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowStockWarning(false)} className="text-slate-500 hover:bg-slate-100">
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
        {showOverdueWarning && overdueSuppliers.length > 0 && (
          <div className="mt-2 bg-slate-50 border border-slate-200 rounded-lg p-3 flex items-start justify-between" data-testid="overdue-stock-warning">
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-slate-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-bold text-slate-800 text-sm">Stock Count Overdue (2+ months)</p>
                <p className="text-xs text-slate-600">
                  {overdueSuppliers.slice(0, 3).map((s, i) => (
                    <span key={s.supplier_id}>
                      {i > 0 && ', '}
                      <strong>{s.supplier_name}</strong>
                      {s.never_counted ? ' (never counted)' : ` (${s.days_since_count} days ago)`}
                    </span>
                  ))}
                  {overdueSuppliers.length > 3 && ` +${overdueSuppliers.length - 3} more`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-100 text-xs" onClick={() => window.location.href = '/stock-count'}>
                Do Stock Count
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowOverdueWarning(false)} className="text-slate-500 hover:bg-slate-100">
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
        {/* Customer Orders Reminder (once a day) */}
        {showOrderReminder && pendingOrderCount > 0 && (
          <div className="mt-2 bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start justify-between" data-testid="order-reminder-banner">
            <div className="flex items-start gap-3">
              <Package className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-bold text-blue-800 text-sm">Customer Orders to Place</p>
                <p className="text-xs text-blue-700">
                  <strong>{pendingOrderCount}</strong> customer order{pendingOrderCount > 1 ? 's' : ''} not yet ordered from supplier. Please check the order list.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-100 text-xs" onClick={() => window.location.href = '/customer-orders'}>
                View Orders
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setShowOrderReminder(false); localStorage.setItem('order_reminder_dismissed_' + new Date().toDateString(), '1'); }} className="text-blue-600 hover:bg-blue-100">
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
        {/* Price Mismatch Alerts */}
        {priceMismatches.length > 0 && (
          <div className="mt-2 space-y-2">
            {priceMismatches.map(mm => (
              <div key={mm.id} className="bg-slate-50 border border-slate-300 rounded-lg p-3" data-testid={`mismatch-${mm.id}`}>
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-slate-500 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-bold text-slate-800 text-sm">Price Mismatch!</p>
                    <p className="text-sm text-slate-700 font-medium">{mm.product_name}</p>
                    <p className="text-xs text-slate-600 mt-1">
                      System: <span className="font-bold">${mm.system_price.toFixed(2)}</span> vs 
                      Label: <span className="font-bold">${mm.sold_price.toFixed(2)}</span>
                      {mm.stock_on_hand > 0 && <> &middot; <span className="font-bold">{mm.stock_on_hand}</span> still on shelf</>}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">Check and update prices on remaining stock</p>
                    <label className="flex items-center gap-2 mt-2 cursor-pointer bg-white border border-slate-300 rounded-md px-3 py-2 hover:bg-slate-100 transition-colors">
                      <input
                        type="checkbox"
                        className="rounded border-slate-400 text-slate-600"
                        onChange={() => resolvePriceMismatch(mm.id)}
                        data-testid={`resolve-mismatch-${mm.id}`}
                      />
                      <span className="text-xs font-medium text-slate-800">I have checked and corrected the shelf prices</span>
                    </label>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Right Side - Cart */}
      <Card className="w-[560px] xl:w-[620px] flex flex-col border-2 border-slate-200" data-testid="pos-cart">
        <CardHeader className="pb-3 border-b bg-slate-900 text-white rounded-t-sm">
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-lg font-bold uppercase flex items-center gap-2">
              <ShoppingCart className="w-5 h-5" /> Cart
            </CardTitle>
            {cart.length > 0 && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={clearCart}
                className="text-slate-300 hover:text-white hover:bg-slate-800"
                data-testid="clear-cart"
              >
                Clear
              </Button>
            )}
          </div>
          
          {/* Customer Selection */}
          <div className="mt-3">
            {selectedCustomer ? (
              <div className="bg-slate-800 rounded-sm p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-medium text-white">{selectedCustomer.name}</p>
                      <p className="text-xs text-slate-400">{selectedCustomer.phone}</p>
                    </div>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setSelectedCustomer(null)}
                    className="text-slate-400 hover:text-white h-7"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                <div className="mt-2 pt-2 border-t border-slate-700 flex items-center justify-between">
                  <div className="flex items-center gap-4 text-xs">
                    {selectedCustomer.email && (
                      <span className="text-slate-400 flex items-center gap-1">
                        <Mail className="w-3 h-3" /> {selectedCustomer.email}
                      </span>
                    )}
                    {customerTier?.tier && (
                      <span
                        className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${
                          customerTier.tier.key === 'platinum' ? 'bg-slate-100 text-slate-900' :
                          customerTier.tier.key === 'gold' ? 'bg-yellow-200 text-yellow-900' :
                          customerTier.tier.key === 'silver' ? 'bg-slate-200 text-slate-800' :
                          'bg-amber-200 text-amber-900'
                        }`}
                        title={`Lifetime: $${customerTier.total_spent} · ${customerTier.tier.perks?.join(' · ')}`}
                        data-testid="customer-tier-badge"
                      >
                        {customerTier.tier.label}
                        {customerTier.next_tier && (
                          <span className="text-[9px] font-normal ml-1 opacity-75">
                            (${customerTier.to_next_tier.toFixed(0)} to {customerTier.next_tier.label})
                          </span>
                        )}
                      </span>
                    )}
                  </div>
                  <Badge className="bg-yellow-500 text-black">
                    <Star className="w-3 h-3 mr-1" /> ${selectedCustomer.loyalty_points || 0} credit
                  </Badge>
                  {(selectedCustomer.store_credit || 0) > 0 && (
                    <Badge className="bg-amber-500 text-black" data-testid="pos-store-credit-badge">
                      <Wallet className="w-3 h-3 mr-1" /> ${(selectedCustomer.store_credit || 0).toFixed(2)} store credit
                    </Badge>
                  )}
                  {(selectedCustomer.store_credit || 0) < 0 && (
                    <Badge className="bg-red-600 text-white animate-pulse" data-testid="pos-owes-badge">
                      <AlertTriangle className="w-3 h-3 mr-1" /> OWES ${Math.abs(selectedCustomer.store_credit).toFixed(2)}
                    </Badge>
                  )}
                </div>
                {selectedCustomer.notes && (
                  <div className="mt-2 p-2 bg-teal-900/40 border border-teal-700 rounded-sm" data-testid="pos-customer-notes">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-xs text-teal-200 flex items-start gap-1.5">
                        <FileText className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                        <span>{selectedCustomer.notes}</span>
                      </p>
                      <button
                        onClick={fetchGoalSuggestions}
                        disabled={loadingSuggestions}
                        className="shrink-0 text-[11px] font-bold uppercase flex items-center gap-1 px-2 py-1 rounded bg-teal-600 hover:bg-teal-500 text-white disabled:opacity-50"
                        data-testid="goal-suggest-btn"
                      >
                        {loadingSuggestions ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                        Suggest
                      </button>
                    </div>
                    {goalSuggestions && goalSuggestions.suggestions?.length > 0 && (
                      <div className="mt-2 space-y-1" data-testid="goal-suggestions-list">
                        {goalSuggestions.goals?.length > 0 && (
                          <p className="text-[10px] uppercase tracking-wide text-teal-400">
                            For: {goalSuggestions.goals.join(' · ')}
                          </p>
                        )}
                        {goalSuggestions.suggestions.map(p => (
                          <div key={p.id} className="flex items-center justify-between gap-2 bg-teal-950/60 rounded px-2 py-1.5">
                            <div className="min-w-0">
                              <p className="text-xs text-white truncate" title={p.name}>{p.name}</p>
                              <p className="text-[10px] text-teal-300">${(p.sell_price || 0).toFixed(2)} · {p.matched_term}{p.order_in ? ' · Order-in' : ''}</p>
                            </div>
                            <button
                              onClick={() => addToCart({ ...p, condition_source: 'AI Goals' })}
                              className="shrink-0 w-6 h-6 rounded bg-emerald-600 hover:bg-emerald-500 text-white flex items-center justify-center"
                              data-testid={`goal-suggestion-add-${p.id}`}
                            >
                              <Plus className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <Button
                variant="outline"
                onClick={() => setShowCustomerModal(true)}
                className="w-full bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700 hover:text-white"
                data-testid="add-customer"
              >
                <User className="w-4 h-4 mr-2" /> Add Customer (Loyalty)
              </Button>
            )}
          </div>
        </CardHeader>

        <CardContent className="flex-1 flex flex-col p-0">
          {/* Cart Items */}
          <ScrollArea className="flex-1 p-4">
            {cart.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <ShoppingCart className="w-16 h-16 mx-auto mb-3 text-slate-200" />
                <p>Cart is empty</p>
                <p className="text-sm mt-1">Scan or click products to add</p>
              </div>
            ) : (
              <div className="space-y-3">
                {cart.map((item) => (
                  <div 
                    key={item.product_id + (item.is_short_dated ? '-sd' : '') + (item.is_oos ? '-oos' : '') + (item.quantity < 0 ? '-rt' : '')} 
                    className={`flex flex-col gap-2 p-3 rounded-sm ${
                      item.quantity < 0 ? 'bg-rose-50 border-2 border-rose-300' :
                      item.is_oos ? 'bg-red-50 border border-red-200' :
                      item.is_short_dated ? 'bg-yellow-50 border border-yellow-200' : 'bg-slate-50'
                    }`}
                    data-testid={`cart-item-${item.product_id}`}
                  >
                    {/* Row 1 — product name + badges + remove */}
                    <div className="flex items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 min-w-0 flex-wrap">
                          <p className="font-semibold text-slate-900 text-base leading-tight min-w-0" title={item.display_name || item.product_name}>
                            {item.display_name || item.product_name}
                          </p>
                          {item.quantity < 0 && <Badge className="bg-rose-600 text-xs shrink-0">RETURN</Badge>}
                          {item.is_short_dated && <Badge className="bg-yellow-500 text-xs shrink-0">{item.discount_percent}% OFF</Badge>}
                          {item.is_oos && <Badge className="bg-red-500 text-xs shrink-0">OOS</Badge>}
                        </div>
                        <p className="text-sm text-slate-500 flex items-center gap-1.5 mt-1 flex-wrap">
                          <button
                            type="button"
                            onClick={() => {
                              const newPrice = window.prompt(
                                `Override price for "${item.product_name}"\n\nCurrent: $${item.unit_price.toFixed(2)}\nEnter the new price (e.g. price on the shelf label):`,
                                item.unit_price.toFixed(2)
                              );
                              if (newPrice !== null && newPrice.trim() !== '') {
                                updatePrice(item.product_id, newPrice, !!item.is_short_dated, !!item.is_wholesale);
                              }
                            }}
                            className={`group inline-flex items-center gap-1 px-2 py-0.5 rounded border ${
                              item.price_overridden
                                ? 'bg-emerald-50 border-emerald-300 text-emerald-700 font-bold'
                                : 'bg-blue-50 border-blue-300 text-blue-700 font-semibold hover:bg-blue-100 hover:border-blue-400'
                            }`}
                            title="Click to override price (e.g. match shelf label)"
                            data-testid={`override-price-${item.product_id}`}
                          >
                            <Pencil className="w-3 h-3" />
                            <span className="text-sm">${item.unit_price.toFixed(2)} each</span>
                          </button>
                          {item.price_overridden && item.original_price != null && (
                            <span className="text-xs line-through text-slate-400" data-testid={`original-price-${item.product_id}`}>
                              ${Number(item.original_price).toFixed(2)}
                            </span>
                          )}
                          <button 
                            onClick={() => { setMismatchProduct(item); setMismatchLabelPrice(''); setShowReportMismatch(true); }}
                            className="ml-1 text-orange-500 hover:text-orange-700" 
                            title="Report price mismatch"
                            data-testid={`report-mismatch-${item.product_id}`}
                          >
                            <AlertTriangle className="w-3 h-3 inline" />
                          </button>
                          {item.quantity > 0 && !item.is_gift_card && (
                            <button
                              type="button"
                              onClick={() => {
                                const current = item.line_discount_value
                                  ? (item.line_discount_type === 'percent' ? `${item.line_discount_value}%` : `${item.line_discount_value}`)
                                  : '';
                                setLineDiscountModal({ item, value: current, reason: item.line_discount_reason || '' });
                              }}
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border ${
                                item.line_discount_value
                                  ? 'bg-rose-50 border-rose-300 text-rose-700 font-bold'
                                  : 'bg-slate-50 border-slate-300 text-slate-500 hover:bg-rose-50 hover:border-rose-300 hover:text-rose-600'
                              }`}
                              title="Discount this line only (e.g. 10% or $5)"
                              data-testid={`line-discount-${item.product_id}`}
                            >
                              <Percent className="w-3 h-3" />
                              {item.line_discount_value && (
                                <span className="text-xs">
                                  {item.line_discount_type === 'percent' ? `${item.line_discount_value}% off` : `$${item.line_discount_value} off`}
                                </span>
                              )}
                            </button>
                          )}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50 shrink-0"
                        onClick={() => removeFromCart(item.product_id, !!item.is_short_dated, !!item.is_wholesale)}
                        data-testid={`remove-${item.product_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    {/* Row 2 — quantity controls + line total. Big touch targets so staff can't miss. */}
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5 shrink-0">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-10 w-10 shrink-0"
                          onClick={() => updateQuantity(item.product_id, -1, !!item.is_short_dated, !!item.is_wholesale)}
                          data-testid={`qty-minus-${item.product_id}`}
                        >
                          <Minus className="w-4 h-4" />
                        </Button>
                        <span className={`w-10 text-center font-bold text-lg ${item.quantity < 0 ? 'text-rose-600' : ''}`} data-testid={`qty-value-${item.product_id}`}>{item.quantity}</span>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-10 w-10 shrink-0"
                          onClick={() => updateQuantity(item.product_id, 1, !!item.is_short_dated, !!item.is_wholesale)}
                          data-testid={`qty-plus-${item.product_id}`}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className={`h-10 w-10 shrink-0 ${item.quantity < 0 ? 'text-rose-600 bg-rose-100 hover:bg-rose-200' : 'text-slate-500 hover:text-rose-600 hover:bg-rose-50'}`}
                          title={item.quantity < 0 ? 'Switch back to SALE' : 'Mark as RETURN/SWAP (customer paying difference)'}
                          onClick={() => toggleSwap(item.product_id, !!item.is_short_dated, !!item.is_wholesale)}
                          data-testid={`swap-toggle-${item.product_id}`}
                        >
                          <RotateCcw className="w-4 h-4" />
                        </Button>
                      </div>
                      <p className={`font-bold text-lg ${item.quantity < 0 ? 'text-rose-600' : 'text-slate-900'}`} data-testid={`line-total-${item.product_id}`}>
                        {lineDiscountAmount(item) > 0 && (
                          <span className="text-sm font-normal text-slate-400 line-through mr-1.5">${(item.quantity * item.unit_price).toFixed(2)}</span>
                        )}
                        ${(item.quantity * item.unit_price - lineDiscountAmount(item)).toFixed(2)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>

          {/* Frequently Bought Together — based on last item added */}
          {cart.length > 0 && fbtSuggestions.length > 0 && (
            <div className="px-4 py-3 bg-slate-100 border-t border-slate-200" data-testid="fbt-suggestions">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Customers also bought</p>
                <button
                  onClick={() => {
                    setFbtDismissedIds(prev => {
                      const next = new Set(prev);
                      fbtSuggestions.forEach(s => next.add(s.product_id));
                      return next;
                    });
                    setFbtSuggestions([]);
                  }}
                  className="text-[10px] text-slate-400 hover:text-slate-600"
                  title="Hide for this sale"
                >
                  ✕ Hide
                </button>
              </div>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {fbtSuggestions.map(s => {
                  const stock = selectedStore === 'orewa' ? s.stock_orewa : s.stock_eastridge;
                  return (
                    <button
                      key={s.product_id}
                      onClick={() => {
                        const productToAdd = products.find(p => p.id === s.product_id);
                        if (productToAdd) {
                          addToCart(productToAdd);
                        }
                      }}
                      disabled={stock <= 0}
                      className={`flex-shrink-0 w-32 p-2 bg-white rounded-md border border-slate-200 text-left hover:border-emerald-400 hover:shadow-sm transition-all ${stock <= 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                      data-testid={`fbt-${s.product_id}`}
                    >
                      <p className="text-xs font-medium text-slate-800 line-clamp-2 leading-tight h-8">{s.name}</p>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-sm font-bold text-emerald-700">${s.sell_price.toFixed(2)}</span>
                        <span className={`text-[10px] ${stock <= 0 ? 'text-red-500' : 'text-slate-400'}`}>{stock <= 0 ? 'OOS' : `${stock} left`}</span>
                      </div>
                      <p className="text-[10px] text-slate-400 mt-0.5">Bought together {s.co_purchase_count}×</p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Applied Gift Card */}
          {appliedGiftCard && (
            <div className="px-4 py-3 bg-purple-50 border-t border-purple-200">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-purple-800 flex items-center gap-1">
                  <Gift className="w-4 h-4" /> Gift Card Applied
                </span>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={removeAppliedGiftCard}
                  className="h-6 text-purple-600 hover:text-purple-800 p-1"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex justify-between text-sm text-purple-700 mt-1">
                <span>{appliedGiftCard.code}</span>
                <span>-${appliedGiftCard.redeemAmount.toFixed(2)}</span>
              </div>
            </div>
          )}

          {/* Loyalty Credit Redemption — flat $5 per transaction, only if balance >= $5 */}
          {selectedCustomer && cart.length > 0 && (() => {
            const eligible = (selectedCustomer.loyalty_points || 0) >= LOYALTY_MIN_BALANCE_TO_REDEEM;
            const isApplied = redeemPoints === LOYALTY_REDEEM_AMOUNT;
            return (
              <div className="px-4 py-3 bg-yellow-50 border-t border-yellow-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-yellow-800 flex items-center gap-1">
                    <Gift className="w-4 h-4" /> Loyalty Credit
                  </span>
                  <span className="text-xs text-yellow-600">${selectedCustomer.loyalty_points || 0} available</span>
                </div>
                {!eligible ? (
                  <p className="text-xs text-yellow-700">
                    Needs at least <b>${LOYALTY_MIN_BALANCE_TO_REDEEM}</b> credit to redeem (currently ${selectedCustomer.loyalty_points || 0}).
                  </p>
                ) : isApplied ? (
                  <Button
                    size="sm"
                    className="w-full bg-yellow-600 hover:bg-yellow-700 h-8"
                    onClick={() => { setRedeemPoints(0); setPointsDiscount(0); }}
                    data-testid="pos-remove-credit"
                  >
                    Remove ${LOYALTY_REDEEM_AMOUNT} Credit
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    className="w-full bg-yellow-600 hover:bg-yellow-700 h-8"
                    onClick={() => { setRedeemPoints(LOYALTY_REDEEM_AMOUNT); setPointsDiscount(LOYALTY_REDEEM_AMOUNT); }}
                    data-testid="pos-apply-credit"
                  >
                    Redeem ${LOYALTY_REDEEM_AMOUNT} Credit
                  </Button>
                )}
              </div>
            );
          })()}

          {/* Totals & Pay Button */}
          <div className="border-t p-4 bg-slate-50">
            <div className="space-y-1 mb-4">
              <div className="flex justify-between text-sm text-slate-600">
                <span>Subtotal (Ex GST)</span>
                <span>${subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm text-slate-600">
                <span>GST (15%)</span>
                <span>${tax.toFixed(2)}</span>
              </div>
              {pointsDiscount > 0 && (
                <div className="flex justify-between text-sm text-yellow-600">
                  <span>Credit Applied</span>
                  <span>-${pointsDiscount.toFixed(2)}</span>
                </div>
              )}
              {giftCardDiscount > 0 && (
                <div className="flex justify-between text-sm text-purple-600">
                  <span>Gift Card</span>
                  <span>-${giftCardDiscount.toFixed(2)}</span>
                </div>
              )}
              {storeCreditDiscount > 0 && (
                <div className="flex justify-between text-sm text-amber-600" data-testid="cart-store-credit-row">
                  <span>
                    Store Credit
                    <button
                      onClick={() => { setAppliedStoreCredit(0); toast.info('Store credit removed'); }}
                      className="ml-2 text-slate-400 hover:text-red-500"
                      data-testid="remove-store-credit-btn"
                    >
                      <X className="w-3 h-3 inline" />
                    </button>
                  </span>
                  <span>-${storeCreditDiscount.toFixed(2)}</span>
                </div>
              )}
              {manualDiscount > 0 && (
                <div className="flex justify-between text-sm text-rose-600" data-testid="cart-manual-discount-row">
                  <span>
                    Discount
                    {appliedDiscount.type === 'percent' && (
                      <span className="text-xs text-slate-500 ml-1">({appliedDiscount.value}% off)</span>
                    )}
                    <button
                      onClick={() => setAppliedDiscount({ type: null, value: 0, reason: null })}
                      className="ml-2 text-xs text-slate-400 hover:text-rose-600 underline"
                      data-testid="cart-clear-discount"
                    >clear</button>
                  </span>
                  <span>-${manualDiscount.toFixed(2)}</span>
                </div>
              )}
              <Separator />
              <div className={`flex justify-between text-xl font-bold ${total < 0 ? 'text-rose-600' : 'text-slate-900'}`}>
                <span>{total < 0 ? 'Refund Due' : 'Total (Inc GST)'}</span>
                <span data-testid="cart-total">${total.toFixed(2)}</span>
              </div>
              {cart.some(i => i.quantity < 0) && total >= 0 && total < 0.01 && (
                <p className="text-xs text-slate-500 italic">Even swap — no payment needed</p>
              )}
              {selectedCustomer && cart.length > 0 && (
                <div className="flex justify-between text-xs text-emerald-600">
                  <span>Credit to earn ({(loyaltySettings.points_per_dollar * 100).toFixed(0)}% back)</span>
                  <span>+${pointsEarned} credit</span>
                </div>
              )}
            </div>

            <Button
              onClick={handlePayClick}
              disabled={cart.length === 0}
              className={`w-full h-14 ${total < 0 ? 'bg-rose-600 hover:bg-rose-700' : 'bg-emerald-600 hover:bg-emerald-700'} text-white text-lg font-bold uppercase tracking-wide`}
              data-testid="pay-button"
            >
              <Calculator className="w-5 h-5 mr-2" />
              {total < 0 ? `Refund $${Math.abs(total).toFixed(2)}` : `Pay $${total.toFixed(2)}`}
            </Button>
            
            {/* Quick Action Buttons */}
            <div className="grid grid-cols-5 gap-2 mt-3">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  // Pre-fill modal with the currently applied discount (or blank)
                  setDiscountType(appliedDiscount.type || 'percent');
                  setDiscountValue(appliedDiscount.value ? String(appliedDiscount.value) : '');
                  setDiscountReason(appliedDiscount.reason || '');
                  setShowDiscountModal(true);
                }}
                disabled={cart.length === 0}
                className="bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100"
                data-testid="discount-button"
              >
                <Percent className="w-4 h-4 mr-1" /> Discount
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowRefundModal(true)}
                className="bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100"
                data-testid="refund-button"
              >
                <RotateCcw className="w-4 h-4 mr-1" /> Refund
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  setShowGiftCardModal(true);
                  setGiftCardTab('sell');
                  setGiftCardLookup(null);
                  setGiftCardCode('');
                }}
                className="bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100"
                data-testid="gift-card-button"
              >
                <Gift className="w-4 h-4 mr-1" /> Gift Card
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => { setSwapSearch(''); setShowSwapModal(true); }}
                className="bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100"
                data-testid="swap-button"
              >
                <ArrowRightLeft className="w-4 h-4 mr-1" /> Swap
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowLoyaltySettingsModal(true)}
                className="bg-yellow-50 text-yellow-700 border-yellow-200 hover:bg-yellow-100"
                data-testid="loyalty-settings-button"
              >
                <Star className="w-4 h-4 mr-1" /> Loyalty
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Swap / Exchange Modal — pick the item being RETURNED */}
      <Dialog open={showSwapModal} onOpenChange={(o) => { setShowSwapModal(o); if (!o) setSwapSearch(''); }}>
        <DialogContent className="sm:max-w-md" data-testid="swap-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-indigo-600" /> Swap / Exchange
            </DialogTitle>
            <DialogDescription>
              Pick the item the customer is <b>bringing back</b>. It goes in the cart as a minus line,
              then scan the replacement as normal — the total shows only the difference to pay.
            </DialogDescription>
          </DialogHeader>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Scan barcode or type the returned item's name..."
              value={swapSearch}
              onChange={(e) => setSwapSearch(e.target.value)}
              className="pl-9"
              autoFocus
              data-testid="swap-search"
            />
          </div>

          {swapSearch.trim().length >= 2 && (() => {
            const q = swapSearch.trim().toLowerCase();
            const matches = products.filter(p =>
              (p.name || '').toLowerCase().includes(q) || (p.barcode || '').includes(swapSearch.trim())
            ).slice(0, 8);
            if (matches.length === 0) return <p className="text-sm text-slate-400 text-center py-3">No products found</p>;
            return (
              <div className="border rounded-sm divide-y max-h-72 overflow-y-auto">
                {matches.map(p => (
                  <button
                    key={p.id}
                    onClick={() => addSwapReturn(p)}
                    className="w-full p-3 text-left hover:bg-indigo-50 flex items-center justify-between gap-2"
                    data-testid={`swap-result-${p.id}`}
                  >
                    <div>
                      <p className="font-medium text-sm">{p.name}</p>
                      <p className="text-xs text-slate-500">{p.barcode}</p>
                    </div>
                    <span className="font-bold text-rose-600 whitespace-nowrap">−${(p.sell_price || 0).toFixed(2)}</span>
                  </button>
                ))}
              </div>
            );
          })()}

          <p className="text-xs text-slate-500">
            Returned stock goes back on the shelf automatically. If the swap is dearer the customer pays the difference;
            if it's cheaper the total shows a refund due.
          </p>
        </DialogContent>
      </Dialog>

      {/* Condition Quick Picks Modal */}
      <Dialog open={showConditionsModal} onOpenChange={(o) => { setShowConditionsModal(o); if (o) loadConditionList(); }}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="conditions-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase flex items-center gap-2">
              <HeartPulse className="w-5 h-5 text-teal-600" /> Condition Lookup
            </DialogTitle>
            <DialogDescription>
              Type what the customer has — you'll see the screening questions to ask first, then the protocol products. Tap + to add to the cart.
            </DialogDescription>
          </DialogHeader>

          <Input
            placeholder="Condition or ingredient, e.g. sleep, menopause, magnesium..."
            value={conditionQuery}
            onChange={(e) => setConditionQuery(e.target.value)}
            autoFocus
            data-testid="condition-search"
          />

          <div className="flex flex-wrap gap-2">
            {(conditionList.length ? conditionList : []).filter(c => {
              const q = conditionQuery.trim().toLowerCase();
              return !q || c.name.toLowerCase().includes(q) || (c.keywords || []).some(k => k.toLowerCase().includes(q));
            }).map(c => (
              <button
                key={c.name}
                onClick={() => fetchConditionProducts(c.name)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide border transition-colors ${
                  activeCondition === c.name
                    ? 'bg-teal-600 text-white border-teal-600'
                    : 'bg-teal-50 text-teal-700 border-teal-200 hover:bg-teal-100'
                }`}
                data-testid={`condition-chip-${c.name.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
              >
                {c.name}
              </button>
            ))}
          </div>

          {activeCondition && (() => {
            const proto = conditionList.find(c => c.name === activeCondition);
            return proto?.staff_notes
              ? <ProtocolTillGuide condition={activeCondition} staffNotes={proto.staff_notes} />
              : <p className="text-xs text-slate-400 italic" data-testid="condition-no-screening">No screening notes for {activeCondition} yet — add them in Protocols.</p>;
          })()}

          {loadingConditionProducts && (
            <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-teal-500" /></div>
          )}

          {!loadingConditionProducts && activeCondition && conditionResults.length > 0 && (
            <div className="space-y-2" data-testid="condition-results">
              <p className="text-xs uppercase tracking-wide text-slate-500 font-bold">
                Top picks for {activeCondition}
              </p>
              {conditionResults.map(p => (
                <div key={p.id} className="flex items-center justify-between gap-2 border rounded-sm px-3 py-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate" title={p.name}>
                      {p.pinned && <Star className="w-3.5 h-3.5 inline mr-1 text-amber-500 fill-amber-400" />}
                      {p.name}
                    </p>
                    <p className="text-xs text-slate-500">
                      ${(p.sell_price || 0).toFixed(2)} · {p.order_in ? <span className="text-indigo-600 font-bold">Order-in · Dispensary</span> : <span className={p.store_quantity > 0 ? '' : 'text-red-500 font-bold'}>{p.store_quantity} in stock</span>} · <span className="text-teal-600">{p.matched_term}</span>
                    </p>
                    {p.benefit && <p className="text-xs text-teal-700">{p.benefit}</p>}
                    {p.caution && <p className="text-xs text-amber-700 font-medium">⚠ {p.caution}</p>}
                        {p.dose && <p className="text-xs text-indigo-700">Dose: {p.dose}</p>}
                  </div>
                  <Button
                    size="sm"
                    onClick={() => { addToCart({ ...p, condition_source: activeCondition }); toast.success(`${p.name} added`); }}
                    className="shrink-0 bg-emerald-600 hover:bg-emerald-700 h-8 w-8 p-0"
                    data-testid={`condition-add-${p.id}`}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Manual Discount Modal — Lightspeed-style */}
      <Dialog open={showDiscountModal} onOpenChange={setShowDiscountModal}>
        <DialogContent className="sm:max-w-sm" data-testid="discount-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase">Add Discount</DialogTitle>
            <DialogDescription>
              This discount will be applied to the whole sale.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex items-center gap-3">
              <div className="flex border border-slate-300 rounded overflow-hidden">
                <button
                  type="button"
                  onClick={() => setDiscountType('percent')}
                  className={`px-4 py-2 text-base font-bold ${discountType === 'percent' ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600'}`}
                  data-testid="discount-type-percent"
                >%</button>
                <button
                  type="button"
                  onClick={() => setDiscountType('dollar')}
                  className={`px-4 py-2 text-base font-bold border-l border-slate-300 ${discountType === 'dollar' ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600'}`}
                  data-testid="discount-type-dollar"
                >$</button>
              </div>
              <div className="relative flex-1">
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={discountValue}
                  onChange={(e) => setDiscountValue(e.target.value)}
                  className="text-right pr-8 text-lg font-bold h-11"
                  placeholder="0"
                  autoFocus
                  data-testid="discount-value-input"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 font-bold">
                  {discountType === 'percent' ? '%' : '$'}
                </span>
              </div>
            </div>
            <p className="text-xs text-slate-500">
              Cart total: <strong>${cartTotal.toFixed(2)}</strong>
              {discountValue && !Number.isNaN(parseFloat(discountValue)) && parseFloat(discountValue) > 0 && (
                <> · Discount preview: <strong className="text-rose-600">
                  -${(discountType === 'percent'
                      ? Math.min(cartTotal, cartTotal * (parseFloat(discountValue) / 100))
                      : Math.min(cartTotal, parseFloat(discountValue))
                    ).toFixed(2)}</strong>
                </>
              )}
            </p>
            <div className="space-y-1.5">
              <p className="text-sm font-semibold text-slate-700">Reason <span className="text-rose-600">*</span></p>
              <div className="flex flex-wrap gap-1.5" data-testid="discount-reason-options">
                {DISCOUNT_REASONS.map(r => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setDiscountReason(r)}
                    className={`px-2.5 py-1.5 text-xs font-medium rounded border transition-colors ${
                      discountReason === r
                        ? 'bg-rose-600 border-rose-600 text-white'
                        : 'bg-white border-slate-300 text-slate-600 hover:border-rose-400 hover:text-rose-600'
                    }`}
                    data-testid={`discount-reason-${r.toLowerCase().replace(/\s+/g, '-')}`}
                  >{r}</button>
                ))}
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setShowDiscountModal(false)} data-testid="discount-cancel-btn">Cancel</Button>
            <Button
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              onClick={() => {
                const val = parseFloat(discountValue);
                if (Number.isNaN(val) || val <= 0) {
                  setAppliedDiscount({ type: null, value: 0, reason: null });
                  setShowDiscountModal(false);
                  return;
                }
                if (discountType === 'percent' && val > 100) {
                  toast.error('Percent discount cannot exceed 100%');
                  return;
                }
                if (!discountReason) {
                  toast.error('Please pick a reason for the discount');
                  return;
                }
                setAppliedDiscount({ type: discountType, value: val, reason: discountReason });
                toast.success(`${discountType === 'percent' ? val + '%' : '$' + val.toFixed(2)} discount applied (${discountReason})`);
                setShowDiscountModal(false);
              }}
              data-testid="discount-apply-btn"
            >Apply Discount</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Per-Line Discount Modal */}
      <Dialog open={!!lineDiscountModal} onOpenChange={(open) => { if (!open) setLineDiscountModal(null); }}>
        <DialogContent className="sm:max-w-sm" data-testid="line-discount-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase">Line Discount</DialogTitle>
            <DialogDescription>
              {lineDiscountModal?.item?.product_name} — this line only. Enter 10% for percent, or 5 for $5 off.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <Input
              value={lineDiscountModal?.value || ''}
              onChange={(e) => setLineDiscountModal(prev => ({ ...prev, value: e.target.value }))}
              className="text-right text-lg font-bold h-11"
              placeholder="e.g. 10% or 5"
              autoFocus
              data-testid="line-discount-value-input"
            />
            <div className="space-y-1.5">
              <p className="text-sm font-semibold text-slate-700">Reason <span className="text-rose-600">*</span></p>
              <div className="flex flex-wrap gap-1.5" data-testid="line-discount-reason-options">
                {DISCOUNT_REASONS.map(r => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setLineDiscountModal(prev => ({ ...prev, reason: r }))}
                    className={`px-2.5 py-1.5 text-xs font-medium rounded border transition-colors ${
                      lineDiscountModal?.reason === r
                        ? 'bg-rose-600 border-rose-600 text-white'
                        : 'bg-white border-slate-300 text-slate-600 hover:border-rose-400 hover:text-rose-600'
                    }`}
                    data-testid={`line-discount-reason-${r.toLowerCase().replace(/\s+/g, '-')}`}
                  >{r}</button>
                ))}
              </div>
            </div>
          </div>
          <div className="flex justify-between gap-2 pt-2">
            {lineDiscountModal?.item?.line_discount_value ? (
              <Button
                variant="outline"
                className="text-rose-600 border-rose-300 hover:bg-rose-50"
                onClick={() => {
                  const it = lineDiscountModal.item;
                  setLineDiscount(it.product_id, !!it.is_short_dated, !!it.is_wholesale, '', null);
                  setLineDiscountModal(null);
                }}
                data-testid="line-discount-remove-btn"
              >Remove</Button>
            ) : <span />}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setLineDiscountModal(null)} data-testid="line-discount-cancel-btn">Cancel</Button>
              <Button
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                onClick={() => {
                  const raw = (lineDiscountModal?.value || '').trim();
                  if (raw === '' || raw === '0') {
                    const it = lineDiscountModal.item;
                    setLineDiscount(it.product_id, !!it.is_short_dated, !!it.is_wholesale, '', null);
                    setLineDiscountModal(null);
                    return;
                  }
                  const num = parseFloat(raw.replace('%', '').replace('$', ''));
                  if (isNaN(num) || num <= 0) {
                    toast.error('Enter a discount like 10% or 5');
                    return;
                  }
                  if (raw.includes('%') && num > 100) {
                    toast.error('Percent discount must be 100% or less');
                    return;
                  }
                  if (!lineDiscountModal?.reason) {
                    toast.error('Please pick a reason for the discount');
                    return;
                  }
                  const it = lineDiscountModal.item;
                  setLineDiscount(it.product_id, !!it.is_short_dated, !!it.is_wholesale, raw, lineDiscountModal.reason);
                  setLineDiscountModal(null);
                }}
                data-testid="line-discount-apply-btn"
              >Apply</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Customer Modal */}
      <Dialog open={showCustomerModal} onOpenChange={setShowCustomerModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase">
              Customer Loyalty
            </DialogTitle>
            <DialogDescription>
              Search existing customer or add new one
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Search existing */}
            <div>
              <label className="text-sm font-medium text-slate-700">Search Customer</label>
              <div className="relative mt-1">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by phone or name..."
                  value={customerSearch}
                  onChange={(e) => setCustomerSearch(e.target.value)}
                  className="pl-9"
                  data-testid="customer-search"
                />
              </div>
              
              {searchingCustomer && <p className="text-sm text-slate-500 mt-2">Searching...</p>}
              
              {customerResults.length > 0 && (
                <div className="mt-2 border rounded-sm max-h-48 overflow-y-auto">
                  {customerResults.map((customer) => (
                    <button
                      key={customer.id}
                      onClick={() => {
                        setSelectedCustomer(customer);
                        setShowCustomerModal(false);
                        setCustomerSearch('');
                        setCustomerResults([]);
                      }}
                      className="w-full p-3 text-left hover:bg-slate-50 border-b last:border-b-0"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{customer.name}</p>
                          <p className="text-sm text-slate-500">{customer.phone}</p>
                          {customer.email && (
                            <p className="text-xs text-slate-400">{customer.email}</p>
                          )}
                        </div>
                        <Badge variant="outline" className="bg-yellow-50">
                          <Star className="w-3 h-3 mr-1 text-yellow-500" />
                          ${customer.loyalty_points || 0} credit
                        </Badge>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <Separator />

            {/* Add new customer */}
            <div>
              <label className="text-sm font-medium text-slate-700 flex items-center gap-1">
                <UserPlus className="w-4 h-4" /> New Loyalty Member
              </label>
              <div className="space-y-2 mt-2">
                <Input
                  placeholder="Full name *"
                  value={newCustomerName}
                  onChange={(e) => setNewCustomerName(e.target.value)}
                  data-testid="new-customer-name"
                />
                <Input
                  placeholder="Phone number *"
                  value={newCustomerPhone}
                  onChange={(e) => setNewCustomerPhone(e.target.value)}
                  data-testid="new-customer-phone"
                />
                <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex items-start gap-2" data-testid="email-nudge-pos">
                  <span className="text-base leading-none">📧</span>
                  <span><b>Always ask for their email!</b> It's how we send marketing — birthday offers, sales, new product alerts. Loyalty without email = they don't come back.</span>
                </div>
                <Input
                  placeholder="Email (for marketing) — please ask!"
                  type="email"
                  value={newCustomerEmail}
                  onChange={(e) => setNewCustomerEmail(e.target.value)}
                  data-testid="new-customer-email"
                />
                <Button onClick={createCustomer} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="create-customer">
                  <UserPlus className="w-4 h-4 mr-2" /> Add to Loyalty Program
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Modal */}
      {showPaymentModal && (
      <Dialog open={showPaymentModal} onOpenChange={(open) => {
        // Block dialog close while EFTPOS is polling — terminal is live and waiting
        // for the card. Closing the modal would leave the staff free to modify the
        // cart, which is exactly the bug we're guarding against.
        if (!open && smartpayPolling && cart.length > 0) {
          const ok = window.confirm('EFTPOS is still waiting for the terminal.\n\nClose anyway? (Also press CANCEL on the terminal so the customer is not charged.)');
          if (!ok) return;
          stopSmartpayPolling();
          setSmartpayPolling(false);
          setSmartpayPollAttempts(0);
          setSmartpayRequestedAmount(null);
          setProcessing(false);
          setPaymentMethod('');
        }
        setShowPaymentModal(open);
      }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-bold uppercase text-center">
              Payment
            </DialogTitle>
            <DialogDescription className="text-center text-slate-500">
              Total: <span className="text-2xl font-bold text-slate-900">${total.toFixed(2)}</span>
            </DialogDescription>
          </DialogHeader>

          {!paymentMethod ? (
            <div className="space-y-4 py-4">
              {/* Primary payment options — Cash | EFTPOS | Gift Card | Store Credit */}
              <div className="grid grid-cols-2 gap-3">
                <Button
                  onClick={() => setPaymentMethod('cash')}
                  className="h-24 flex-col bg-emerald-600 hover:bg-emerald-700"
                  data-testid="pay-cash"
                >
                  <Banknote className="w-8 h-8 mb-1" />
                  <span className="font-bold uppercase">Cash</span>
                </Button>
                <Button
                  onClick={() => {
                    if (smartpayConnected && selectedTerminalId) {
                      setPaymentMethod('eftpos');
                      initiateSmartpayPayment();
                    } else {
                      const proceedManual = window.confirm(
                        'No SmartConnect terminal is paired to this register.\n\n' +
                        'Click OK to record this as a MANUAL card sale (no money will be charged from the customer — you must charge them via another method).\n\n' +
                        'Click Cancel to go back and pair a terminal first.'
                      );
                      if (proceedManual) setPaymentMethod('card_manual');
                    }
                  }}
                  className="h-24 flex-col bg-blue-600 hover:bg-blue-700"
                  data-testid="pay-eftpos"
                >
                  <CreditCard className="w-8 h-8 mb-1" />
                  <span className="font-bold uppercase">EFTPOS</span>
                  {!smartpayConnected && <span className="text-[10px] opacity-75">Manual</span>}
                </Button>
                <Button
                  onClick={() => { setShowPaymentModal(false); setShowGiftCardModal(true); }}
                  className="h-24 flex-col bg-purple-600 hover:bg-purple-700"
                  data-testid="pay-giftcard"
                >
                  <Gift className="w-8 h-8 mb-1" />
                  <span className="font-bold uppercase">Gift Card</span>
                </Button>
                <Button
                  onClick={() => {
                    const balance = selectedCustomer?.store_credit || 0;
                    if (!selectedCustomer) {
                      toast.error('Select a customer first to use store credit');
                      return;
                    }
                    if (balance <= 0) {
                      toast.error(`${selectedCustomer.name} has no store credit`);
                      return;
                    }
                    setStoreCreditUseAmount(Math.min(balance, total).toFixed(2));
                    setPaymentMethod('store_credit');
                  }}
                  disabled={!selectedCustomer || (selectedCustomer?.store_credit || 0) <= 0}
                  className="h-24 flex-col bg-amber-600 hover:bg-amber-700 disabled:opacity-50"
                  data-testid="pay-store-credit"
                >
                  <Wallet className="w-8 h-8 mb-1" />
                  <span className="font-bold uppercase">Store Credit</span>
                  <span className="text-[10px] opacity-90">
                    {selectedCustomer
                      ? (selectedCustomer.store_credit || 0) < 0
                        ? `Owes $${Math.abs(selectedCustomer.store_credit).toFixed(2)}`
                        : `$${(selectedCustomer.store_credit || 0).toFixed(2)} available`
                      : 'No customer selected'}
                  </span>
                </Button>
              </div>

              {/* On Account — customer takes goods now, owes us the money */}
              <Button
                variant="outline"
                onClick={() => {
                  if (!selectedCustomer) {
                    toast.error('Attach a customer first — the debt has to be recorded against someone');
                    return;
                  }
                  setOnAccountPin('');
                  setPaymentMethod('on_account');
                }}
                className="w-full h-10 border-red-300 text-red-700 hover:bg-red-50"
                data-testid="pay-on-account"
              >
                <AlertTriangle className="w-4 h-4 mr-2" />
                On Account — Customer Owes Us
              </Button>

              {/* Split — kept for cases like "$50 cash + rest on card" */}
              <div className="border-t pt-3">
                <Button
                  variant="outline"
                  onClick={() => setPaymentMethod('split')}
                  className="w-full h-10 border-purple-200 text-purple-700 hover:bg-purple-50"
                  data-testid="pay-split"
                >
                  <ArrowRightLeft className="w-4 h-4 mr-2" />
                  Split Payment (Cash + Card)
                </Button>
              </div>
            </div>
          ) : paymentMethod === 'cash' ? (
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Cash Received</label>
                <Input
                  type="number"
                  step="0.01"
                  min={total}
                  value={cashReceived}
                  onChange={(e) => setCashReceived(e.target.value)}
                  placeholder="Enter amount"
                  className="h-14 text-2xl text-center font-bold mt-1"
                  autoFocus
                  data-testid="cash-received"
                />
              </div>

              {/* Quick Cash Buttons */}
              <div className="grid grid-cols-4 gap-2">
                {quickCashAmounts.map((amount, idx) => (
                  <Button
                    key={idx}
                    variant="outline"
                    onClick={() => setCashReceived(amount.toFixed(2))}
                    className="h-12"
                  >
                    ${amount.toFixed(0)}
                  </Button>
                ))}
              </div>

              {parseFloat(cashReceived) >= total && (
                <div className="text-center p-4 bg-emerald-50 rounded-sm">
                  <p className="text-sm text-emerald-600">Change Due</p>
                  <p className="text-3xl font-bold text-emerald-700" data-testid="change-due">
                    ${change.toFixed(2)}
                  </p>
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setPaymentMethod('')}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button
                  onClick={processCashPayment}
                  disabled={processing || parseFloat(cashReceived) < total}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  data-testid="confirm-payment"
                >
                  {processing ? <Loader2 className="w-5 h-5 spinner" /> : <Check className="w-5 h-5 mr-2" />}
                  Confirm
                </Button>
              </div>
            </div>
          ) : paymentMethod === 'on_account' ? (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-red-50 border border-red-200 rounded-sm space-y-1">
                <p className="font-bold text-red-800 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" /> {selectedCustomer?.name} will OWE ${total.toFixed(2)}
                </p>
                <p className="text-xs text-red-600">
                  The sale is recorded and their store credit goes to
                  {' '}<b>${((selectedCustomer?.store_credit || 0) - total).toFixed(2)}</b>.
                  When they pay, add it back via their Store Credit dialog.
                </p>
                <p className="text-xs text-slate-500">
                  Current balance: ${(selectedCustomer?.store_credit || 0).toFixed(2)}
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700">Manager PIN</label>
                <Input
                  type="password"
                  value={onAccountPin}
                  onChange={(e) => setOnAccountPin(e.target.value)}
                  placeholder="Enter manager PIN"
                  className="h-12 text-xl text-center font-bold mt-1"
                  autoFocus
                  data-testid="on-account-pin"
                />
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => { setPaymentMethod(''); setOnAccountPin(''); }}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button
                  onClick={handleOnAccountConfirm}
                  disabled={processing || !onAccountPin.trim()}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  data-testid="confirm-on-account"
                >
                  {processing ? <Loader2 className="w-5 h-5 spinner" /> : <Check className="w-5 h-5 mr-2" />}
                  Record Debt & Complete
                </Button>
              </div>
            </div>
          ) : paymentMethod === 'store_credit' ? (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-amber-50 rounded-sm space-y-1">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-amber-800">{selectedCustomer?.name}</span>
                  <span className="text-2xl font-bold text-amber-700" data-testid="store-credit-balance">
                    ${(selectedCustomer?.store_credit || 0).toFixed(2)}
                  </span>
                </div>
                <p className="text-xs text-amber-600">Store credit available</p>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700">Amount to Use</label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max={Math.min(selectedCustomer?.store_credit || 0, total)}
                  value={storeCreditUseAmount}
                  onChange={(e) => setStoreCreditUseAmount(e.target.value)}
                  className="h-14 text-2xl text-center font-bold mt-1"
                  autoFocus
                  data-testid="store-credit-amount"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Max ${Math.min(selectedCustomer?.store_credit || 0, total).toFixed(2)}
                </p>
              </div>

              {(() => {
                const amt = Math.min(parseFloat(storeCreditUseAmount) || 0, selectedCustomer?.store_credit || 0, total);
                if (amt <= 0) return null;
                return amt >= total - 0.005 ? (
                  <div className="text-center p-3 bg-emerald-50 rounded-sm text-emerald-700 text-sm font-medium">
                    Covers the full sale — no other payment needed
                  </div>
                ) : (
                  <div className="text-center p-3 bg-blue-50 rounded-sm text-blue-700 text-sm font-medium">
                    ${(total - amt).toFixed(2)} remaining — pay by Cash / EFTPOS after applying
                  </div>
                );
              })()}

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => { setPaymentMethod(''); setStoreCreditUseAmount(''); }}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button
                  onClick={handleStoreCreditConfirm}
                  disabled={processing || !(parseFloat(storeCreditUseAmount) > 0)}
                  className="flex-1 bg-amber-600 hover:bg-amber-700"
                  data-testid="confirm-store-credit"
                >
                  {processing ? <Loader2 className="w-5 h-5 spinner" /> : <Check className="w-5 h-5 mr-2" />}
                  {(() => {
                    const amt = Math.min(parseFloat(storeCreditUseAmount) || 0, selectedCustomer?.store_credit || 0, total);
                    return amt >= total - 0.005 && amt > 0 ? 'Complete Sale' : 'Apply Credit';
                  })()}
                </Button>
              </div>
            </div>
          ) : paymentMethod === 'split' ? (
            /* Split Payment Section */
            <div className="space-y-4 py-4">
              <div className="p-3 bg-purple-50 rounded-lg text-sm text-purple-800">
                <strong>Split Payment:</strong> Enter cash amount, remainder goes to card.
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Cash Amount</label>
                <Input
                  type="number"
                  step="0.01"
                  min={0}
                  max={total}
                  value={cashReceived}
                  onChange={(e) => setCashReceived(e.target.value)}
                  placeholder="0.00"
                  className="h-12 text-xl text-center font-bold mt-1"
                  autoFocus
                />
              </div>
              <div className="grid grid-cols-2 gap-4 p-3 bg-slate-50 rounded-lg">
                <div className="text-center">
                  <p className="text-xs text-slate-500">Cash</p>
                  <p className="text-lg font-bold text-emerald-600">${(parseFloat(cashReceived) || 0).toFixed(2)}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-slate-500">Card</p>
                  <p className="text-lg font-bold text-blue-600">${Math.max(0, total - (parseFloat(cashReceived) || 0)).toFixed(2)}</p>
                </div>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setPaymentMethod('')} className="flex-1">
                  Back
                </Button>
                <Button
                  onClick={() => processSplitPayment(parseFloat(cashReceived) || 0, total - (parseFloat(cashReceived) || 0))}
                  disabled={processing || !cashReceived || parseFloat(cashReceived) <= 0 || parseFloat(cashReceived) >= total}
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                >
                  {processing ? <Loader2 className="w-5 h-5 spinner" /> : <Check className="w-5 h-5 mr-2" />}
                  Confirm Split
                </Button>
              </div>
            </div>
          ) : ['visa', 'mastercard', 'amex', 'other_card', 'card_manual'].includes(paymentMethod) ? (
            /* Manual Card Payment (without EFTPOS terminal) */
            <div className="space-y-4 py-4">
              <div className="text-center p-6 bg-blue-50 rounded-lg">
                <CreditCard className="w-12 h-12 mx-auto mb-3 text-blue-500" />
                <p className="text-lg font-bold text-blue-700 capitalize">
                  {paymentMethod === 'card_manual' ? 'Card' : paymentMethod.replace('_', ' ')} Payment
                </p>
                <p className="text-2xl font-bold text-slate-900 mt-2">${total.toFixed(2)}</p>
              </div>
              <div className="p-3 bg-yellow-50 rounded-lg text-sm text-yellow-800">
                Process the payment on your external terminal, then confirm below.
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setPaymentMethod('')} className="flex-1">
                  Back
                </Button>
                <Button
                  onClick={() => processCardPayment(paymentMethod)}
                  disabled={processing}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  {processing ? <Loader2 className="w-5 h-5 spinner" /> : <Check className="w-5 h-5 mr-2" />}
                  Payment Received
                </Button>
              </div>
            </div>
          ) : paymentMethod === 'eftpos' ? (
            /* EFTPOS Terminal Payment */
            <div className="space-y-4 py-4">
              {/* CRITICAL — lock notice. Tells staff the cart is frozen and shows
                  the exact amount the terminal is charging. Prevents the "cart was
                  modified mid-payment" bug. */}
              {smartpayPolling && smartpayRequestedAmount != null && (
                <div className="rounded-md border-2 border-amber-400 bg-amber-50 px-3 py-2 text-center" data-testid="eftpos-lock-banner">
                  <p className="text-[11px] uppercase tracking-wider font-bold text-amber-900">Cart locked — Terminal is charging</p>
                  <p className="text-2xl font-extrabold text-amber-900">${smartpayRequestedAmount.toFixed(2)}</p>
                  <p className="text-[11px] text-amber-800">Do NOT modify the cart. Cancel below to add items.</p>
                </div>
              )}

              {/* Which terminal & register_id are we targeting? Visible so staff can
                  immediately spot if the wrong physical terminal would be triggered
                  (e.g. after a terminal swap). */}
              {(() => {
                const t = smartpayTerminals.find(x => x.id === selectedTerminalId);
                if (!t) return null;
                return (
                  <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700" data-testid="eftpos-target-info">
                    <p className="font-semibold">Targeting: <span className="text-slate-900">{t.label || t.name || 'Terminal'}</span></p>
                    <p className="text-slate-500 font-mono">register_id: {t.register_id}</p>
                    <p className="text-slate-500">env: {t.environment || 'dev'}</p>
                  </div>
                );
              })()}

              <div className="text-center p-8 border-2 border-dashed border-blue-200 rounded-sm bg-blue-50">
                {smartpayPolling ? (
                  <>
                    <Loader2 className="w-16 h-16 mx-auto mb-4 text-blue-500 spinner" />
                    <p className="text-lg font-medium text-blue-700">Present Card to Terminal</p>
                    <p className="text-sm text-blue-600 mt-2">
                      Waiting for payment... ({Math.floor(smartpayPollAttempts * 2)}s elapsed)
                    </p>
                  </>
                ) : (
                  <>
                    <CreditCard className="w-16 h-16 mx-auto mb-4 text-blue-500" />
                    <p className="text-lg font-medium text-blue-700">Connecting to Terminal...</p>
                  </>
                )}
              </div>

              {/* Escape hatch: appears after ~30s of polling. Lets staff save the sale
                  when the card already cleared on the terminal but SmartConnect's
                  status poll is stuck/unreachable. */}
              {smartpayPolling && smartpayPollAttempts > 15 && (
                <div className="p-3 bg-amber-50 border-2 border-amber-300 rounded">
                  <p className="text-sm font-medium text-amber-900 mb-2">
                    Stuck waiting? If the terminal already shows <strong>APPROVED</strong> and the card was charged, you can save this sale manually.
                  </p>
                  <Button
                    onClick={() => {
                      if (!window.confirm('Confirm: the EFTPOS terminal has already approved this payment and the card was successfully charged?\n\nClick OK to save the sale as paid by card.')) return;
                      stopSmartpayPolling();
                      setSmartpayPolling(false);
                      completeSale('card', {
                        manual_override: true,
                        transaction_result: 'OK-ACCEPTED-MANUAL',
                        approved: true,
                      });
                    }}
                    className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                    data-testid="force-complete-eftpos"
                  >
                    Card was charged — Save sale now
                  </Button>
                </div>
              )}

              <Button
                variant="outline"
                onClick={() => {
                  if (smartpayPolling) {
                    const ok = window.confirm(
                      'Cancel EFTPOS?\n\n' +
                      '⚠️ This stops the till from waiting, but the terminal may STILL CHARGE the card if the customer taps now.\n\n' +
                      `Recommended: ask the customer NOT to tap, then press CANCEL on the EFTPOS terminal as well.\n\n` +
                      `Amount sent to terminal: $${(smartpayRequestedAmount || 0).toFixed(2)}\n\n` +
                      'Continue?'
                    );
                    if (!ok) return;
                  }
                  stopSmartpayPolling();
                  setPaymentMethod('');
                  setSmartpayPolling(false);
                  setSmartpayPollAttempts(0);
                  setSmartpayRequestedAmount(null);
                  setProcessing(false);
                }}
                className="w-full"
                data-testid="cancel-eftpos"
              >
                {smartpayPolling ? 'Cancel EFTPOS (also press cancel on terminal!)' : 'Cancel'}
              </Button>
            </div>
          ) : (
            /* Fallback for card payment via terminal */
            <div className="space-y-4 py-4">
              {smartpayPolling && smartpayRequestedAmount != null && (
                <div className="rounded-md border-2 border-amber-400 bg-amber-50 px-3 py-2 text-center">
                  <p className="text-[11px] uppercase tracking-wider font-bold text-amber-900">Cart locked — Terminal is charging</p>
                  <p className="text-2xl font-extrabold text-amber-900">${smartpayRequestedAmount.toFixed(2)}</p>
                </div>
              )}
              <div className="text-center p-8 border-2 border-dashed border-blue-200 rounded-sm bg-blue-50">
                {smartpayPolling ? (
                  <>
                    <Loader2 className="w-16 h-16 mx-auto mb-4 text-blue-500 spinner" />
                    <p className="text-lg font-medium text-blue-700">Present Card to Terminal</p>
                    <p className="text-sm text-blue-600 mt-2">
                      Waiting for payment... ({Math.floor(smartpayPollAttempts * 2)}s elapsed)
                    </p>
                  </>
                ) : (
                  <>
                    <CreditCard className="w-16 h-16 mx-auto mb-4 text-blue-500" />
                    <p className="text-lg font-medium text-blue-700">Connecting to Terminal...</p>
                  </>
                )}
              </div>

              {smartpayPolling && smartpayPollAttempts > 15 && (
                <div className="p-3 bg-amber-50 border-2 border-amber-300 rounded">
                  <p className="text-sm font-medium text-amber-900 mb-2">
                    Stuck waiting? If the terminal already shows <strong>APPROVED</strong> and the card was charged, you can save this sale manually.
                  </p>
                  <Button
                    onClick={() => {
                      if (!window.confirm('Confirm: the EFTPOS terminal has already approved this payment and the card was successfully charged?\n\nClick OK to save the sale as paid by card.')) return;
                      stopSmartpayPolling();
                      setSmartpayPolling(false);
                      completeSale('card', {
                        manual_override: true,
                        transaction_result: 'OK-ACCEPTED-MANUAL',
                        approved: true,
                      });
                    }}
                    className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                    data-testid="force-complete-eftpos-fallback"
                  >
                    Card was charged — Save sale now
                  </Button>
                </div>
              )}

              <Button
                variant="outline"
                onClick={() => {
                  stopSmartpayPolling();
                  setPaymentMethod('');
                  setSmartpayPolling(false);
                  setSmartpayPollAttempts(0);
                  setProcessing(false);
                }}
                className="w-full"
              >
                Cancel
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      )}

      {/* Receipt Modal */}
      {/* Mounted only while open: unmounting removes the dialog instantly, so it can never be left on screen
          waiting for a close animation that the browser skipped (print dialog / terminal popup / hidden window). */}
      {showReceiptModal && (
      <Dialog open={showReceiptModal} onOpenChange={setShowReceiptModal}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase text-center flex items-center justify-center gap-2">
              <Check className="w-6 h-6 text-emerald-500" /> Sale Complete
            </DialogTitle>
          </DialogHeader>

          {lastReceipt && (
            <div className="space-y-4" data-testid="receipt">
              <div className="text-center border-b pb-4">
                <h3 className="font-heading text-lg font-bold uppercase">{user?.business_name || 'SupplyTrack'}</h3>
                <p className="font-medium text-slate-700">{getCurrentStore()?.name?.toUpperCase()}</p>
                {getCurrentStore()?.address && (
                  <p className="text-xs text-slate-500">{getCurrentStore().address}</p>
                )}
                {getCurrentStore()?.phone && (
                  <p className="text-xs text-slate-500">Ph: {getCurrentStore().phone}</p>
                )}
                {getCurrentStore()?.website && (
                  <p className="text-xs text-slate-500">{getCurrentStore().website}</p>
                )}
                {getCurrentStore()?.gst_number && (
                  <p className="text-xs text-slate-500">GST: {getCurrentStore().gst_number}</p>
                )}
                <p className="text-sm text-slate-500 mt-2">TAX INVOICE</p>
                <p className="text-xs text-slate-400">
                  {formatDateTime(lastReceipt.created_at)}
                </p>
              </div>

              <div className="text-sm text-slate-600">
                {lastReceipt.customer_name && lastReceipt.customer_name !== 'Walk-in Customer' && (
                  <p>Customer: {lastReceipt.customer_name}</p>
                )}
                <p>Receipt #: {lastReceipt.id?.slice(-8).toUpperCase()}</p>
              </div>

              <Separator />

              <div className="space-y-2">
                {lastReceipt.items.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span>
                      {item.quantity}x {item.product_name}
                      {(item.line_discount || 0) > 0 && (
                        <span className="text-rose-600"> (−${Number(item.line_discount).toFixed(2)} disc)</span>
                      )}
                    </span>
                    <span>${(item.quantity * item.unit_price - (item.line_discount || 0)).toFixed(2)}</span>
                  </div>
                ))}
              </div>

              <Separator />

              <div className="space-y-1 text-sm">
                <div className="flex justify-between text-slate-600">
                  <span>Subtotal (Ex GST)</span>
                  <span>${lastReceipt.subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-slate-600">
                  <span>GST (15%)</span>
                  <span>${lastReceipt.tax_amount.toFixed(2)}</span>
                </div>
                {lastReceipt.points_discount > 0 && (
                  <div className="flex justify-between text-yellow-600">
                    <span>Credit Applied</span>
                    <span>-${lastReceipt.points_discount.toFixed(2)}</span>
                  </div>
                )}
                {lastReceipt.gift_card_discount > 0 && (
                  <div className="flex justify-between text-purple-600">
                    <span>Gift Card ({lastReceipt.gift_card_code})</span>
                    <span>-${lastReceipt.gift_card_discount.toFixed(2)}</span>
                  </div>
                )}
                {lastReceipt.store_credit_used > 0 && lastReceipt.payment_method !== 'store_credit' && (
                  <div className="flex justify-between text-amber-600">
                    <span>Store Credit</span>
                    <span>-${lastReceipt.store_credit_used.toFixed(2)}</span>
                  </div>
                )}
                {lastReceipt.manual_discount > 0 && (
                  <div className="flex justify-between text-rose-600">
                    <span>Discount{lastReceipt.manual_discount_type === 'percent' ? ` (${lastReceipt.manual_discount_value}%)` : ''}</span>
                    <span>-${lastReceipt.manual_discount.toFixed(2)}</span>
                  </div>
                )}
                <Separator className="my-2" />
                <div className="flex justify-between font-bold text-lg">
                  <span>Total (Inc GST)</span>
                  <span>${lastReceipt.total_amount.toFixed(2)}</span>
                </div>
              </div>

              {lastReceipt.payment_method === 'cash' && (
                <>
                  <Separator />
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span>Cash Received</span>
                      <span>${lastReceipt.cash_received?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between font-bold text-emerald-600">
                      <span>Change</span>
                      <span>${lastReceipt.change_given?.toFixed(2)}</span>
                    </div>
                  </div>
                </>
              )}

              {lastReceipt.points_earned > 0 && (
                <>
                  <Separator />
                  <div className="text-center p-2 bg-yellow-50 rounded-sm">
                    <p className="text-sm text-yellow-700">
                      <Star className="w-4 h-4 inline mr-1" />
                      Earned ${lastReceipt.points_earned} loyalty credit!
                    </p>
                  </div>
                </>
              )}

              <div className="text-center pt-2">
                {lastReceipt.split_payment ? (
                  <div className="text-sm text-slate-500 space-y-1">
                    <p className="font-medium">Payment: SPLIT</p>
                    <p>Cash: ${lastReceipt.cash_amount?.toFixed(2)}</p>
                    <p>Card: ${lastReceipt.card_amount?.toFixed(2)}</p>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">
                    Payment: {lastReceipt.payment_method?.toUpperCase()}
                  </p>
                )}
                <p className="text-xs text-slate-400 mt-2">Thank you for your purchase!</p>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    const store = getCurrentStore();
                    const reprintData = {
                      id: lastReceipt.id,
                      created_at: lastReceipt.created_at,
                      store_name: store?.name || '',
                      store_phone: store?.phone || '',
                      gst_number: store?.gst_number || '',
                      customer_name: lastReceipt.customer_name,
                      items: lastReceipt.items,
                      subtotal: lastReceipt.subtotal,
                      tax_amount: lastReceipt.tax_amount,
                      total_amount: lastReceipt.total_amount,
                      payment_method: lastReceipt.payment_method,
                      cash_received: lastReceipt.cash_received,
                      change_given: lastReceipt.change_given,
                      points_discount: lastReceipt.points_discount || 0,
                      gift_card_discount: lastReceipt.gift_card_discount || 0,
                      gift_card_code: lastReceipt.gift_card_code || '',
                      split_payment: lastReceipt.split_payment,
                      cash_amount: lastReceipt.cash_amount,
                      card_amount: lastReceipt.card_amount,
                      points_earned: lastReceipt.points_earned || 0,
                      points_balance: lastReceipt.points_balance || 0,
                      isReprint: true,
                    };
                    qzPrintReceipt(reprintData);
                  }}
                  className="flex-1"
                  data-testid="reprint-receipt"
                >
                  <Printer className="w-4 h-4 mr-2" /> Reprint
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setEmailReceiptTo(lastReceipt.customer_email || '');
                    setShowEmailReceiptInput(v => !v);
                  }}
                  className="flex-1 border-sky-200 text-sky-700 hover:bg-sky-50"
                  data-testid="email-receipt-btn"
                >
                  <Mail className="w-4 h-4 mr-2" /> Email
                </Button>
                <Button
                  onClick={() => setShowReceiptModal(false)}
                  className="flex-1 bg-slate-900 hover:bg-slate-800"
                  data-testid="close-receipt"
                >
                  <Receipt className="w-4 h-4 mr-2" /> Done
                </Button>
              </div>

              {showEmailReceiptInput && (
                <div className="flex gap-2 mt-3" data-testid="email-receipt-row">
                  <Input
                    type="email"
                    placeholder="customer@email.com"
                    value={emailReceiptTo}
                    onChange={(e) => setEmailReceiptTo(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleEmailReceipt()}
                    autoFocus
                    data-testid="email-receipt-input"
                  />
                  <Button
                    onClick={handleEmailReceipt}
                    disabled={sendingEmailReceipt || !emailReceiptTo.trim()}
                    className="bg-sky-600 hover:bg-sky-700 shrink-0"
                    data-testid="email-receipt-send"
                  >
                    {sendingEmailReceipt ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />}
                    Send
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      )}

      {/* Gift Card Modal */}
      <Dialog open={showGiftCardModal} onOpenChange={setShowGiftCardModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase flex items-center gap-2">
              <Gift className="w-5 h-5 text-purple-500" /> Gift Cards
            </DialogTitle>
            <DialogDescription>
              Sell new gift cards or redeem existing ones
            </DialogDescription>
          </DialogHeader>

          <Tabs value={giftCardTab} onValueChange={setGiftCardTab} className="mt-4">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="sell" data-testid="gc-sell-tab">Sell Gift Card</TabsTrigger>
              <TabsTrigger value="redeem" data-testid="gc-redeem-tab">Redeem</TabsTrigger>
            </TabsList>
            
            <TabsContent value="sell" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Gift Card Amount</Label>
                <div className="grid grid-cols-4 gap-2">
                  {[25, 50, 100, 200].map(amt => (
                    <Button 
                      key={amt} 
                      variant="outline"
                      onClick={() => setGiftCardAmount(amt.toString())}
                      className={giftCardAmount === amt.toString() ? 'border-purple-500 bg-purple-50' : ''}
                    >
                      ${amt}
                    </Button>
                  ))}
                </div>
                <Input
                  type="number"
                  placeholder="Custom amount"
                  value={giftCardAmount}
                  onChange={(e) => setGiftCardAmount(e.target.value)}
                  className="mt-2"
                  data-testid="gc-amount"
                />
              </div>
              <div className="space-y-2">
                <Label>Recipient Name (Optional)</Label>
                <Input
                  placeholder="For: John Doe"
                  value={giftCardRecipient}
                  onChange={(e) => setGiftCardRecipient(e.target.value)}
                  data-testid="gc-recipient"
                />
              </div>
              <Button 
                onClick={handleSellGiftCard} 
                disabled={giftCardLoading || !giftCardAmount}
                className="w-full bg-purple-600 hover:bg-purple-700"
                data-testid="gc-create"
              >
                {giftCardLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Gift className="w-4 h-4 mr-2" />}
                Create & Add to Cart
              </Button>
            </TabsContent>
            
            <TabsContent value="redeem" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Gift Card Code</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="GC-XXXXXXXXXXXX"
                    value={giftCardCode}
                    onChange={(e) => setGiftCardCode(e.target.value.toUpperCase())}
                    data-testid="gc-code"
                  />
                  <Button onClick={handleLookupGiftCard} disabled={giftCardLoading}>
                    {giftCardLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              
              {giftCardLookup && (
                <div className="p-4 bg-purple-50 rounded-sm space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Balance</span>
                    <span className="text-2xl font-bold text-purple-700">${giftCardLookup.balance.toFixed(2)}</span>
                  </div>
                  <p className="text-sm text-slate-500">For: {giftCardLookup.customer_name}</p>
                  
                  <div className="space-y-2">
                    <Label>Amount to Redeem</Label>
                    <Input
                      type="number"
                      max={giftCardLookup.balance}
                      value={giftCardRedeemAmount}
                      onChange={(e) => setGiftCardRedeemAmount(e.target.value)}
                      data-testid="gc-redeem-amount"
                    />
                  </div>
                  
                  <Button 
                    onClick={handleApplyGiftCard}
                    disabled={!giftCardRedeemAmount || parseFloat(giftCardRedeemAmount) <= 0}
                    className="w-full bg-purple-600 hover:bg-purple-700"
                    data-testid="gc-apply"
                  >
                    Apply to Sale
                  </Button>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Refund Modal */}
      <Dialog open={showRefundModal} onOpenChange={(open) => { setShowRefundModal(open); if (!open) resetRefundState(); }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-amber-500" /> Process Refund
            </DialogTitle>
            <DialogDescription>
              {refundMode === 'menu' && 'Choose how to process the refund'}
              {refundMode === 'phone' && 'Search customer purchase history by phone number'}
              {refundMode === 'plain' && 'Quick refund without linking to a sale'}
              {refundMode === 'sale' && 'Select items to refund from this sale'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            {/* MENU - Choose refund type */}
            {refundMode === 'menu' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <button
                  className="p-6 border-2 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all text-left"
                  onClick={() => setRefundMode('phone')}
                  data-testid="refund-phone-lookup"
                >
                  <Phone className="w-8 h-8 text-blue-500 mb-3" />
                  <h3 className="font-bold text-lg">Customer Lookup</h3>
                  <p className="text-sm text-slate-500">Search by phone number to find the original purchase</p>
                </button>
                <button
                  className="p-6 border-2 rounded-lg hover:border-amber-400 hover:bg-amber-50 transition-all text-left"
                  onClick={() => setRefundMode('plain')}
                  data-testid="refund-plain"
                >
                  <DollarSign className="w-8 h-8 text-amber-500 mb-3" />
                  <h3 className="font-bold text-lg">Plain Refund</h3>
                  <p className="text-sm text-slate-500">Quick refund without looking up a sale</p>
                </button>
              </div>
            )}

            {/* PHONE SEARCH */}
            {refundMode === 'phone' && !refundSale && (
              <div className="space-y-4">
                <Button variant="ghost" size="sm" onClick={() => { setRefundMode('menu'); setPhoneSales([]); setPhoneSearch(''); }}>
                  <ArrowLeft className="w-4 h-4 mr-1" /> Back
                </Button>
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter customer phone number"
                    value={phoneSearch}
                    onChange={(e) => setPhoneSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handlePhoneSearch()}
                    data-testid="refund-phone-input"
                  />
                  <Button onClick={handlePhoneSearch} disabled={phoneSearching}>
                    {phoneSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  </Button>
                </div>

                {phoneSales.length > 0 && (
                  <div className="space-y-2 max-h-80 overflow-auto">
                    <p className="text-sm text-slate-500 font-medium">{phoneSales.length} sales found</p>
                    {phoneSales.map((sale) => (
                      <button
                        key={sale.id}
                        className="w-full p-3 border rounded-lg hover:bg-blue-50 hover:border-blue-300 text-left transition-all"
                        onClick={() => selectSaleForRefund(sale)}
                        data-testid={`phone-sale-${sale.id}`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium">{formatDate(sale.created_at)} {new Date(sale.created_at).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}</p>
                            <p className="text-xs text-slate-500">
                              {sale.items?.length || 0} items: {sale.items?.map(i => i.product_name).join(', ').substring(0, 60)}...
                            </p>
                          </div>
                          <span className="font-bold text-lg">${parseFloat(sale.total_amount || 0).toFixed(2)}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* PLAIN REFUND */}
            {refundMode === 'plain' && (
              <div className="space-y-4">
                <Button variant="ghost" size="sm" onClick={() => setRefundMode('menu')}>
                  <ArrowLeft className="w-4 h-4 mr-1" /> Back
                </Button>

                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800" data-testid="plain-refund-scan-hint">
                  💡 Scan a product barcode now to add it to this refund — its price fills in automatically and stock is restored.
                </div>

                {plainRefundItems.length > 0 && (
                  <div className="space-y-1" data-testid="plain-refund-items">
                    {plainRefundItems.map((it, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 border rounded-lg bg-slate-50 text-sm">
                        <span className="font-medium truncate mr-2">{it.product_name}</span>
                        <div className="flex items-center gap-3 shrink-0">
                          <span className="font-bold">${(it.unit_price || 0).toFixed(2)}</span>
                          <button
                            className="text-rose-500 hover:text-rose-700 font-bold"
                            onClick={() => {
                              setPlainRefundItems(prev => prev.filter((_, i) => i !== idx));
                              setPlainRefundAmount(prev => Math.max(0, (parseFloat(prev) || 0) - (it.unit_price || 0)).toFixed(2));
                            }}
                            data-testid={`plain-refund-item-remove-${idx}`}
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label>Refund Amount</Label>
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={plainRefundAmount}
                    onChange={(e) => setPlainRefundAmount(e.target.value)}
                    className="text-2xl h-14 font-bold"
                    data-testid="plain-refund-amount"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Reason (Optional)</Label>
                  <Input
                    placeholder="Reason for refund"
                    value={refundReason}
                    onChange={(e) => setRefundReason(e.target.value)}
                    data-testid="plain-refund-reason"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Refund Method</Label>
                  <div className="grid grid-cols-3 gap-3">
                    <button
                      className={`p-4 border-2 rounded-lg flex flex-col items-center gap-1 transition-all ${refundMethod === 'cash' ? 'border-green-500 bg-green-50' : 'hover:bg-slate-50'}`}
                      onClick={() => setRefundMethod('cash')}
                      data-testid="plain-refund-cash"
                    >
                      <Banknote className="w-6 h-6 text-green-600" />
                      <span className="font-bold">Cash</span>
                    </button>
                    <button
                      className={`p-4 border-2 rounded-lg flex flex-col items-center gap-1 transition-all ${refundMethod === 'card' ? 'border-blue-500 bg-blue-50' : 'hover:bg-slate-50'}`}
                      onClick={() => setRefundMethod('card')}
                      data-testid="plain-refund-card"
                    >
                      <CreditCard className="w-6 h-6 text-blue-600" />
                      <span className="font-bold">Card</span>
                    </button>
                    <button
                      className={`p-4 border-2 rounded-lg flex flex-col items-center gap-1 transition-all ${refundMethod === 'store_credit' ? 'border-amber-500 bg-amber-50' : 'hover:bg-slate-50'}`}
                      onClick={() => setRefundMethod('store_credit')}
                      data-testid="plain-refund-store-credit"
                    >
                      <Wallet className="w-6 h-6 text-amber-600" />
                      <span className="font-bold text-sm">Store Credit</span>
                    </button>
                  </div>
                </div>

                {refundMethod === 'store_credit' && renderRefundCreditPicker()}

                <Button
                  onClick={handlePlainRefund}
                  disabled={refundLoading || !plainRefundAmount || parseFloat(plainRefundAmount) <= 0 || (refundMethod === 'store_credit' && !refundCreditCustomer)}
                  className="w-full h-12 bg-amber-500 hover:bg-amber-600"
                  data-testid="process-plain-refund"
                >
                  {refundLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RotateCcw className="w-4 h-4 mr-2" />}
                  Process {refundMethod === 'cash' ? 'Cash' : refundMethod === 'card' ? 'Card' : 'Store Credit'} Refund {plainRefundAmount ? `$${parseFloat(plainRefundAmount).toFixed(2)}` : ''}
                </Button>
              </div>
            )}

            {/* SALE REFUND - Items selection */}
            {refundMode === 'sale' && refundSale && (
              <div className="space-y-4">
                <Button variant="ghost" size="sm" onClick={() => { setRefundSale(null); setRefundItems([]); setRefundMode('phone'); }}>
                  <ArrowLeft className="w-4 h-4 mr-1" /> Back to results
                </Button>

                <div className="p-3 bg-slate-50 rounded-sm text-sm">
                  <p><strong>Date:</strong> {formatDateTime(refundSale.created_at)}</p>
                  <p><strong>Original Total:</strong> ${parseFloat(refundSale.total_amount || 0).toFixed(2)}</p>
                  {refundSale.customer_phone && <p><strong>Phone:</strong> {refundSale.customer_phone}</p>}
                </div>
                
                <div className="space-y-2">
                  <Label>Select Items to Refund</Label>
                  {refundItems.map((item, idx) => (
                    <div 
                      key={idx}
                      className={`p-3 border rounded-sm cursor-pointer transition-colors ${
                        item.refund ? 'border-amber-500 bg-amber-50' : 'hover:bg-slate-50'
                      }`}
                      onClick={() => toggleRefundItem(idx)}
                      data-testid={`refund-item-${idx}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                            item.refund ? 'border-amber-500 bg-amber-500' : 'border-slate-300'
                          }`}>
                            {item.refund && <Check className="w-3 h-3 text-white" />}
                          </div>
                          <div>
                            <p className="font-medium">{item.product_name}</p>
                            <p className="text-sm text-slate-500">${item.unit_price.toFixed(2)} each × {item.quantity}</p>
                          </div>
                        </div>
                        {item.refund && (
                          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                            <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => updateRefundQty(idx, item.refund_qty - 1)}>
                              <Minus className="w-3 h-3" />
                            </Button>
                            <span className="w-8 text-center">{item.refund_qty}</span>
                            <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => updateRefundQty(idx, item.refund_qty + 1)}>
                              <Plus className="w-3 h-3" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="space-y-2">
                  <Label>Reason (Optional)</Label>
                  <Input
                    placeholder="Reason for refund"
                    value={refundReason}
                    onChange={(e) => setRefundReason(e.target.value)}
                    data-testid="refund-reason"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Refund Method</Label>
                  <div className="grid grid-cols-3 gap-3">
                    <button
                      className={`p-3 border-2 rounded-lg flex flex-col items-center gap-1 transition-all ${refundMethod === 'cash' ? 'border-green-500 bg-green-50' : 'hover:bg-slate-50'}`}
                      onClick={() => setRefundMethod('cash')}
                      data-testid="sale-refund-cash"
                    >
                      <Banknote className="w-5 h-5 text-green-600" />
                      <span className="font-bold">Cash</span>
                    </button>
                    <button
                      className={`p-3 border-2 rounded-lg flex flex-col items-center gap-1 transition-all ${refundMethod === 'card' ? 'border-blue-500 bg-blue-50' : 'hover:bg-slate-50'}`}
                      onClick={() => setRefundMethod('card')}
                      data-testid="sale-refund-card"
                    >
                      <CreditCard className="w-5 h-5 text-blue-600" />
                      <span className="font-bold">Card</span>
                    </button>
                    <button
                      className={`p-3 border-2 rounded-lg flex flex-col items-center gap-1 transition-all ${refundMethod === 'store_credit' ? 'border-amber-500 bg-amber-50' : 'hover:bg-slate-50'}`}
                      onClick={() => setRefundMethod('store_credit')}
                      data-testid="sale-refund-store-credit"
                    >
                      <Wallet className="w-5 h-5 text-amber-600" />
                      <span className="font-bold text-sm">Store Credit</span>
                    </button>
                  </div>
                </div>

                {refundMethod === 'store_credit' && renderRefundCreditPicker()}
                
                <div className="p-4 bg-amber-50 rounded-sm">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-amber-800">Refund Amount ({refundMethod === 'store_credit' ? 'store credit' : refundMethod})</span>
                    <span className="text-2xl font-bold text-amber-700" data-testid="refund-total">
                      ${calculateRefundTotal().toFixed(2)}
                    </span>
                  </div>
                </div>
                
                <Button 
                  onClick={handleProcessRefund}
                  disabled={refundLoading || calculateRefundTotal() <= 0 || (refundMethod === 'store_credit' && !refundCreditCustomer)}
                  className="w-full h-12 bg-amber-500 hover:bg-amber-600"
                  data-testid="process-refund"
                >
                  {refundLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RotateCcw className="w-4 h-4 mr-2" />}
                  Process {refundMethod === 'cash' ? 'Cash' : refundMethod === 'card' ? 'Card' : 'Store Credit'} Refund
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Loyalty Settings Modal */}
      <Dialog open={showLoyaltySettingsModal} onOpenChange={setShowLoyaltySettingsModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase flex items-center gap-2">
              <Settings className="w-5 h-5 text-yellow-500" /> Loyalty Settings
            </DialogTitle>
            <DialogDescription>
              Configure your loyalty program settings
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Star className="w-4 h-4 text-yellow-500" /> Loyalty Credit ($ back per $1 spent)
              </Label>
              <Input
                type="number"
                step="0.1"
                min="0"
                value={loyaltySettings.points_per_dollar}
                onChange={(e) => setLoyaltySettings({...loyaltySettings, points_per_dollar: parseFloat(e.target.value) || 0})}
                data-testid="points-per-dollar"
              />
              <p className="text-xs text-slate-500">How many points customers earn per dollar spent</p>
            </div>
            
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-emerald-500" /> Point Redemption Value ($)
              </Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={loyaltySettings.redemption_value}
                onChange={(e) => setLoyaltySettings({...loyaltySettings, redemption_value: parseFloat(e.target.value) || 0})}
                data-testid="redemption-value"
              />
              <p className="text-xs text-slate-500">Dollar value of each point when redeeming (e.g., 0.01 = 1 cent per point)</p>
            </div>
            
            <div className="space-y-2">
              <Label>Minimum Credit to Redeem</Label>
              <Input
                type="number"
                min="0"
                value={loyaltySettings.min_redemption_points}
                onChange={(e) => setLoyaltySettings({...loyaltySettings, min_redemption_points: parseInt(e.target.value) || 0})}
                data-testid="min-points"
              />
              <p className="text-xs text-slate-500">Minimum points required before redemption is allowed</p>
            </div>
            
            <div className="p-4 bg-yellow-50 rounded-sm">
              <h4 className="font-medium text-yellow-800 mb-2">Preview</h4>
              <div className="text-sm text-yellow-700 space-y-1">
                <p>$100 purchase → {Math.floor(100 * loyaltySettings.points_per_dollar)} points earned</p>
                <p>100 points → ${(100 * loyaltySettings.redemption_value).toFixed(2)} discount</p>
              </div>
            </div>
            
            <Button 
              onClick={handleSaveLoyaltySettings}
              disabled={loyaltyLoading}
              className="w-full bg-yellow-500 hover:bg-yellow-600"
              data-testid="save-loyalty"
            >
              {loyaltyLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              Save Settings
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Misc Modal */}
      <Dialog open={showMiscModal} onOpenChange={setShowMiscModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase flex items-center gap-2">
              <MoreHorizontal className="w-5 h-5 text-slate-500" /> Miscellaneous
            </DialogTitle>
            <DialogDescription>
              Add unlisted items to cart, record misc sales, or cash movements
            </DialogDescription>
          </DialogHeader>

          <Tabs value={miscTab} onValueChange={setMiscTab} className="mt-4">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="item" data-testid="misc-item-tab">Add Item</TabsTrigger>
              <TabsTrigger value="sale" data-testid="misc-sale-tab">Quick Sale</TabsTrigger>
              <TabsTrigger value="cash" data-testid="misc-cash-tab">Cash In/Out</TabsTrigger>
            </TabsList>
            
            {/* Add Misc Item to Cart Tab */}
            <TabsContent value="item" className="space-y-4 mt-4">
              <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
                <strong>Tip:</strong> Use this to sell items not in the system. The item will be added to your cart.
              </div>
              <div className="space-y-2">
                <Label>Product Description *</Label>
                <Input
                  placeholder="e.g., Custom supplement mix, Special order item"
                  value={miscSaleDesc}
                  onChange={(e) => setMiscSaleDesc(e.target.value)}
                  data-testid="misc-item-desc"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Price ($) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={miscSaleAmount}
                    onChange={(e) => setMiscSaleAmount(e.target.value)}
                    data-testid="misc-item-price"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Quantity</Label>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={() => setMiscItemQty(Math.max(1, miscItemQty - 1))}
                    >
                      <Minus className="w-4 h-4" />
                    </Button>
                    <Input
                      type="number"
                      min="1"
                      value={miscItemQty}
                      onChange={(e) => setMiscItemQty(parseInt(e.target.value) || 1)}
                      className="w-16 text-center"
                    />
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={() => setMiscItemQty(miscItemQty + 1)}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
              {miscSaleAmount && (
                <div className="p-3 bg-slate-100 rounded-lg flex justify-between items-center">
                  <span className="text-slate-600">Total:</span>
                  <span className="text-xl font-bold">
                    ${((parseFloat(miscSaleAmount) || 0) * miscItemQty).toFixed(2)}
                  </span>
                </div>
              )}
              <Button 
                onClick={handleAddMiscItemToCart}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                data-testid="misc-item-add"
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                Add to Cart
              </Button>
            </TabsContent>
            
            {/* Quick Sale Tab (Direct sale without adding to cart) */}
            <TabsContent value="sale" className="space-y-4 mt-4">
              <div className="p-3 bg-yellow-50 rounded-lg text-sm text-yellow-800">
                <strong>Note:</strong> This records a direct sale without going through payment. Use "Add Item" tab to add to cart instead.
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  placeholder="e.g., Delivery fee, Service charge"
                  value={miscSaleDesc}
                  onChange={(e) => setMiscSaleDesc(e.target.value)}
                  data-testid="misc-sale-desc"
                />
              </div>
              <div className="space-y-2">
                <Label>Amount ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={miscSaleAmount}
                  onChange={(e) => setMiscSaleAmount(e.target.value)}
                  data-testid="misc-sale-amount"
                />
              </div>
              <Button 
                onClick={handleMiscSale}
                disabled={miscLoading}
                className="w-full bg-slate-700 hover:bg-slate-800"
                data-testid="misc-sale-submit"
              >
                {miscLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <DollarSign className="w-4 h-4 mr-2" />}
                Record Quick Sale
              </Button>
            </TabsContent>
            
            <TabsContent value="cash" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant={cashMovementType === 'in' ? 'default' : 'outline'}
                    onClick={() => setCashMovementType('in')}
                    className={cashMovementType === 'in' ? 'bg-emerald-600' : ''}
                  >
                    <ArrowDownCircle className="w-4 h-4 mr-2" /> Cash In
                  </Button>
                  <Button
                    variant={cashMovementType === 'out' ? 'default' : 'outline'}
                    onClick={() => setCashMovementType('out')}
                    className={cashMovementType === 'out' ? 'bg-red-600' : ''}
                  >
                    <ArrowUpCircle className="w-4 h-4 mr-2" /> Cash Out
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Amount ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={cashMovementAmount}
                  onChange={(e) => setCashMovementAmount(e.target.value)}
                  data-testid="cash-amount"
                />
              </div>
              <div className="space-y-2">
                <Label>Reason</Label>
                <Input
                  placeholder="e.g., Float, Bank deposit, Petty cash"
                  value={cashMovementReason}
                  onChange={(e) => setCashMovementReason(e.target.value)}
                  data-testid="cash-reason"
                />
              </div>
              <Button 
                onClick={handleCashMovement}
                disabled={miscLoading}
                className={`w-full ${cashMovementType === 'in' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'}`}
                data-testid="cash-submit"
              >
                {miscLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : (cashMovementType === 'in' ? <ArrowDownCircle className="w-4 h-4 mr-2" /> : <ArrowUpCircle className="w-4 h-4 mr-2" />)}
                Record Cash {cashMovementType === 'in' ? 'In' : 'Out'}
              </Button>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Stock Report Modal */}
      <Dialog open={showStockReportModal} onOpenChange={setShowStockReportModal}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" /> Stock Issues Report
            </DialogTitle>
            <DialogDescription>
              Products with negative, zero, or low stock levels
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Threshold Setting */}
            <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-sm">
              <Label className="whitespace-nowrap">Low Stock Threshold:</Label>
              <Input
                type="number"
                min="1"
                className="w-24"
                value={lowStockThreshold}
                onChange={(e) => setLowStockThreshold(parseInt(e.target.value) || 10)}
                data-testid="low-stock-threshold"
              />
              <Button variant="outline" size="sm" onClick={fetchStockReport}>
                {stockReportLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Refresh'}
              </Button>
            </div>
            
            {stockReportLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : stockReport ? (
              <div className="space-y-4">
                {/* Summary */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-3 bg-red-50 rounded-sm text-center">
                    <p className="text-2xl font-bold text-red-600">{stockReport.summary.negative_count}</p>
                    <p className="text-xs text-red-700">Negative Stock</p>
                  </div>
                  <div className="p-3 bg-amber-50 rounded-sm text-center">
                    <p className="text-2xl font-bold text-amber-600">{stockReport.summary.zero_count}</p>
                    <p className="text-xs text-amber-700">Zero Stock</p>
                  </div>
                  <div className="p-3 bg-yellow-50 rounded-sm text-center">
                    <p className="text-2xl font-bold text-yellow-600">{stockReport.summary.low_count}</p>
                    <p className="text-xs text-yellow-700">Low Stock (&lt;{lowStockThreshold})</p>
                  </div>
                </div>
                
                {/* Negative Stock */}
                {stockReport.negative_stock.length > 0 && (
                  <div>
                    <h4 className="font-medium text-red-700 mb-2 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" /> Negative Stock ({stockReport.negative_stock.length})
                    </h4>
                    <div className="border border-red-200 rounded-sm overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-red-50">
                          <tr>
                            <th className="text-left p-2">Product</th>
                            <th className="text-center p-2">{storeName('orewa')}</th>
                            <th className="text-center p-2">{storeName('eastridge')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {stockReport.negative_stock.map(item => (
                            <tr key={item.id} className="border-t border-red-100">
                              <td className="p-2">{item.name}</td>
                              <td className={`text-center p-2 font-bold ${item.stock_orewa < 0 ? 'text-red-600' : ''}`}>
                                {item.stock_orewa}
                              </td>
                              <td className={`text-center p-2 font-bold ${item.stock_eastridge < 0 ? 'text-red-600' : ''}`}>
                                {item.stock_eastridge}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                
                {/* Zero Stock */}
                {stockReport.zero_stock.length > 0 && (
                  <div>
                    <h4 className="font-medium text-amber-700 mb-2 flex items-center gap-2">
                      <Package className="w-4 h-4" /> Out of Stock ({stockReport.zero_stock.length})
                    </h4>
                    <div className="border border-amber-200 rounded-sm overflow-hidden max-h-40 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-amber-50 sticky top-0">
                          <tr>
                            <th className="text-left p-2">Product</th>
                            <th className="text-right p-2">Retail</th>
                          </tr>
                        </thead>
                        <tbody>
                          {stockReport.zero_stock.map(item => (
                            <tr key={item.id} className="border-t border-amber-100">
                              <td className="p-2">{item.name}</td>
                              <td className="text-right p-2">${item.sell_price?.toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                
                {/* Low Stock */}
                {stockReport.low_stock.length > 0 && (
                  <div>
                    <h4 className="font-medium text-yellow-700 mb-2 flex items-center gap-2">
                      <FileText className="w-4 h-4" /> Low Stock ({stockReport.low_stock.length})
                    </h4>
                    <div className="border border-yellow-200 rounded-sm overflow-hidden max-h-40 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-yellow-50 sticky top-0">
                          <tr>
                            <th className="text-left p-2">Product</th>
                            <th className="text-center p-2">{storeName('orewa')}</th>
                            <th className="text-center p-2">{storeName('eastridge')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {stockReport.low_stock.map(item => (
                            <tr key={item.id} className="border-t border-yellow-100">
                              <td className="p-2">{item.name}</td>
                              <td className={`text-center p-2 ${item.stock_orewa < lowStockThreshold ? 'text-yellow-600 font-bold' : ''}`}>
                                {item.stock_orewa}
                              </td>
                              <td className={`text-center p-2 ${item.stock_eastridge < lowStockThreshold ? 'text-yellow-600 font-bold' : ''}`}>
                                {item.stock_eastridge}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                
                {stockReport.negative_stock.length === 0 && stockReport.zero_stock.length === 0 && stockReport.low_stock.length === 0 && (
                  <div className="text-center py-8 text-emerald-600">
                    <Check className="w-12 h-12 mx-auto mb-2" />
                    <p className="font-medium">All stock levels are healthy!</p>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>

      {/* Margin Warning Dialog - Allows price fix or proceed */}
      <Dialog open={showMarginWarning} onOpenChange={setShowMarginWarning}>
        <DialogContent className="sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-bold uppercase flex items-center gap-2 text-yellow-600">
              <AlertTriangle className="w-7 h-7" />
              Low Margin Warning
            </DialogTitle>
            <DialogDescription className="text-base">
              The following items are below {MIN_MARGIN_PERCENT}% margin (calculated on ex-GST prices). Fix prices or proceed.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="border border-yellow-200 rounded-lg overflow-hidden">
              <table className="w-full text-base">
                <thead className="bg-yellow-50">
                  <tr>
                    <th className="text-left p-3 font-bold text-base">Product</th>
                    <th className="text-right p-3 font-bold text-base">Buy (ex)</th>
                    <th className="text-right p-3 font-bold text-base">Sell (inc)</th>
                    <th className="text-right p-3 font-bold text-base">Min Price</th>
                    <th className="text-center p-3 font-bold text-base">New Price</th>
                    <th className="text-center p-3 font-bold text-base">Margin</th>
                  </tr>
                </thead>
                <tbody>
                  {lowMarginItems.map((item, idx) => {
                    const minPrice = getMinSellPrice(item.buy_price);
                    const editPrice = editingPrices[item.product_id] || item.sell_price;
                    const editPriceExGst = editPrice / 1.15; // Convert to ex-GST for margin calc
                    const newMargin = ((editPriceExGst - item.buy_price) / editPriceExGst) * 100;
                    const isFixed = newMargin >= MIN_MARGIN_PERCENT;
                    
                    return (
                      <tr key={idx} className="border-t border-yellow-100">
                        <td className="p-3 font-semibold text-base text-slate-900 whitespace-nowrap" data-testid={`low-margin-name-${idx}`}>{item.name}</td>
                        <td className="p-3 text-right text-slate-700 font-medium">${item.buy_price.toFixed(2)}</td>
                        <td className="p-3 text-right font-medium">
                          ${item.sell_price.toFixed(2)}
                          <span className="text-xs text-slate-400 block">(ex: ${(item.sell_price/1.15).toFixed(2)})</span>
                        </td>
                        <td className="p-3 text-right text-emerald-600 font-bold">${minPrice.toFixed(2)}</td>
                        <td className="p-3">
                          <div className="flex items-center gap-1 justify-center">
                            <span className="text-slate-500 font-semibold">$</span>
                            <Input
                              type="number"
                              step="0.01"
                              min={minPrice}
                              value={editingPrices[item.product_id] || ''}
                              onChange={(e) => setEditingPrices({
                                ...editingPrices,
                                [item.product_id]: e.target.value
                              })}
                              placeholder={minPrice.toFixed(2)}
                              className="w-28 h-10 text-right text-base font-semibold"
                            />
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-10 px-3"
                              disabled={updatingPrice || !editingPrices[item.product_id]}
                              onClick={() => handleUpdatePrice(item.product_id, editingPrices[item.product_id])}
                            >
                              {updatingPrice ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                            </Button>
                          </div>
                        </td>
                        <td className="p-3 text-center">
                          <Badge className={`text-base px-3 py-1 font-bold ${isFixed ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                            {newMargin.toFixed(1)}%
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            {lowMarginItems.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-base text-blue-800">
                  <strong>Tip:</strong> The input already shows the suggested price for {MIN_MARGIN_PERCENT}% margin (rounded up to the nearest 5¢). Just click ✓ to save — or type a different price first.
                </p>
              </div>
            )}
          </div>
          
          <div className="flex justify-between gap-3 mt-4">
            <Button variant="outline" size="lg" onClick={() => setShowMarginWarning(false)}>
              Cancel
            </Button>
            <div className="flex gap-2">
              <Button 
                size="lg"
                onClick={proceedWithLowMargin}
                className="bg-yellow-500 hover:bg-yellow-600"
              >
                Proceed Anyway
              </Button>
              {lowMarginItems.length === 0 && (
                <Button 
                  onClick={() => { setShowMarginWarning(false); setShowPaymentModal(true); }}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  <Check className="w-4 h-4 mr-1" /> Continue to Payment
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Short-Dated Stock Alert Dialog */}
      <Dialog open={showShortDatedAlert} onOpenChange={setShowShortDatedAlert}>
        <DialogContent className="sm:max-w-md" onOpenAutoFocus={(e) => e.preventDefault()} data-testid="short-dated-alert">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase flex items-center gap-2 text-yellow-600">
              <Star className="w-6 h-6" />
              Short-Dated Stock Available!
            </DialogTitle>
            <DialogDescription>
              This product has short-dated stock available at a discount. Check the label on the item.
            </DialogDescription>
          </DialogHeader>
          
          {shortDatedProduct && (
            <div className="space-y-4">
              <p className="font-bold text-lg">{shortDatedProduct.product?.name}</p>
              {(shortDatedProduct.batches || [shortDatedProduct.shortDated]).map((b) => (
                <Card key={b.id} className="bg-yellow-50 border-yellow-200" data-testid={`sd-batch-${b.id}`}>
                  <CardContent className="p-4 flex items-center justify-between gap-3">
                    <div className="space-y-1 text-sm">
                      <p>Expiry: <span className="font-medium text-red-600">{formatDate(b.expiry_date)}</span></p>
                      <p>Available: <Badge variant="outline">{b.quantity} units</Badge></p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="line-through text-slate-400">${b.original_price?.toFixed(2)}</span>
                        <span className="text-2xl font-bold text-emerald-600">${b.discounted_price?.toFixed(2)}</span>
                        <Badge className="bg-yellow-500">{b.discount_percent}% OFF</Badge>
                      </div>
                    </div>
                    <Button 
                      className="bg-yellow-500 hover:bg-yellow-600"
                      onClick={() => {
                        setShowShortDatedAlert(false);
                        addToCart(shortDatedProduct.product, true, b);
                      }}
                      data-testid={`sd-add-batch-${b.id}`}
                    >
                      <Star className="w-4 h-4 mr-2" /> Add at {b.discount_percent}% OFF
                    </Button>
                  </CardContent>
                </Card>
              ))}
              {(shortDatedProduct.batches || []).length > 1 && (
                <p className="text-xs text-slate-500">Match the % OFF and expiry printed on the product's label.</p>
              )}
              
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => {
                  setShowShortDatedAlert(false);
                  addToCart(shortDatedProduct.product, false, false);
                }}
              >
                Add Full Price
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Out-of-Stock Warning Dialog */}
      <Dialog open={showOOSWarning} onOpenChange={setShowOOSWarning}>
        <DialogContent className="sm:max-w-md" data-testid="oos-warning-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-6 h-6" />
              Out of Stock Warning!
            </DialogTitle>
            <DialogDescription>
              This product is currently out of stock. Do you want to proceed anyway?
            </DialogDescription>
          </DialogHeader>
          
          {oosProduct && (
            <div className="space-y-4">
              <Card className="bg-red-50 border-red-200">
                <CardContent className="p-4">
                  <p className="font-bold text-lg">{oosProduct.name}</p>
                  <div className="mt-2 space-y-1 text-sm">
                    <p>Current Stock: <Badge className="bg-red-100 text-red-700">0 at {getCurrentStore()?.name || selectedStore}</Badge></p>
                    <p>Price: <span className="font-bold text-lg">${oosProduct.sell_price?.toFixed(2)}</span></p>
                  </div>
                  <div className="mt-3 p-3 bg-amber-100 rounded-lg border border-amber-300">
                    <p className="text-sm text-amber-800 flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span><strong>Warning:</strong> Selling this item will create negative stock. Only proceed if you're sure about the sale (e.g., product is physically available but not scanned in).</span>
                    </p>
                  </div>
                </CardContent>
              </Card>
              
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => {
                    setShowOOSWarning(false);
                    setOosProduct(null);
                  }}
                  data-testid="oos-cancel-btn"
                >
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  onClick={addOOSToCart}
                  data-testid="oos-confirm-btn"
                >
                  <AlertTriangle className="w-4 h-4 mr-2" /> Sell Anyway
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* New Products Dialog - Shows once per day when staff logs in */}
      <Dialog open={showNewProductsDialog} onOpenChange={(open) => { if (!open) dismissNewProductsForToday(); }}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase flex items-center gap-2 text-blue-600">
              <Sparkles className="w-6 h-6" />
              New Products This Week!
            </DialogTitle>
            <DialogDescription>
              {newProducts.length} new product{newProducts.length !== 1 ? 's' : ''} added. Take a moment to learn about them.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {newProducts.map((product) => (
              <Card key={product.id} className="border-l-4 border-l-blue-500">
                <CardContent className="p-4">
                  <div className="flex gap-4">
                    {product.image_url ? (
                      <img 
                        src={product.image_url} 
                        alt={product.name}
                        className="w-20 h-20 object-cover rounded-lg"
                      />
                    ) : (
                      <div className="w-20 h-20 bg-slate-100 rounded-lg flex items-center justify-center">
                        <Package className="w-8 h-8 text-slate-400" />
                      </div>
                    )}
                    <div className="flex-1">
                      <h3 className="font-bold text-lg text-slate-900">{product.name}</h3>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2 text-sm">
                        <p className="text-slate-500">SKU: <span className="text-slate-700">{product.sku || '-'}</span></p>
                        <p className="text-slate-500">Barcode: <span className="font-mono text-slate-700">{product.barcode || '-'}</span></p>
                        <p className="text-slate-500">Category: <span className="text-slate-700">{product.category || '-'}</span></p>
                        <p className="text-slate-500">Brand: <span className="text-slate-700">{product.brand || '-'}</span></p>
                        <p className="text-slate-500">Supplier: <span className="text-slate-700">{product.supplier_name || '-'}</span></p>
                        <p className="text-emerald-600 font-bold">Price: ${product.sell_price?.toFixed(2)}</p>
                      </div>
                      {product.description && (
                        <p className="text-sm text-slate-600 mt-2 italic">"{product.description}"</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <DialogFooter className="mt-4">
            <Button 
              onClick={dismissNewProductsForToday}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Check className="w-4 h-4 mr-2" /> Got It!
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Edit Product Dialog */}
      <Dialog open={showEditProduct} onOpenChange={setShowEditProduct}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase flex items-center gap-2">
              <Edit className="w-5 h-5 text-blue-600" /> Edit Product
            </DialogTitle>
          </DialogHeader>
          {editProduct && (
            <div className="space-y-4 mt-2">
              {editProduct.shopify_product_id && (
                <div className="bg-green-50 border border-green-200 rounded p-2 text-sm text-green-700 flex items-center gap-2">
                  <Check className="w-4 h-4" /> Changes will sync to Shopify
                </div>
              )}
              <div>
                <label className="text-sm font-medium text-slate-700">Product Name</label>
                <Input value={editProductData.name || ''} onChange={(e) => setEditProductData({...editProductData, name: e.target.value})} data-testid="edit-product-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-slate-700">Sell Price ($)</label>
                  <Input type="number" step="0.01" value={editProductData.sell_price || ''} onChange={(e) => setEditProductData({...editProductData, sell_price: e.target.value})} data-testid="edit-product-sell-price" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Buy Price ($)</label>
                  <Input type="number" step="0.01" value={editProductData.buy_price || ''} onChange={(e) => setEditProductData({...editProductData, buy_price: e.target.value})} data-testid="edit-product-buy-price" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-slate-700">Stock {storeName('orewa')}</label>
                  <Input type="number" value={editProductData.stock_orewa || ''} onChange={(e) => setEditProductData({...editProductData, stock_orewa: e.target.value})} />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Stock {storeName('eastridge')}</label>
                  <Input type="number" value={editProductData.stock_eastridge || ''} onChange={(e) => setEditProductData({...editProductData, stock_eastridge: e.target.value})} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-slate-700">Barcode</label>
                  <Input value={editProductData.barcode || ''} onChange={(e) => setEditProductData({...editProductData, barcode: e.target.value})} />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">SKU</label>
                  <Input value={editProductData.sku || ''} onChange={(e) => setEditProductData({...editProductData, sku: e.target.value})} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-slate-700">Category</label>
                  <Input value={editProductData.category || ''} onChange={(e) => setEditProductData({...editProductData, category: e.target.value})} />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Brand</label>
                  <Input value={editProductData.brand || ''} onChange={(e) => setEditProductData({...editProductData, brand: e.target.value})} />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Description</label>
                <Input value={editProductData.description || ''} onChange={(e) => setEditProductData({...editProductData, description: e.target.value})} />
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setShowEditProduct(false)}>Cancel</Button>
                <Button className="flex-1 bg-blue-600 hover:bg-blue-700" onClick={handleSaveEditProduct} disabled={editProductSaving} data-testid="save-edit-product">
                  {editProductSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                  {editProduct.shopify_product_id ? 'Save & Sync to Shopify' : 'Save'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Report Price Mismatch Dialog */}
      <Dialog open={showReportMismatch} onOpenChange={setShowReportMismatch}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg font-bold uppercase flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-500" /> Report Price Mismatch
            </DialogTitle>
          </DialogHeader>
          {mismatchProduct && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="font-medium text-sm">{mismatchProduct.product_name || mismatchProduct.name}</p>
                <p className="text-xs text-slate-500 mt-1">System price: <span className="font-bold">${(mismatchProduct.sell_price || mismatchProduct.unit_price)?.toFixed(2)}</span></p>
              </div>
              <div className="space-y-2">
                <Label>What price is on the shelf label?</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="e.g. 37.95"
                  value={mismatchLabelPrice}
                  onChange={(e) => setMismatchLabelPrice(e.target.value)}
                  autoFocus
                  data-testid="mismatch-label-price"
                />
              </div>
              <Button
                className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                disabled={!mismatchLabelPrice || parseFloat(mismatchLabelPrice) <= 0}
                onClick={async () => {
                  const product = products.find(p => p.id === (mismatchProduct.product_id || mismatchProduct.id));
                  if (product) {
                    await reportPriceMismatch(product, parseFloat(mismatchLabelPrice));
                  }
                  setShowReportMismatch(false);
                }}
                data-testid="submit-mismatch"
              >
                Report Mismatch
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Negative Stock Fix Modal - lets staff correct counts individually */}
      <Dialog open={showNegativeStockModal} onOpenChange={setShowNegativeStockModal}>
        <DialogContent className="sm:max-w-4xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              Fix negative stock — {getCurrentStore()?.name || selectedStore}
            </DialogTitle>
            <DialogDescription>
              Enter the actual stock on the shelf for each item below. Click <strong>Save</strong> per row — no need to do a full stock take.
            </DialogDescription>
          </DialogHeader>

          <div className="overflow-y-auto flex-1 -mx-6 px-6">
            {negativeStockWarnings.map((group) => (
              <div key={group.supplier} className="mb-4">
                <div className="sticky top-0 bg-white py-2 border-b border-slate-200 z-10">
                  <p className="text-sm font-semibold text-slate-700">
                    {group.supplier} <span className="text-slate-400 font-normal">· {group.count} item{group.count === 1 ? '' : 's'}</span>
                  </p>
                </div>
                <table className="w-full text-sm" data-testid={`neg-stock-group-${group.supplier}`}>
                  <thead>
                    <tr className="text-xs text-slate-500 border-b">
                      <th className="text-left py-2 font-medium">Product</th>
                      <th className="text-center py-2 font-medium w-24">Current</th>
                      <th className="text-center py-2 font-medium w-40">Actual on shelf</th>
                      <th className="text-right py-2 font-medium w-32"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.products.map((p) => {
                      const inputValue = negativeStockFixes[p.id] ?? '';
                      const isSaving = savingNegFix === p.id;
                      return (
                        <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`neg-stock-row-${p.id}`}>
                          <td className="py-2.5">
                            <p className="font-medium text-slate-900 leading-tight">{p.name}</p>
                            <p className="text-xs text-slate-400">{p.barcode || p.sku || '—'}</p>
                          </td>
                          <td className="text-center">
                            <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 font-bold">
                              {p.store_quantity}
                            </Badge>
                          </td>
                          <td className="text-center">
                            <div className="flex items-center justify-center gap-1">
                              <Input
                                type="number"
                                min="0"
                                placeholder="0"
                                value={inputValue}
                                onChange={(e) => setNegativeStockFixes(prev => ({ ...prev, [p.id]: e.target.value }))}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') saveNegativeStockFix(p, inputValue);
                                }}
                                onFocus={(e) => e.target.select()}
                                className="w-20 text-center"
                                disabled={isSaving}
                                data-testid={`neg-stock-input-${p.id}`}
                              />
                              <button
                                type="button"
                                onClick={() => setNegativeStockFixes(prev => ({ ...prev, [p.id]: '0' }))}
                                className="text-xs text-slate-500 hover:text-slate-700 px-1.5 py-1 rounded hover:bg-slate-100"
                                title="Set to 0"
                                disabled={isSaving}
                                data-testid={`neg-stock-zero-${p.id}`}
                              >
                                0
                              </button>
                            </div>
                          </td>
                          <td className="text-right">
                            <Button
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-700 text-white"
                              onClick={() => saveNegativeStockFix(p, inputValue)}
                              disabled={isSaving || inputValue === ''}
                              data-testid={`neg-stock-save-${p.id}`}
                            >
                              {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))}
          </div>

          <DialogFooter className="border-t pt-3">
            <p className="text-xs text-slate-400 flex-1 self-center">
              Tip: Press Enter in the input to save quickly. Items disappear from the list once fixed.
            </p>
            <Button variant="outline" onClick={() => setShowNegativeStockModal(false)} data-testid="neg-stock-close">
              Done
            </Button>
            <Button variant="outline" onClick={() => window.location.href = '/stock-count'} data-testid="neg-stock-goto-count">
              Open full Stock Count
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      </div>
      <LinkBarcodeDialog scan={linkBarcodeScan} products={products} onClose={() => setLinkBarcodeScan(null)} onLinked={handleBarcodeLinked} />
    </div>
  );
};

export default POS;

